<template>
  <div class="pursuit-page">
    <header class="title-strip">
      <span class="title-shield" aria-hidden="true"><i></i></span>
      <h1>{{ this.langMap.pursuitTitle[this.lang] }}</h1>
      <span class="title-rule"></span><span class="title-slashes">///</span>
    </header>
    <section class="robot-band">
      <div class="robot-header-row">
        <DetailHeader
          :robots="robots"
          :selected-robot="selectedRobot || {}"
          view-mode="status"
          data-source="realtime"
          @switch-robot="selectRobot"
        />
        <aside class="monitor-card">
          <div class="monitor-copy">
            <Icon type="md-wifi" />
            <div>
              <strong>{{ langMap.pursuitRealtimeConnection[lang] }}</strong
              ><span
                ><i class="socket-dot" :class="'socket-' + monitorStatus"></i
                >{{ monitorStatusLabel }}</span
              >
            </div>
          </div>
        </aside>
      </div>
    </section>
<section class="live-panel">
<div class="live-heading">
<b>///</b><strong>{{ liveEventTitle }}</strong
><i></i><b>///</b>
</div>
<div v-if="currentEvent" class="live-grid">
<div class="live-identity">
<div class="radar-mark">
<i class="radar-ring"></i>
<i class="radar-sweep"></i>
<b class="radar-blip blip-a"></b>
<b class="radar-blip blip-b"></b>
<span></span>
</div>
<div class="live-identity-fields">
<div class="live-field">
<label class="live-label-source-time">{{ this.langMap.pursuitSourceReportTime[this.lang] }}</label
><em v-if="currentEvent.sourceReportTimestamp" class="live-chip tone-time mono"
><i></i>{{ formatTime(currentEvent.sourceReportTimestamp) }}</em
><em v-else class="live-chip is-empty"><i></i>{{ langMap.pursuitNoData[lang] }}</em>
</div>
<div class="live-field">
<label class="live-label-target">{{ this.langMap.pursuitTargetId[this.lang] }}</label
><em v-if="currentEvent.targetId" class="live-chip tone-cyan">{{ currentEvent.targetId }}</em><em v-else class="live-chip is-empty"><i></i>{{ langMap.pursuitNoData[lang] }}</em>
</div>
</div>
<img
v-if="currentEvent.sourceAnnotatedImageUrl"
class="live-source-image"
:src="currentEvent.sourceAnnotatedImageUrl"
alt=""
@dblclick="openImagePreview(currentEvent.sourceAnnotatedImageUrl)"
/>
<div v-else class="live-source-placeholder" aria-hidden="true">
<span class="live-source-frame"></span>
<span class="live-source-scan"></span>
<b class="live-source-cross"></b>
<em>{{ langMap.pursuitNoData[lang] }}</em>
</div>
</div>
<div class="live-facts">
<div class="live-field">
<label class="live-label-event-status">{{ this.langMap.pursuitEventStatus[this.lang] }}</label
><em v-if="currentEvent.eventStatus" class="live-chip" :class="statusTone(currentEvent.eventStatus)"
><i></i>{{ eventStatusLabel(currentEvent.eventStatus) }}</em
><em v-else class="live-chip is-empty"><i></i>{{ langMap.pursuitNoStatus[lang] }}</em>
</div>
<div class="live-field">
<label class="live-label-feedback">{{ this.langMap.pursuitLatestFeedback[this.lang] }}</label
><em v-if="currentFeedback" class="live-chip tone-cyan" :title="currentFeedback"
><i></i>{{ currentFeedback }}</em
><em v-else class="live-chip is-empty"><i></i>{{ langMap.pursuitNoData[lang] }}</em>
</div>
<div class="live-field">
<label class="live-label-start-time">{{ this.langMap.pursuitStartTime[this.lang] }}</label
><em v-if="currentEvent.startTime" class="live-chip tone-time mono"><i></i>{{ formatTime(currentEvent.startTime) }}</em
><em v-else class="live-chip is-empty"><i></i>{{ langMap.pursuitNoData[lang] }}</em>
</div>
</div>
<div class="live-facts live-facts-side">
<div class="live-field">
<label class="live-label-tracking">{{ this.langMap.pursuitTrackingStatus[this.lang] }}</label
><em v-if="currentEvent.pursuitState" class="live-chip" :class="pursuitTone(currentEvent.pursuitState)"
><i></i>{{ pursuitStateLabelFor(currentEvent.pursuitState) }}</em
><em v-else class="live-chip is-empty"><i></i>{{ langMap.pursuitNoStatus[lang] }}</em>
</div>
<div class="live-field">
<label class="live-label-tracking-time">{{ this.langMap.pursuitTrackingTime[this.lang] }}</label
><em v-if="pursuitStateTimestamp" class="live-chip tone-time mono"
><i></i>{{ formatTime(pursuitStateTimestamp) }}</em
><em v-else class="live-chip is-empty"><i></i>{{ langMap.pursuitNoData[lang] }}</em>
</div>
<div class="live-field">
<label class="live-label-condition">{{ this.langMap.pursuitWorkCondition[this.lang] }}</label
><em class="live-chip condition-status"
:class="[conditionTone(workCondition && workCondition.status), { 'is-empty': workConditionEmpty }]"
><i></i>{{ workConditionLabel }}</em
>
</div>
</div>
<div class="cancel-block">
<button
v-if="canCancelEvent"
class="cancel-button"
:disabled="isCommandPending"
@click="cancelEvent"
>
{{ this.langMap.pursuitCancel[this.lang] }}</button
><span v-else class="operation-muted">{{
this.langMap.pursuitNoOperation[this.lang]
}}</span>
</div>
</div>
      <div v-else class="live-empty">
        <div class="live-empty-event">
          <div class="live-empty-mark" aria-hidden="true"><span></span><i></i></div>
          <div class="live-empty-copy">
            <strong>{{ langMap.pursuitNoActiveEvent[lang] }}</strong>
          </div>
        </div>
        <div class="live-empty-condition">
          <div class="live-field">
            <label class="live-label-condition">{{ langMap.pursuitWorkCondition[lang] }}</label>
            <em class="live-chip condition-status"
              :class="[conditionTone(workCondition && workCondition.status), { 'is-empty': workConditionEmpty }]"
            ><i></i>{{ workConditionLabel }}</em>
          </div>
        </div>
      </div>
    </section>
    <section class="workspace">
      <aside class="left-panel">
        <div class="tab-strip">
          <button
            class="workspace-tab"
            :class="{ active: activeTab === 'messages' }"
            @click="activeTab = 'messages'"
          >
            {{ this.langMap.pursuitDetection[this.lang] }} <b>››</b></button
          ><button
            class="workspace-tab history-tab"
            :class="{ active: activeTab === 'events' }"
            @click="activeTab = 'events'"
          >
            {{ this.langMap.pursuitHistoryEvents[this.lang] }} <b>››</b>
          </button>
        </div>
        <div v-if="activeTab === 'messages'" class="list-mode">
          <div class="time-filter">
            <label>{{ this.langMap.pursuitDetectionTime[this.lang] }}</label
            ><DatePicker
              v-model="messageTimeRange"
              ref="messageDatePicker"
              class="pursuit-date-picker"
              @on-open-change="messageDatePickerOpened"
              @on-change="messageTimeChanged"
              type="datetimerange"
              format="yyyy-MM-dd HH:mm:ss"
              :placeholder="this.langMap.pursuitSelectDetectionTime[this.lang]"
              placement="bottom-end"
              clearable
              size="small"
              :transfer="true"
              style="width: 100%"
            /><button @click="queryMessages">
              {{ this.langMap.pursuitQuery[this.lang] }}</button
            ><button class="filter-reset" @click="resetMessageFilter">
              {{ this.langMap.pursuitReset[this.lang] }}
            </button>
          </div>
          <div
            v-for="message in messages"
            :key="messageKey(message)"
            class="detection-row"
            :class="{ selected: selectedMessageKey === messageKey(message) }"
            @click="selectMessage(message)"
          >
            <span class="row-thumb" :class="{ 'has-image': message.annotatedImageUrl }"
              ><img
                v-if="message.annotatedImageUrl"
                :src="message.annotatedImageUrl"
                :alt="langMap.pursuitAnnotatedImage[lang]"
                @dblclick="openImagePreview(message.annotatedImageUrl)" /></span
            ><span class="row-time mono">{{
              formatTime(message.reportTimestamp)
            }}</span
            ><Icon type="ios-arrow-forward" />
          </div>
<div v-if="!messages.length" class="list-empty-state">
<div class="empty-signal" aria-hidden="true">
<i class="empty-arc arc-1"></i>
<i class="empty-arc arc-2"></i>
<i class="empty-arc arc-3"></i>
<i class="empty-core"></i>
</div>
<span class="empty-text">{{ langMap.pursuitNoData[lang] }}</span>
</div>
          <div class="pagination-bar cyber-pagination">
            <div class="pagination-info">
              <span class="info-label">{{
                this.langMap.pursuitTotal[this.lang]
              }}</span
              ><span class="info-value">{{ messagesTotal }}</span
              ><span class="info-divider">|</span
              ><span class="info-text"
                >{{ messagesPage }} / {{ messagePageCount }}</span
              >
            </div>
            <div class="pagination-controls">
              <button
                class="page-btn"
                :disabled="messagesPage <= 1"
                @click="changeMessagePage(1)"
              >
                <span class="btn-icon">《《</span></button
              ><button
                class="page-btn"
                :disabled="messagesPage <= 1"
                @click="changeMessagePage(messagesPage - 1)"
              >
                <span class="btn-icon">‹</span>
              </button>
              <div class="page-numbers">
                <button
                  v-for="page in messagePageNumbers"
                  :key="'message-page-' + page"
                  class="page-number"
                  :class="{ active: page === messagesPage }"
                  @click="changeMessagePage(page)"
                >{{ page }}</button>
              </div>
              <button
                class="page-btn"
                :disabled="messagesPage >= messagePageCount"
                @click="changeMessagePage(messagesPage + 1)"
              >
                <span class="btn-icon">›</span></button
              ><button
                class="page-btn"
                :disabled="messagesPage >= messagePageCount"
                @click="changeMessagePage(messagePageCount)"
              >
                <span class="btn-icon">》》</span>
              </button>
            </div>
            <div class="page-jumper">
              <span class="jumper-label">{{
                this.langMap.pursuitJump[this.lang]
              }}</span
              ><input
                class="jumper-input"
                type="number"
                :value="messagesPage"
                min="1"
                :max="messagePageCount"
                @change="changeMessagePage(Number($event.target.value))"
              />
            </div>
          </div>
        </div>
        <div v-else class="list-mode">
          <div class="time-filter history-status-filter">
            <label>{{ this.langMap.pursuitEventTime[this.lang] }}</label
            ><DatePicker
              v-model="eventTimeRange"
              ref="eventDatePicker"
              class="pursuit-date-picker history-date-picker"
              @on-open-change="eventDatePickerOpened"
              type="datetimerange"
              format="yyyy-MM-dd HH:mm:ss"
              :placeholder="this.langMap.pursuitSelectEventTime[this.lang]"
              placement="bottom-end"
              clearable
              size="small"
              :transfer="true"
              style="width: 100%"
            /><button @click="queryEvents">
              {{ this.langMap.pursuitQuery[this.lang] }}</button
            ><button class="filter-reset" @click="resetEventFilter">
              {{ this.langMap.pursuitReset[this.lang] }}
            </button>
          </div>
          <div class="history-head">
            <span>{{ this.langMap.pursuitTargetId[this.lang] }}</span
            ><span>{{ this.langMap.pursuitEventStatus[this.lang] }}</span
            ><span>{{ this.langMap.pursuitTrackingStatus[this.lang] }}</span
            ><span>{{ this.langMap.pursuitEventTime[this.lang] }}</span>
          </div>
          <div
            v-for="event in events"
            :key="event.eventId"
            class="history-row"
            :class="{ selected: selectedEventId === event.eventId }"
            @click="openEventDetail(event)"
          >
            <span :title="event.targetId">{{ event.targetId }}</span
            ><em :class="statusTone(event.eventStatus)">{{
              eventStatusLabel(event.eventStatus)
            }}</em
            ><em class="tone-cyan">{{
              pursuitStateLabelFor(event.pursuitState)
            }}</em
            ><span class="mono">{{
              formatTime(event.startTime)
            }}</span
            ><Icon type="ios-arrow-forward" />
          </div>
<div v-if="!events.length" class="list-empty-state">
<div class="empty-signal" aria-hidden="true">
<i class="empty-arc arc-1"></i>
<i class="empty-arc arc-2"></i>
<i class="empty-arc arc-3"></i>
<i class="empty-core"></i>
</div>
<span class="empty-text">{{ langMap.pursuitNoData[lang] }}</span>
          </div>
          <div class="pagination-bar cyber-pagination">
            <div class="pagination-info">
              <span class="info-label">{{
                this.langMap.pursuitTotal[this.lang]
              }}</span
              ><span class="info-value">{{ eventsTotal }}</span
              ><span class="info-divider">|</span
              ><span class="info-text"
                >{{ eventsPage }} / {{ eventPageCount }}</span
              >
            </div>
            <div class="pagination-controls">
              <button
                class="page-btn"
                :disabled="eventsPage <= 1"
                @click="changeEventPage(1)"
              >
                <span class="btn-icon">《《</span></button
              ><button
                class="page-btn"
                :disabled="eventsPage <= 1"
                @click="changeEventPage(eventsPage - 1)"
              >
                <span class="btn-icon">‹</span>
              </button>
              <div class="page-numbers">
                <button
                  v-for="page in eventPageNumbers"
                  :key="'event-page-' + page"
                  class="page-number"
                  :class="{ active: page === eventsPage }"
                  @click="changeEventPage(page)"
                >{{ page }}</button>
              </div>
              <button
                class="page-btn"
                :disabled="eventsPage >= eventPageCount"
                @click="changeEventPage(eventsPage + 1)"
              >
                <span class="btn-icon">›</span></button
              ><button
                class="page-btn"
                :disabled="eventsPage >= eventPageCount"
                @click="changeEventPage(eventPageCount)"
              >
                <span class="btn-icon">》》</span>
              </button>
            </div>
            <div class="page-jumper">
              <span class="jumper-label">{{
                this.langMap.pursuitJump[this.lang]
              }}</span
              ><input
                class="jumper-input"
                type="number"
                :value="eventsPage"
                min="1"
                :max="eventPageCount"
                @change="changeEventPage(Number($event.target.value))"
              />
            </div>
          </div>
        </div>
      </aside>
      <main class="right-panel">
        <div v-if="activeTab === 'messages'" class="message-view">
          <div class="detail-time">
            <strong>{{ detectionTimeLabel }}</strong
            ><span class="mono">{{
              selectedMessage
                ? formatTime(selectedMessage.reportTimestamp)
                : "-"
            }}</span>
          </div>
          <div v-if="selectedMessage" class="message-detail">
            <div class="annotated-image">
              <img
                v-if="selectedMessage.annotatedImageUrl"
                :src="selectedMessage.annotatedImageUrl"
                :alt="langMap.pursuitAnnotatedImage[lang]"
                @dblclick="openImagePreview(selectedMessage.annotatedImageUrl)"
              />
            </div>
            <div class="target-selector">
              <h2>
                {{ this.langMap.pursuitTargetList[this.lang] }}
                <small
                  >({{ this.langMap.pursuitSelectTarget[this.lang] }})</small
                >
              </h2>
              <div class="target-options">
                <button
                  v-for="target in selectedMessage.targets"
                  :key="target.targetId"
                  class="target-row"
                  :class="{ selected: isTargetSelected(selectedMessage, target) }"
                  @click="selectTarget(selectedMessage, target)"
                >
                  <span class="radio-dot"></span
                  ><b>{{ target.targetId }}</b>
                </button>
              </div>
              <button
                class="authorize-button"
                :disabled="!isMessageSelected(selectedMessage) || isCommandPending"
                @click="authorizeSelected(selectedMessage)"
              >
                {{ this.langMap.pursuitAuthorize[this.lang] }}
              </button>
            </div>
          </div>
<div v-else class="right-empty-state">
<div class="empty-signal" aria-hidden="true">
<i class="empty-arc arc-1"></i>
<i class="empty-arc arc-2"></i>
<i class="empty-arc arc-3"></i>
<i class="empty-core"></i>
</div>
<span class="empty-text">{{ this.langMap.pursuitNoData[this.lang] }}</span>
          </div>
          <div class="event-log">
            <strong>{{ liveEventTitle }}</strong>
            <div class="event-log-head">
<span>{{ langMap.pursuitLogTimestamp[lang] }}</span><span>{{ langMap.pursuitLogType[lang] }}</span><span>{{ langMap.pursuitTargetId[lang] }}</span
><span>{{ langMap.pursuitLogStatus[lang] }}</span>
            </div>
          </div>
        </div>
        <div v-else class="history-detail-view">
          <div v-if="historicalEvent" class="history-detail">
            <div class="history-title">
              <strong>{{ this.langMap.pursuitHistoryDetail[this.lang] }}</strong>
            </div>
            <div class="summary">
              <div class="history-source-image" v-if="historicalEvent.sourceAnnotatedImageUrl">
                <img
                  :src="historicalEvent.sourceAnnotatedImageUrl"
                  alt=""
                  @dblclick="openImagePreview(historicalEvent.sourceAnnotatedImageUrl)"
                />
              </div>
              <div>
                <p>
                  <label>{{ this.langMap.pursuitTargetId[this.lang] }}</label
                  ><b>{{ historicalEvent.targetId }}</b>
                </p>
                <p>
                  <label>{{ this.langMap.pursuitEventStatus[this.lang] }}</label
                  ><em :class="statusTone(historicalEvent.eventStatus)">{{
                    eventStatusLabel(historicalEvent.eventStatus)
                  }}</em>
                </p>
                <p>
                  <label>{{ this.langMap.pursuitActive[this.lang] }}</label
                  ><b>{{
                    historicalEvent.active
                      ? this.langMap.pursuitActive[this.lang]
                      : this.langMap.pursuitEnded[this.lang]
                  }}</b>
                </p>
                <p>
                  <label>{{
                    this.langMap.pursuitTrackingStatus[this.lang]
                  }}</label
                  ><em :class="pursuitTone(historicalEvent.pursuitState)">{{
                    pursuitStateLabelFor(historicalEvent.pursuitState)
                  }}</em>
                </p>
              </div>
              <div>
                <p>
                  <label>{{
                    this.langMap.pursuitSourceReportTime[this.lang]
                  }}</label
                  ><b class="mono">{{
                    formatTime(historicalEvent.sourceReportTimestamp)
                  }}</b>
                </p>
                <p>
                  <label>{{ this.langMap.pursuitStartTime[this.lang] }}</label
                  ><b class="mono">{{
                    formatTime(historicalEvent.startTime)
                  }}</b>
                </p>
                <p>
                  <label>{{ this.langMap.pursuitCloseTime[this.lang] }}</label
                  ><b class="mono">{{
                    formatTime(historicalEvent.closeTime)
                  }}</b>
                </p>
                <p>
                  <label>{{
                    this.langMap.pursuitLatestFeedback[this.lang]
                  }}</label
                  ><em class="tone-cyan">{{
                    eventFeedback(historicalEvent) || "-"
                  }}</em>
                </p>
              </div>
            </div>
            <h3>{{ this.langMap.pursuitOperationHistory[this.lang] }}</h3>
            <div class="operations-head">
              <span>{{ this.langMap.pursuitOperationType[this.lang] }}</span
              ><span>{{ this.langMap.pursuitOperationStatus[this.lang] }}</span
              ><span>{{ this.langMap.pursuitOperationTime[this.lang] }}</span
              ><span>{{ this.langMap.pursuitFeedback[this.lang] }}</span
              ><span>{{ this.langMap.pursuitFeedbackTime[this.lang] }}</span>
            </div>
            <div class="operations-scroll">
              <div
                v-for="operation in detailData.operations || []"
                :key="operation.id"
                class="operation-row"
              >
                <span>{{ operationTypeLabel(operation.operationType) }}</span
                ><span
                  ><em
                    :class="operationStatusTone(operation.operationStatus)"
                    >{{ operationStatusLabel(operation.operationStatus) }}</em
                  ></span
                ><span class="mono">{{
                  formatTime(operation.commandTimestamp)
                }}</span
                ><span>{{ operationFeedback(operation) || "-" }}</span
                ><span class="mono">{{
                  formatTime(operation.feedbackTimestamp)
                }}</span>
              </div>
            </div>
          </div>
<div v-else class="right-empty-state">
<div class="empty-signal" aria-hidden="true">
<i class="empty-arc arc-1"></i>
<i class="empty-arc arc-2"></i>
<i class="empty-arc arc-3"></i>
<i class="empty-core"></i>
</div>
<span class="empty-text">{{ this.langMap.pursuitNoData[this.lang] }}</span>
          </div>
        </div>
      </main>
    </section>
    <div
      v-if="previewImageUrl"
      class="pursuit-image-preview"
      role="dialog"
      aria-modal="true"
      @click.self="closeImagePreview"
    >
      <button class="pursuit-image-preview-close" type="button" aria-label="Close" @click="closeImagePreview">×</button>
      <img :src="previewImageUrl" alt="" @load="syncPreviewImageSize" @click.stop />
    </div>
  </div>
</template>
<script>
import api from "@/api";
import { connectWithUrl, releaseSocket, sendMsg } from "@/utils/websocket";
import { getTenantId } from "@/libs/util";
import * as timeUtil from "@/utils/dateTimeUtil.js";
import { mapState } from "vuex";
import langMap from "@/utils/language";
import DetailHeader from "@/view/monitor/dashboard/module/header/DetailHeader.vue";
const EVENT_STATUS_LABEL_KEYS = {
  AUTHORIZING: "pursuitStatusAuthorizing",
  ACTIVE: "pursuitStatusActive",
  AUTHORIZE_FAILED: "pursuitStatusAuthorizeFailed",
  AUTHORIZE_TIMEOUT: "pursuitStatusAuthorizeTimeout",
  CANCELLING: "pursuitStatusCancelling",
  CANCEL_FAILED: "pursuitStatusCancelFailed",
  CANCEL_TIMEOUT: "pursuitStatusCancelTimeout",
  CANCELED: "pursuitStatusCanceled",
  SUPERSEDED: "pursuitStatusPlatformClosed",
  WORK_CONDITION_CLOSED: "pursuitStatusPlatformClosed",
  STATE_TIMEOUT: "pursuitStatusPlatformClosed",
  OFFLINE_CLOSED: "pursuitStatusPlatformClosed",
};
const OPERATION_STATUS_LABEL_KEYS = {
  CREATED: "pursuitOperationPreparing",
  SENT: "pursuitOperationWaiting",
  ACK_OK: "pursuitOperationSuccess",
  ACK_FAILED: "pursuitOperationFailed",
  SEND_FAILED: "pursuitSendFailed",
  TIMEOUT: "pursuitResponseTimeout",
  STALE: "pursuitStale",
};
const PURSUIT_STATE_LABEL_KEYS = {
  DETECTING: "pursuitTrackingDetecting",
  TRACKING: "pursuitTracking",
  TEMP_LOST: "pursuitTrackingLost",
};
export default {
  name: "PursuitPage",
  components: { DetailHeader },
  data() {
    return {
      langMap,
      robots: [],
      robotsLoaded: false,
      selectedCode: "",
      monitorSocket: null,
      monitorConnecting: false,
      monitorStatus: "disconnected",
      refreshing: false,
      activeTab: "messages",
      currentEvent: null,
      pursuitStateTimestamp: null,
      pursuitStateEventId: "",
      previewImageUrl: "",
      workCondition: null,
      messages: [],
      messagesTotal: 0,
      messagesPage: 1,
      messagePageSize: 20,
      messageTimeRange: [],
      exactMessageTimeRange: null,
      ignoredRouteQuery: null,
      selectedMessageKey: "",
      selectedTargetId: "",
      selectedReportTimestamp: "",
      events: [],
      eventsTotal: 0,
      eventsPage: 1,
      eventPageSize: 20,
      eventTimeRange: [],
      selectedEventId: "",
      detailData: {},
      monitorReconnectTimer: null,
      monitorClosing: false,
      monitorSession: 0,
      monitorReady: false,
      robotOnlineByCode: {},
      datePickerObserver: null,
      messagesRequestId: 0,
      robotsRequestId: 0,
      pageDisposed: false,
      eventsRequestId: 0,
      detailRequestId: 0,
      commandRequestId: 0,
      isCommandPending: false,
    };
  },
  computed: {
    ...mapState({ lang: (s) => s.app.language }),
    liveEventTitle() {
      return this.langMap.pursuitLiveEvent[this.lang];
    },
    detectionTimeLabel() {
      return this.langMap.pursuitDetectionTime[this.lang];
    },
    currentFeedback() {
      return this.eventFeedback(this.currentEvent);
    },
    selectedRobot() {
      return this.robots.find((i) => i.code === this.selectedCode) || null;
    },
    selectedMessage() {
      return (
        this.messages.find(
          (i) => this.messageKey(i) === this.selectedMessageKey
        ) || null
      );
    },
    historicalEvent() {
      return (
        (this.detailData && this.detailData.event) ||
        this.events.find(
          (i) => String(i.eventId) === String(this.selectedEventId)
        ) ||
        null
      );
    },
    monitorStatusLabel() {
      return this.langMap[
        {
          connecting: "pursuitMonitorConnecting",
          connected: "pursuitMonitorConnected",
          disconnected: "pursuitMonitorDisconnected",
          error: "pursuitMonitorError",
        }[this.monitorStatus] || "pursuitMonitorDisconnected"
      ][this.lang];
},
workConditionEmpty() {
const v = this.workCondition && this.workCondition.status;
return v === undefined || v === null;
},
workConditionLabel() {
      let v = this.workCondition && this.workCondition.status;
      return v === undefined || v === null
        ? this.langMap.pursuitConditionSnapshotEmpty[this.lang]
        : {
            0: this.langMap.pursuitConditionBackup[this.lang],
            1: this.langMap.pursuitConditionPatrol[this.lang],
            4: this.langMap.pursuitConditionLowBattery[this.lang],
            6: this.langMap.pursuitConditionActive[this.lang],
          }[v] || this.langMap.pursuitConditionUnknown[this.lang] + " " + v;
    },
    canCancelEvent() {
      return !!(this.currentEvent && this.currentEvent.active === true);
    },
    messagePageCount() {
      return Math.max(1, Math.ceil(this.messagesTotal / this.messagePageSize));
    },
    eventPageCount() {
      return Math.max(1, Math.ceil(this.eventsTotal / this.eventPageSize));
    },
    messagePageNumbers() {
      return this.getPageNumbers(this.messagesPage, this.messagePageCount);
    },
    eventPageNumbers() {
      return this.getPageNumbers(this.eventsPage, this.eventPageCount);
    },
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler(q) {
        if (this.pageDisposed) return;
        if (!q || !Object.keys(q).length) this.ignoredRouteQuery = null;
        q = this.detailRouteQuery(q);
        // Validate the requested robot before applying its detail time range.
        if (!this.robots.length && !this.robotsLoaded) return;
        if (!this.robots.length) {
          this.rejectRouteDetail("pursuitDetailRobotsUnavailable");
          return;
        }
        if (q.code && !this.robots.some((robot) => robot.code === String(q.code))) {
          this.rejectRouteDetail("pursuitDetailRobotMissing");
          return;
        }
        const hasDetailTime = q && (q.startTimestamp || q.reportTimestamp);
        if (
          q &&
          q.code &&
          this.robots.length &&
          String(q.code) !== this.selectedCode
        )
          this.selectCode(q.code);
        else if (hasDetailTime && this.selectedCode) {
          this.applyRouteSelection(q, true);
          this.messagesPage = 1;
          this.messages = [];
          this.messagesTotal = 0;
          this.fetchMessages();
        }
        else this.applyRouteSelection(q, !!hasDetailTime);
      },
    },
    activeTab(t) {
      if (t === "events") this.fetchEvents();
    },
  },
  created() {
    this.messageTimeRange = this.defaultTimeRange();
    this.eventTimeRange = this.defaultTimeRange();
    // Establish the shared Monitor connection as soon as the page opens.
    // Robot selection only changes the subscription on this connection.
    this.connectMonitor();
    this.fetchRobots();
  },
  mounted() {
    document.body.classList.add("pursuit-date-theme-active");
    this.installDatePickerObserver();
    window.addEventListener("beforeunload", this.closeMonitor);
  },
  activated() {
    document.body.classList.add("pursuit-date-theme-active");
  },
  deactivated() {
    document.body.classList.remove("pursuit-date-theme-active");
  },
  beforeDestroy() {
    this.pageDisposed = true;
    this.closeImagePreview();
    this.robotsRequestId++;
    document.body.classList.remove("pursuit-date-theme-active");
    if (this.datePickerObserver) this.datePickerObserver.disconnect();
    window.removeEventListener("beforeunload", this.closeMonitor);
    this.messagesRequestId++;
    this.eventsRequestId++;
    this.detailRequestId++;
    this.commandRequestId++;
    this.closeMonitor();
  },
  methods: {
    getPageNumbers(currentPage, pageCount) {
      const visibleCount = 5;
      const start = Math.max(
        1,
        Math.min(currentPage - Math.floor(visibleCount / 2), pageCount - visibleCount + 1)
      );
      const end = Math.min(pageCount, start + visibleCount - 1);
      const pages = [];
      for (let page = start; page <= end; page++) pages.push(page);
      return pages;
    },
    installDatePickerObserver() {
      if (typeof MutationObserver === "undefined") return;
      const mark = () =>
        document
          .querySelectorAll(".ivu-date-picker-transfer")
          .forEach((node) => node.classList.add("pursuit-date-picker-popup"));
      this.datePickerObserver = new MutationObserver(mark);
      this.datePickerObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
      mark();
    },
    messageDatePickerOpened(opened) {
      this.markDatePickerPopup("messageDatePicker", opened);
    },
    messageTimeChanged() {
      this.exactMessageTimeRange = null;
      this.messagesRequestId++;
      this.consumeRouteDetailQuery();
    },
    defaultTimeRange() {
      const end = new Date();
      const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
      return [start, end];
    },
    eventDatePickerOpened(opened) {
      this.markDatePickerPopup("eventDatePicker", opened);
    },
    markDatePickerPopup(refName, opened) {
      if (!opened) return;
      const apply = () => {
        let picker = this.$refs[refName];
        let drop = picker && picker.$refs && picker.$refs.drop;
        let popup = drop && drop.$el;
        if (popup) popup.classList.add("pursuit-date-picker-popup");
      };
      this.$nextTick(apply);
      setTimeout(apply, 60);
    },
    applyRouteSelection(q, focusMessages = false) {
      q = this.detailRouteQuery(q);
      if (!q) return;
      const routeStart = q.startTimestamp || q.reportTimestamp;
      const routeEnd = q.endTimestamp || q.reportTimestamp;
      if (routeStart && routeEnd) {
        const startDate = new Date(Number(routeStart));
        const endDate = new Date(Number(routeEnd));
        if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
          if (focusMessages) this.activeTab = "messages";
          this.messageTimeRange = [startDate, endDate];
          this.exactMessageTimeRange = [startDate.getTime(), endDate.getTime()];
        }
      }
      if (q.eventId) {
        let event = this.events.find(
          (i) => String(i.eventId) === String(q.eventId)
        );
        if (event) this.openEventDetail(event);
      }
      if (!this.messages.length) return;
      let targetId = q.targetId ? String(q.targetId) : "";
      let message = q.reportTimestamp
        ? this.messages.find(
            (i) => String(i.reportTimestamp) === String(q.reportTimestamp)
          )
        : null;
      if (!message && targetId)
        message = this.messages.find((i) =>
          (i.targets || []).some(
            (target) => String(target.targetId) === targetId
          )
        );
      if (!message) return;
      this.selectedMessageKey = this.messageKey(message);
      this.selectedReportTimestamp = message.reportTimestamp;
      let target =
        message.targets &&
        message.targets.find((i) => String(i.targetId) === targetId);
      this.selectedTargetId = target
        ? target.targetId
        : message.targets && message.targets[0]
        ? message.targets[0].targetId
        : "";
    },
    detailRouteQuery(q = this.$route.query) {
      return JSON.stringify(q || {}) === this.ignoredRouteQuery ? {} : q || {};
    },
    rejectRouteDetail(messageKey) {
      const query = this.detailRouteQuery();
      if (!["code", "targetId", "reportTimestamp", "startTimestamp", "endTimestamp", "eventId", "type"].some((key) => Object.prototype.hasOwnProperty.call(query, key))) return;
      if (!this.selectedCode) this.exactMessageTimeRange = null;
      this.$Notice.error({
        title: this.langMap.pursuitDetailUnavailable[this.lang],
        desc: this.langMap[messageKey][this.lang] + (messageKey === "pursuitDetailRobotMissing" ? " " + query.code : ""),
      });
      this.consumeRouteDetailQuery();
    },
    consumeRouteDetailQuery() {
      const query = { ...(this.$route.query || {}) };
      ["code", "targetId", "reportTimestamp", "startTimestamp", "endTimestamp", "eventId", "type"].forEach((key) => {
        delete query[key];
      });
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query || {})) {
        // Ignore consumed detail parameters immediately, even while navigation is pending.
        this.ignoredRouteQuery = JSON.stringify(this.$route.query || {});
        this.$router.replace({ path: this.$route.path, query }, (route) => {
          if (this.pageDisposed) return;
          this.ignoredRouteQuery = null;
          this.$store.commit("updateTagQuery", route);
        }, () => {});
      }
    },
    selectRobot(r) {
      if (r && r.code) {
        if (String(r.code) !== this.selectedCode) this.consumeRouteDetailQuery();
        this.selectCode(r.code);
      }
    },
    selectCode(c) {
      if (this.pageDisposed) return;
      const code = String(c || "");
      if (!code) return;
      const changed = this.selectedCode !== code;
      this.selectedCode = code;
      if (changed) {
        this.clearState();
        this.applyRouteSelection(this.$route.query, true);
        if (this.monitorSocket) this.subscribeMonitor();
        else this.connectMonitor();
      } else if (!this.monitorSocket) {
        this.connectMonitor();
      }
      this.fetchMessages();
      this.fetchEvents();
    },
    clearState() {
      this.currentEvent = null;
      this.pursuitStateTimestamp = null;
      this.pursuitStateEventId = "";
      this.workCondition = null;
      this.messagesPage = 1;
      this.eventsPage = 1;
      this.messages = [];
      this.events = [];
      this.messagesTotal = 0;
      this.eventsTotal = 0;
      this.exactMessageTimeRange = null;
      this.selectedMessageKey = "";
      this.selectedTargetId = "";
      this.selectedReportTimestamp = "";
      this.selectedEventId = "";
      this.detailData = {};
      this.messagesRequestId++;
      this.eventsRequestId++;
      this.detailRequestId++;
      this.commandRequestId++;
      this.isCommandPending = false;
    },
    refreshAll() {
      if (this.refreshing) return Promise.resolve();
      this.refreshing = true;
      return Promise.all([this.fetchMessages(), this.fetchEvents()]).finally(
        () => {
          this.refreshing = false;
        }
      );
    },
    fetchRobots() {
      if (this.pageDisposed) return Promise.resolve();
      const requestId = ++this.robotsRequestId;
      return api.getData("robot-server/robot/list/all", this).then((res) => {
        if (this.pageDisposed || requestId !== this.robotsRequestId) return;
        this.robotsLoaded = true;
        if (!res || res.state === 0) {
          this.rejectRouteDetail("pursuitDetailRobotsUnavailable");
          return;
        }
        this.robots = (res.data || []).map((r) => ({
          ...r,
          code: String(r.code || r.serialNumber || r.id),
          name: r.name || r.code,
          status: Object.prototype.hasOwnProperty.call(this.robotOnlineByCode, String(r.code || r.serialNumber || r.id))
            ? this.robotOnlineByCode[String(r.code || r.serialNumber || r.id)] ? "online" : "offline"
            : r.onlineStatus ? "online" : "offline",
        }));
        const query = this.detailRouteQuery();
        if (query.code && !this.robots.some((robot) => robot.code === String(query.code))) {
          this.rejectRouteDetail("pursuitDetailRobotMissing");
          return;
        }
        if (!this.robots.length) {
          this.rejectRouteDetail("pursuitDetailRobotsUnavailable");
          return;
        }
        const code = query.code || this.selectedCode;
        const robot = this.robots.find((item) => item.code === String(code)) || this.robots[0];
        this.selectCode(robot.code);
      });
    },
    closeMonitor() {
      if (this.monitorReconnectTimer) clearTimeout(this.monitorReconnectTimer);
      this.monitorReconnectTimer = null;
      this.monitorSession++;
      this.monitorClosing = true;
      this.monitorConnecting = false;
      this.monitorReady = false;
      const socket = this.monitorSocket;
      releaseSocket(socket, "pursuitMonitor");
      if (socket && socket.readyState < 2) socket.close();
      this.monitorSocket = null;
      this.monitorStatus = "disconnected";
    },
    connectMonitor() {
      if (this.pageDisposed || this.monitorSocket || this.monitorConnecting) return;
      if (this.monitorReconnectTimer) clearTimeout(this.monitorReconnectTimer);
      this.monitorReconnectTimer = null;
      this.monitorClosing = false;
      this.monitorConnecting = true;
      this.monitorReady = false;
      this.monitorStatus = "connecting";
      const session = ++this.monitorSession;
      const socket = connectWithUrl(
        "/monitor",
        "pursuitMonitor",
        JSON.stringify({
          type: "init",
          data: this.$store.state.user.userInfo.userId,
          language: this.lang || "zh",
          tenantId: getTenantId(),
        }),
        (m) => {
          if (session === this.monitorSession && !this.monitorClosing)
            this.handleMonitorMessage(m);
        },
        (s) => {
          if (session !== this.monitorSession || this.monitorClosing) {
            releaseSocket(s, "pursuitMonitor");
            if (s.readyState < 2) s.close();
            return;
          }
          this.monitorConnecting = false;
          this.monitorSocket = s;
          this.monitorStatus = "connected";
        },
        () => {
          if (session === this.monitorSession) {
            this.scheduleMonitorReconnect(session);
          }
        }
      );
      if (session === this.monitorSession && !this.monitorClosing) {
        this.monitorSocket = socket || null;
        if (socket) socket.onclose = () => {
          releaseSocket(socket, "pursuitMonitor");
          this.scheduleMonitorReconnect(session);
        };
      } else {
        releaseSocket(socket, "pursuitMonitor");
        if (socket && socket.readyState < 2) socket.close();
      }
    },
    scheduleMonitorReconnect(session) {
      if (session !== this.monitorSession || this.monitorClosing) return;
      this.monitorSession++;
      const socket = this.monitorSocket;
      releaseSocket(socket, "pursuitMonitor");
      if (socket && socket.readyState < 2) socket.close();
      this.monitorSocket = null;
      this.monitorConnecting = false;
      this.monitorReady = false;
      this.monitorStatus = "error";
      this.currentEvent = null;
      this.pursuitStateTimestamp = null;
      this.pursuitStateEventId = "";
      this.workCondition = null;
      if (this.monitorReconnectTimer) clearTimeout(this.monitorReconnectTimer);
      this.monitorReconnectTimer = setTimeout(() => {
        this.monitorReconnectTimer = null;
        this.connectMonitor();
      }, 5000);
    },
    subscribeMonitor() {
      if (this.monitorSocket && this.monitorReady && this.selectedCode)
        sendMsg("pursuitMonitor", {
          type: "subscribe",
          robotCode: this.selectedCode,
        });
    },
    handleMonitorMessage(raw) {
      let m;
      try {
        m = typeof raw === "string" ? JSON.parse(raw) : raw;
      } catch (e) {
        return;
      }
      if (!m || !m.type) return;
      if (m.type === "init_success") {
        this.monitorReady = true;
        this.subscribeMonitor();
        return;
      }
      let d = m.data || {};
      const code = m.robotCode || d.code || (d.event && d.event.code);
      if (
        m.type !== "online" &&
        m.type !== "offline" &&
        m.type !== "robot_status" &&
        code &&
        String(code) !== this.selectedCode
      ) return;
      if (m.type === "work_condition") {
        let timestamp = Number(d.timestamp || 0);
        if (
          !this.workCondition ||
          timestamp >= Number(this.workCondition.timestamp || 0)
        )
          this.workCondition = { ...d, timestamp };
        if (
          d.onlineStatus !== undefined ||
          d.online !== undefined
        )
          this.updateRobotOnlineStatus(
            code,
            d.onlineStatus !== undefined
              ? d.onlineStatus
              : d.online
          );
      } else if (
        m.type === "robot_status" ||
        m.type === "online" ||
        m.type === "offline"
      ) {
        if (m.type === "online" || m.type === "offline")
          this.updateRobotOnlineStatus(code, m.type === "online");
        else if (d.onlineStatus !== undefined || d.online !== undefined)
          this.updateRobotOnlineStatus(code, d.onlineStatus !== undefined ? d.onlineStatus : d.online);
      } else if (m.type === "pursuit_active_event") {
        const event = d.event;
        if (event && event.active === true) {
          if (!this.isCurrentEvent(event.eventId)) {
            this.pursuitStateTimestamp = null;
            this.pursuitStateEventId = "";
          }
          this.currentEvent = this.normalizeEvent(event);
        } else if (
          (event && event.active === false && this.isCurrentEvent(event.eventId)) ||
          (!event && d.closedEventId && this.isCurrentEvent(d.closedEventId))
        ) {
          this.currentEvent = null;
          this.pursuitStateTimestamp = null;
          this.pursuitStateEventId = "";
        }
      } else if (m.type === "pursuit_event") {
        const event = d.event || d;
        if (event.active === false) {
          if (this.isCurrentEvent(event.eventId)) {
            this.currentEvent = null;
            this.pursuitStateTimestamp = null;
            this.pursuitStateEventId = "";
          }
        } else if (event.active === true && event.eventId) {
          const sameEvent = this.isCurrentEvent(event.eventId);
          const current = sameEvent ? this.currentEvent : {};
          if (!sameEvent) {
            this.pursuitStateTimestamp = null;
            this.pursuitStateEventId = "";
          }
          this.currentEvent = this.normalizeEvent({ ...current, ...event });
        }
      } else if (m.type === "pursuit_state") {
        if (
          this.isCurrentEvent(d.eventId) &&
          Object.prototype.hasOwnProperty.call(PURSUIT_STATE_LABEL_KEYS, d.state)
        ) {
          this.$set(this.currentEvent, "pursuitState", d.state);
          const timestamp = Number(d.reportTimestamp || 0);
          const previousTimestamp =
            this.pursuitStateEventId === String(d.eventId)
              ? Number(this.pursuitStateTimestamp || 0)
              : 0;
          if (timestamp > 0 && timestamp >= previousTimestamp) {
            this.pursuitStateTimestamp = d.reportTimestamp;
            this.pursuitStateEventId = String(d.eventId);
          }
        }
      }
    },
    isCurrentEvent(eventId) {
      return !!(
        eventId &&
        this.currentEvent &&
        String(eventId) === String(this.currentEvent.eventId)
      );
    },
    updateRobotOnlineStatus(code, isOnline) {
      if (!code) return;
      this.$set(this.robotOnlineByCode, String(code), !!isOnline);
      const robot = this.robots.find(
        (item) => String(item.code) === String(code)
      );
      if (robot) this.$set(robot, "status", isOnline ? "online" : "offline");
    },
    normalizeEvent(e) {
      return {
        ...e,
        active: e.active === true,
        code: e.code || this.selectedCode,
      };
    },
    eventFeedback(event) {
      if (!event) return "";
      return this.lang === "en"
        ? event.lastFeedbackMessageEn ||
            event.lastFeedbackMessageZh ||
            event.lastFeedbackMessage ||
            ""
        : event.lastFeedbackMessageZh ||
            event.lastFeedbackMessageEn ||
            event.lastFeedbackMessage ||
            "";
    },
    operationFeedback(operation) {
      if (!operation) return "";
      return this.lang === "en"
        ? operation.feedbackMessageEn || operation.feedbackMessageZh || ""
        : operation.feedbackMessageZh || operation.feedbackMessageEn || "";
    },
    queryTimestamp(date, isEnd) {
      if (!date) return null;
      const time = new Date(date).getTime();
      if (Number.isNaN(time)) return null;
      return isEnd ? time + 999 - (time % 1000) : time;
    },
    queryMessages() {
      this.messagesPage = 1;
      this.fetchMessages();
    },
    resetMessageFilter() {
      this.messageTimeChanged();
      this.messageTimeRange = this.defaultTimeRange();
      this.queryMessages();
    },
    queryEvents() {
      this.eventsPage = 1;
      this.fetchEvents();
    },
    resetEventFilter() {
      this.eventTimeRange = this.defaultTimeRange();
      this.queryEvents();
    },
    fetchMessages() {
      if (this.pageDisposed || !this.selectedCode) return Promise.resolve();
      const code = this.selectedCode;
      const requestId = ++this.messagesRequestId;
      let q =
        "code=" +
        encodeURIComponent(code) +
        "&index=" +
        this.messagesPage +
        "&pageSize=" +
        this.messagePageSize;
      const routeQuery = { ...this.detailRouteQuery() };
      if (routeQuery.startTimestamp || routeQuery.reportTimestamp)
        this.applyRouteSelection(routeQuery);
      const exactRange = this.exactMessageTimeRange;
      let start = routeQuery.startTimestamp || routeQuery.reportTimestamp
        ? String(routeQuery.startTimestamp || routeQuery.reportTimestamp)
        : exactRange ? exactRange[0] : this.queryTimestamp(this.messageTimeRange[0], false);
      let end = routeQuery.endTimestamp || routeQuery.reportTimestamp
        ? String(routeQuery.endTimestamp || routeQuery.reportTimestamp)
        : exactRange ? exactRange[1] : this.queryTimestamp(this.messageTimeRange[1], true);
      if (start !== null) q += "&startTimestamp=" + start;
      if (end !== null) q += "&endTimestamp=" + end;
      return api.getData("notice-server/pursuit/messages?" + q, this).then((res) => {
        if (requestId !== this.messagesRequestId || code !== this.selectedCode) return;
        if (res && res.state === 1) {
          let d = res.data || {};
          this.messages = (d.rows || []).map((m) => ({
            ...m,
            targets: (m.targets || []).map((t) => ({
              ...t,
              targetId: String(t.targetId),
            })),
          }));
          this.messagesTotal = d.total == null ? this.messages.length : d.total;
          if (!this.messagesTotal) this.messagesPage = 1;
          const lastPage = Math.max(1, Math.ceil(this.messagesTotal / this.messagePageSize));
          if (this.messagesPage > lastPage) {
            this.messagesPage = lastPage;
            this.messages = [];
            this.selectedMessageKey = "";
            this.selectedTargetId = "";
            this.selectedReportTimestamp = "";
            return this.fetchMessages();
          }
          const selected = this.messages.find((m) => this.messageKey(m) === this.selectedMessageKey);
          if (!selected) {
            if (this.messages[0]) this.selectMessage(this.messages[0]);
            else {
              this.selectedMessageKey = "";
              this.selectedTargetId = "";
              this.selectedReportTimestamp = "";
            }
          } else if (!(selected.targets || []).some((t) => t.targetId === this.selectedTargetId)) {
            this.selectMessage(selected);
          }
          this.applyRouteSelection(routeQuery);
        } else {
          this.messages = [];
          this.messagesTotal = 0;
          this.messagesPage = 1;
          this.selectedMessageKey = "";
          this.selectedTargetId = "";
          this.selectedReportTimestamp = "";
        }
        if (
          JSON.stringify(this.$route.query || {}) === JSON.stringify(routeQuery) &&
          (routeQuery.code || routeQuery.reportTimestamp || routeQuery.startTimestamp || routeQuery.endTimestamp || routeQuery.targetId || routeQuery.eventId || routeQuery.type)
        ) this.consumeRouteDetailQuery();
      });
    },
    fetchEvents() {
      if (this.pageDisposed || !this.selectedCode) return Promise.resolve();
      const code = this.selectedCode;
      const requestId = ++this.eventsRequestId;
      let q =
        "code=" +
        encodeURIComponent(code) +
        "&index=" +
        this.eventsPage +
        "&pageSize=" +
        this.eventPageSize;
      let start = this.queryTimestamp(this.eventTimeRange[0], false),
        end = this.queryTimestamp(this.eventTimeRange[1], true);
      if (start !== null) q += "&startTimestamp=" + start;
      if (end !== null) q += "&endTimestamp=" + end;
      return api.getData("notice-server/pursuit/events?" + q, this).then((res) => {
        if (requestId !== this.eventsRequestId || code !== this.selectedCode) return;
        if (res && res.state === 1) {
          let d = res.data || {};
          this.events = d.rows || [];
          this.eventsTotal = d.total == null ? this.events.length : d.total;
          if (!this.eventsTotal) this.eventsPage = 1;
          const lastPage = Math.max(1, Math.ceil(this.eventsTotal / this.eventPageSize));
          if (this.eventsPage > lastPage) {
            this.eventsPage = lastPage;
            this.events = [];
            this.detailRequestId++;
            this.selectedEventId = "";
            this.detailData = {};
            return this.fetchEvents();
          }
          const selected = this.events.find((event) => String(event.eventId) === this.selectedEventId);
          if (selected || this.events[0]) this.openEventDetail(selected || this.events[0]);
          else {
            this.detailRequestId++;
            this.selectedEventId = "";
            this.detailData = {};
          }
          this.applyRouteSelection(this.$route.query);
        } else {
          this.events = [];
          this.eventsTotal = 0;
          this.eventsPage = 1;
          this.detailRequestId++;
          this.selectedEventId = "";
          this.detailData = {};
        }
      });
    },
    changeMessagePage(p) {
      this.messagesPage = Math.max(1, Math.min(Math.trunc(Number(p)) || 1, this.messagePageCount));
      this.fetchMessages();
    },
    changeEventPage(p) {
      this.eventsPage = Math.max(1, Math.min(Math.trunc(Number(p)) || 1, this.eventPageCount));
      this.fetchEvents();
    },
    messageKey(i) {
      return String(i.reportTimestamp || i.id || i.annotatedImageUrl);
    },
    selectMessage(m) {
      this.selectedMessageKey = this.messageKey(m);
      this.selectedReportTimestamp = m.reportTimestamp;
      this.selectedTargetId =
        m.targets && m.targets[0] ? m.targets[0].targetId : "";
    },
    selectTarget(m, t) {
      this.selectedMessageKey = this.messageKey(m);
      this.selectedTargetId = t.targetId;
      this.selectedReportTimestamp = m.reportTimestamp;
    },
    isTargetSelected(m, t) {
      return (
        this.selectedMessageKey === this.messageKey(m) &&
        this.selectedTargetId === t.targetId
      );
    },
    isMessageSelected(message) {
      return !!(
        message &&
        this.messageKey(message) === this.selectedMessageKey &&
        message.reportTimestamp !== null &&
        message.reportTimestamp !== undefined &&
        String(message.reportTimestamp).trim() !== "" &&
        Number.isFinite(Number(message.reportTimestamp)) &&
        (message.targets || []).some((target) => target.targetId === this.selectedTargetId)
      );
    },
    showCommandMessage(res) {
      let message = res && (res.message || res.msg);
      if (!message) return;
      let failed = res.state === 0 || res.success === false || (res.code && res.code !== 200);
      this.$Notice[failed ? "error" : "success"]({
        title: this.langMap[failed ? "pursuitCommandFailed" : "pursuitCommandNotice"][this.lang],
        desc: message,
      });
    },
    authorizeSelected(message) {
      if (this.isCommandPending || !this.isMessageSelected(message)) return;
      const code = this.selectedCode;
      const requestId = ++this.commandRequestId;
      this.isCommandPending = true;
      return api
        .postData(
          "notice-server/pursuit/authorize",
          {
            code,
            targetId: this.selectedTargetId,
            reportTimestamp: message.reportTimestamp,
          },
          this
        )
        .then((res) => {
          if (requestId !== this.commandRequestId || code !== this.selectedCode) return;
          this.showCommandMessage(res);
          if (res && res.code === 200 && res.success !== false) this.fetchEvents();
        })
        .finally(() => {
          if (requestId === this.commandRequestId) this.isCommandPending = false;
        });
    },
    cancelEvent() {
      if (this.isCommandPending || !this.canCancelEvent) return;
      const code = this.selectedCode;
      const requestId = ++this.commandRequestId;
      this.isCommandPending = true;
      return api
        .postData(
          "notice-server/pursuit/cancel",
          { code },
          this
        )
        .then((res) => {
          if (requestId === this.commandRequestId && code === this.selectedCode)
            this.showCommandMessage(res);
        })
        .finally(() => {
          if (requestId === this.commandRequestId) this.isCommandPending = false;
        });
    },
    openEventDetail(e) {
      if (!e || !e.eventId) return;
      const eventId = String(e.eventId);
      const code = this.selectedCode;
      const requestId = ++this.detailRequestId;
      this.selectedEventId = eventId;
      this.detailData = { event: e };
      return api
        .getData(
          "notice-server/pursuit/events/" + encodeURIComponent(eventId),
          this
        )
        .then((res) => {
          if (requestId !== this.detailRequestId || code !== this.selectedCode || eventId !== this.selectedEventId) return;
          if (res && res.state === 1)
            this.detailData = res.data || { event: e };
        });
    },
    formatTime(v) {
      return v ? timeUtil.formatTime(v) : "-";
    },
    openImagePreview(url) {
      if (url) {
        this.previewImageUrl = String(url);
        this.$nextTick(this.syncPreviewImageSize);
      }
    },
    closeImagePreview() {
      this.previewImageUrl = "";
    },
    syncPreviewImageSize() {
      const image = this.$el && this.$el.querySelector('.pursuit-image-preview > img');
      if (!image || !image.naturalWidth || !image.naturalHeight) return;
      image.style.width = `${image.naturalWidth}px`;
      image.style.height = `${image.naturalHeight}px`;
    },
    eventStatusLabel(v) {
      const key = EVENT_STATUS_LABEL_KEYS[v];
      return key
        ? this.langMap[key][this.lang]
        : v || this.langMap.pursuitNoStatus[this.lang];
    },
    operationStatusLabel(v) {
      const key = OPERATION_STATUS_LABEL_KEYS[v];
      return key ? this.langMap[key][this.lang] : v || "-";
    },
    operationTypeLabel(v) {
      return this.langMap[
        v === "CANCEL" ? "pursuitCancelShort" : "pursuitAuthorizeShort"
      ][this.lang];
    },
    operationStatusTone(v) {
      return v === "ACK_OK"
        ? "tone-green"
        : v === "ACK_FAILED" || v === "SEND_FAILED" || v === "TIMEOUT"
        ? "tone-magenta"
        : v === "SENT" || v === "CREATED"
        ? "tone-cyan"
        : "tone-muted";
    },
    pursuitStateLabelFor(v) {
      const key = PURSUIT_STATE_LABEL_KEYS[v];
      return key
        ? this.langMap[key][this.lang]
        : this.langMap.pursuitNoStatus[this.lang];
    },
    conditionTone(v) {
      return v === 6
        ? "condition-active"
        : v === 4
        ? "condition-amber"
        : v === 1
        ? "condition-patrol"
        : v === 0
        ? "condition-backup"
        : "condition-muted";
    },
    activeTone(v) {
      return v ? "active-live" : "active-closed";
    },
    activeLabel(v) {
      return this.langMap[v ? "pursuitActive" : "pursuitEnded"][this.lang];
    },
    pursuitTone(v) {
      return !v
        ? "tone-muted"
        : v === "TEMP_LOST"
        ? "pursuit-lost"
        : v === "DETECTING"
        ? "pursuit-detecting"
        : "pursuit-tracking";
    },
    feedbackTone(v) {
      return v && /失败|超时|失效/.test(v)
        ? "feedback-error"
        : v && /成功|执行中/.test(v)
        ? "feedback-success"
        : "feedback-neutral";
    },
    statusTone(v) {
      return v === "ACTIVE"
        ? "tone-green"
        : v === "AUTHORIZING"
        ? "tone-cyan"
        : v === "CANCELLING"
        ? "tone-amber"
        : v === "AUTHORIZE_TIMEOUT" ||
          v === "CANCEL_TIMEOUT" ||
          (v && v.indexOf("FAILED") > -1)
        ? "tone-magenta"
        : "tone-muted";
    },
  },
};
</script>
<style lang="less" scoped>
.pursuit-page {
  min-height: 100vh;
  background: #020b13;
  color: #d7edf2;
  font-size: 13px;
}
.title-strip {
  height: 40px;
  padding: 0 32px 0 40px;
  border-bottom: 1px solid #03d9e8;
  background: #03121b;
  overflow: hidden;
  isolation: isolate;
}
.title-strip:before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 212px;
  height: 100%;
  background: #051923;
  clip-path: polygon(0 0, 100% 0, 88% 100%, 0 100%);
  border-right: 1px solid #08d7e5;
  z-index: -1;
}
.title-strip:after {
  content: "";
  position: absolute;
  left: 220px;
  right: 136px;
  top: 18px;
  height: 1px;
  background: #04d8e8;
  box-shadow: 0 0 8px rgba(0, 226, 241, 0.8);
  z-index: -1;
}
.title-shield {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 27px;
  margin-right: 10px;
  clip-path: polygon(50% 0, 92% 13%, 86% 73%, 50% 100%, 14% 73%, 8% 13%);
  background: #0be4f2;
  filter: drop-shadow(0 0 6px rgba(0, 225, 241, 0.85));
}
.title-shield:before {
  content: "";
  position: absolute;
  inset: 2px;
  clip-path: inherit;
  background: #053044;
}
.title-shield i {
  position: relative;
  width: 8px;
  height: 4px;
  border-left: 2px solid #0be4f2;
  border-bottom: 2px solid #0be4f2;
  transform: rotate(-45deg) translate(1px, -1px);
}
.title-strip h1 {
  position: relative;
  margin: 0;
  color: #effcff;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 1px;
  text-shadow: 0 0 8px rgba(0, 226, 241, 0.25);
}
.title-rule {
  position: absolute;
  left: 244px;
  right: 166px;
  top: 11px;
  height: 2px;
  background: repeating-linear-gradient(
    135deg,
    #10dbe7 0 18px,
    transparent 18px 24px
  );
  opacity: 0.8;
}
.title-slashes {
  position: absolute;
  right: 145px;
  top: 10px;
  color: #0de4ee;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 5px;
}
.title-slashes:before {
  content: "";
  position: absolute;
  right: 105px;
  top: 5px;
  width: 36px;
  height: 5px;
  background: repeating-linear-gradient(
    135deg,
    #08dce9 0 7px,
    transparent 7px 12px
  );
}
.robot-band {
  height: 98px;
  padding: 11px 31px 10px;
  box-sizing: border-box;
  border-bottom: 9px solid #3d4248;
  background: #06111b;
}
.robot-header-row {
  height: 76px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.robot-header-row > .detail-header {
  flex: 1;
  min-width: 0;
  margin: 0;
}
.robot-header-row > .detail-header /deep/ .header-right {
  display: none;
}
.robot-header-row > .detail-header /deep/ .robot-selection-container {
  min-width: 0;
  width: 100%;
  margin-right: 0;
}
.robot-header-row > .detail-header /deep/ .robot-selector {
  height: 76px;
}
.robot-header-row > .detail-header /deep/ .selector-inner {
  width: 100%;
  padding: 8px 10px;
}
.robot-header-row > .detail-header /deep/ .robot-item {
  height: 58px;
  min-width: 145px;
  margin: 0 5px;
}
.robot-header-row > .detail-header /deep/ .robot-content {
  padding: 0 13px;
}
.robot-header-row > .detail-header /deep/ .nav-btn {
  width: 42px;
  height: 42px;
}
.robot-header-row > .detail-header /deep/ .search-btn {
  width: 37px;
  height: 37px;
}
.monitor-card {
  height: 76px;
  flex: 0 0 252px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
  box-sizing: border-box;
  border: 1px solid #05d3e7;
  background: #061c29;
  clip-path: polygon(
    9px 0,
    calc(100% - 9px) 0,
    100% 9px,
    100% calc(100% - 9px),
    calc(100% - 9px) 100%,
    9px 100%,
    0 calc(100% - 9px),
    0 9px
  );
}
.monitor-copy {
  display: flex;
  align-items: center;
  gap: 11px;
}
.monitor-copy > .ivu-icon {
  font-size: 26px;
  color: #10e9ef;
}
.monitor-copy strong {
  display: block;
  color: #eafcff;
  font-size: 16px;
}
.monitor-copy span {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #a7d8dc;
  font-size: 11px;
}
.socket-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #72858b;
}
.socket-connected {
  background: #2cdda9;
  box-shadow: 0 0 8px #2cdda9;
}
.socket-error,
.socket-disconnected {
  background: #e87879;
}
.refresh-button {
  height: 35px;
  border: 1px solid #02d9e8;
  color: #d9fdff;
  background: #071c29;
}
.live-panel {
  min-height: 229px;
  margin-bottom: 4px;
  border: 1px solid #08b8d0;
  background: #061624;
  clip-path: polygon(
    0 13px,
    14px 0,
    340px 0,
    360px 17px,
    calc(100% - 12px) 17px,
    100% 30px,
    100% calc(100% - 13px),
    calc(100% - 14px) 100%,
    18px 100%,
    0 calc(100% - 14px)
  );
}
.live-heading {
  height: 45px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 27px;
  color: #12e5ed;
  font-size: 27px;
  font-weight: 600;
}
.live-heading > b {
  color: #19e6ee;
  letter-spacing: -5px;
}
.live-heading i {
  flex: 1;
  height: 1px;
  background: #0d7085;
}
.live-grid {
  display: grid;
  grid-template-columns: 31% 36% 33%;
  min-height: 177px;
  padding: 8px 38px 15px;
}
.live-grid label {
  display: block;
  color: #79a5b1;
  font-size: 15px;
}
.live-identity {
  display: flex;
  align-items: center;
  gap: 20px;
  border-right: 1px solid #1c6577;
}
.radar-mark {
  width: 94px;
  height: 94px;
  position: relative;
  border: 1px solid #00dbea;
  border-radius: 50%;
  box-shadow: inset 0 0 0 14px #063345, 0 0 15px #00cee7;
}
.radar-mark:before,
.radar-mark:after {
  content: "";
  position: absolute;
  left: 50%;
  top: 50%;
  background: #12e9fb;
  transform: translate(-50%, -50%);
}
.radar-mark:before {
  width: 1px;
  height: 120px;
}
.radar-mark:after {
  width: 120px;
  height: 1px;
}
.radar-mark span {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #13e9ff;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 13px 5px #00e1ff;
}
.live-identity strong {
  display: block;
  margin: 5px 0 8px;
  color: #12e3f0;
  font-size: 25px;
}
.live-identity b {
  display: inline-block;
  margin: 2px 0 8px;
  color: #e6f9fc;
  font-size: 17px;
}
.live-facts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-content: center;
  gap: 10px 12px;
  padding: 0 20px;
  border-right: 1px solid #1c6577;
}
.live-facts > div {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}
.live-facts label {
  flex: 0 0 auto;
}
.live-facts b {
  overflow: hidden;
  color: #e5f8fb;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.live-facts em,
.condition-block em,
.history-row em,
.summary em,
.operation-row em {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border: 1px solid currentColor;
  border-radius: 18px;
  font-style: normal;
  white-space: nowrap;
}
.live-facts em i,
.condition-block em i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}
.condition-block {
  display: flex;
  flex-direction: column;
  gap: 13px;
  padding-left: 24px;
}
.condition-block em {
  font-size: 17px;
}
.cancel-button {
  min-width: 175px;
  height: 50px;
  border: 2px solid #f212aa;
  color: #ff3ec1;
  background: #26102b;
}
.tone-cyan {
  color: #04e7df !important;
  border-color: #00a9b6 !important;
  background: #06353b;
}
.tone-magenta {
  color: #ff32c1 !important;
  border-color: #da159c !important;
  background: #3b0c32;
}
.tone-amber {
  color: #f0bd69 !important;
  border-color: #bd7b2c !important;
  background: #3a2813;
}
.tone-muted {
  color: #a7bbc0 !important;
  border-color: #546a71 !important;
  background: #26343a;
}
.live-empty,
.panel-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 170px;
  color: #6f949f;
  gap: 10px;
}
.workspace {
  display: grid;
  grid-template-columns: 38% 62%;
  min-height: 545px;
  background: #020b13;
}
.left-panel,
.right-panel {
  min-width: 0;
  border: 1px solid #07556a;
  background: #03141f;
}
.right-panel {
  container: pursuit-details / inline-size;
}
.left-panel {
  border-right: 0;
}
.tab-strip {
  display: flex;
  height: 42px;
}
.workspace-tab {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  padding: 0 10px;
  border: 1px solid #076278;
  color: #71a3ad;
  background: #03111a;
  font-size: 13px;
}
.workspace-tab.active {
  color: #18e5ed;
  background: #062234;
  border-color: #0ae4ef;
}
.history-tab {
  flex-basis: 52%;
}
.detection-list {
  padding: 10px 12px 0;
}
.detection-row {
  height: 72px;
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: 9px;
  padding: 0 12px 0 6px;
  border: 1px solid #073345;
  color: #c7e1e5;
  background: #03121c;
}
.detection-row.selected {
  border-color: #0ce3ec;
  background: #072a36;
}
.row-thumb {
  width: 78px;
  height: 39px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 78px;
  overflow: hidden;
  border: 0;
}
.row-thumb:not(.has-image) {
  border: 1px solid #0b4153;
  box-sizing: border-box;
}
.row-thumb img {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border: 1px solid #0b4153;
  box-sizing: border-box;
}
.row-time {
  flex: 1;
  margin-left: 14px;
}
.history-head {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  gap: 7px;
  padding: 12px;
  color: #13d7e3;
  font-size: 11px;
}
.history-row {
  display: grid;
  grid-template-columns: 1.12fr 0.9fr 1.05fr 1.05fr 0.9fr 16px;
  align-items: center;
  gap: 7px;
  margin: 0 12px 8px;
  padding: 0 9px;
  min-height: 54px;
  border: 1px solid #073445;
  color: #c8e0e3;
  background: #03121c;
  font-size: 10px;
}
.history-row.selected {
  border-color: #0ce3ec;
  background: #072a36;
}
.pagination-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 23px 12px 13px;
}
.pagination-bar button {
  height: 28px;
  min-width: 26px;
  border: 1px solid #07546a;
  color: #8ec7ce;
  background: #03131d;
}
.pagination-bar .page-active {
  border-color: #13e9ef;
  color: #efffff;
  background: #075064;
}
.pagination-bar span {
  margin-left: auto;
}
.message-view,
.history-detail-view {
  min-height: 543px;
  padding: 10px 16px;
}
.detail-time {
  height: 39px;
  display: flex;
  align-items: center;
  gap: 15px;
}
.detail-time strong {
  color: #14e3ec;
  font-size: 15px;
}
.message-detail {
  display: grid;
  grid-template-columns: 58% 42%;
  gap: 26px;
  min-height: 347px;
  padding: 0 2px 12px;
}
.annotated-image {
  display: block;
  width: max-content;
  max-width: 62%;
  min-height: 0;
  overflow: hidden;
  border: 0;
}
.annotated-image img {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 62vh;
  box-sizing: border-box;
  object-fit: contain;
  border: 1px solid #08adc6;
}
.target-selector {
  display: flex;
  flex-direction: column;
  flex: 1 1 220px;
  padding: 0;
}
.target-selector h2 {
  margin: 0 0 19px;
  color: #00e7ed;
  font-size: 17px;
}
.target-selector h2 small {
  font-size: 12px;
  font-weight: 400;
}
.target-row {
  height: 59px;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  padding: 0 17px;
  border: 1px solid #24414c;
  color: #d7e9eb;
  background: #07131d;
}
.target-row.selected {
  border-color: #00e5ed;
  background: #082937;
}
.target-row b {
  margin-left: 13px;
  font-size: 17px;
}
.radio-dot {
  width: 19px;
  height: 19px;
  border: 2px solid #98a9ac;
  border-radius: 50%;
}
.target-row.selected .radio-dot {
  border-color: #00e7ee;
  box-shadow: inset 0 0 0 4px #07131d;
}
.authorize-button {
  width: 100%;
  height: 57px;
  margin-top: auto;
  border: 2px solid #f318ae;
  color: #ff44c6;
  background: #2a102d;
  font-size: 18px;
}
.event-log {
  min-height: 109px;
  border: 1px solid #075369;
}
.event-log > strong {
  display: block;
  padding: 9px 17px;
  border-bottom: 1px solid #0b4b5c;
  color: #13e6ec;
}
.event-log-head,
.event-log-row {
  display: grid;
  grid-template-columns: 1.15fr 1.2fr 1fr 0.7fr;
  gap: 10px;
  padding: 8px 17px;
}
.event-log-head {
  color: #00dce7;
}
.magenta {
  color: #ff1eb5;
}
.success-text {
  color: #16ec95;
}
.history-title {
  height: 40px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 14px;
  border: 1px solid #075a6c;
  color: #11e5ed;
  font-size: 16px;
}
.history-title b {
  margin-left: auto;
}
.summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 265px;
  padding: 16px 18px 8px;
  border: 1px solid #07556a;
  border-top: 0;
}
.summary > div {
  padding-right: 20px;
}
.summary > div + div {
  padding-left: 24px;
  border-left: 1px solid #1c5c6c;
}
.summary p {
  display: flex;
  align-items: center;
  min-height: 32px;
  margin: 0;
}
.summary label {
  flex: 1;
  color: #79a5b1;
}
.summary p > b,
.summary p > em {
  margin-left: auto;
}
.history-detail h3 {
  padding: 10px 0 7px;
  margin: 0;
  color: #11e5ed;
}
.operations-head,
.operation-row {
  display: grid;
  grid-template-columns: 1.05fr 1.1fr 1.2fr 1.25fr 1.2fr 1fr;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-top: 1px solid #114a5a;
}
.operations-head {
  color: #00dce7;
  font-size: 11px;
}
.operation-row {
  min-height: 47px;
  font-size: 11px;
}
.operation-row small {
  display: block;
  margin-top: 3px;
  color: #83a5ad;
}
.mono {
  font-family: Consolas, "Courier New", monospace;
}
.pursuit-page /deep/ .ivu-spin-dot {
  background-color: #0ce3ec;
}
@media (max-width: 800px) {
  .workspace {
    grid-template-columns: 1fr;
  }
  .left-panel {
    border-right: 1px solid #07556a;
  }
  .live-grid {
    grid-template-columns: 1fr;
  }
  .message-detail {
    grid-template-columns: 1fr;
  }
}
.live-panel:has(.live-empty) {
  min-height: 150px;
}
.live-panel:has(.live-empty) .live-heading {
  height: 38px;
  font-size: 21px;
}
.list-mode:not(:has(.detection-row)):not(:has(.history-row)) {
  min-height: 230px;
}
.list-mode:not(:has(.detection-row)):not(:has(.history-row)):before {
  content: "";
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 185px;
  color: #5f8792;
  font-size: 12px;
}
.list-mode:not(:has(.detection-row)):not(:has(.history-row)) .pagination-bar {
  border-top: 1px solid #07556a;
  padding-top: 14px;
}
.workspace-tab {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.message-view .event-log {
  margin-top: 4px;
}
@media (max-width: 800px) {
  .workspace-tab {
    font-size: 11px;
    padding: 0 6px;
  }
  .robot-band {
    padding-left: 10px;
    padding-right: 10px;
  }
  .monitor-card {
    flex-basis: 190px;
  }
}
/* Keep the title strip in its own flow so its decorative geometry cannot cover the robot band. */
.title-strip {
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}
.title-shield {
  flex: 0 0 auto;
}
.title-strip h1 {
  position: relative;
  z-index: 1;
}
.live-panel:has(.live-empty) {
  min-height: 132px;
}
.live-panel:has(.live-empty) .live-heading {
  height: 36px;
}
.workspace {
  min-height: 500px;
  align-items: stretch;
}
.left-panel {
  display: flex;
  flex-direction: column;
}
.left-panel > .tab-strip {
  flex: 0 0 auto;
}
.left-panel > .list-mode {
  min-height: 0;
}
.left-panel > .list-mode:has(.detection-row),
.left-panel > .list-mode:has(.history-row) {
  display: flex;
  flex: 1;
  flex-direction: column;
}
.left-panel > .list-mode:has(.detection-row) .pagination-bar,
.left-panel > .list-mode:has(.history-row) .pagination-bar {
  margin-top: auto;
}
/* Cyberpunk generated-design overrides: these sit at the end of the component stylesheet, so they intentionally define the final visual frame. */
.title-strip {
  height: 38px;
  padding: 0 16px 0 30px;
}
.title-strip:before {
  width: 128px;
}
.title-shield {
  width: 19px;
  height: 22px;
  margin-right: 7px;
}
.title-strip h1 {
  font-size: 15px;
  letter-spacing: 0;
}
.title-rule {
  left: 142px;
  right: 60px;
  top: 17px;
  height: 2px;
}
.title-slashes {
  right: 24px;
  top: 14px;
  font-size: 9px;
}
.robot-band {
  height: 75px;
  padding: 6px 16px;
  border-bottom: 1px solid #3d4248;
}
.robot-header-row {
  height: 62px;
  gap: 10px;
}
.robot-header-row > .detail-header /deep/ .robot-selector {
  height: 62px;
}
.robot-header-row > .detail-header /deep/ .robot-item {
  height: 50px;
  min-width: 118px;
}
.monitor-card {
  height: 62px;
  flex: 0 0 188px;
  padding: 0 10px;
  justify-content: flex-start;
}
.monitor-copy > .ivu-icon {
  font-size: 20px;
}
.monitor-copy strong {
  font-size: 13px;
}
.monitor-copy span {
  font-size: 9px;
}
.refresh-button {
  height: 26px;
}
.live-panel {
  min-height: 148px;
  margin-bottom: 0;
  background: #020b13;
  clip-path: none;
}
.live-heading {
  height: 34px;
  padding: 0 14px;
  color: #12e5ed;
  font-size: 19px;
}
.live-heading i {
  height: 1px;
  background: #0d7085;
}
.live-empty {
  min-height: 108px;
  color: #6f949f;
  font-size: 12px;
}
.workspace {
  grid-template-columns: 38% 62%;
  min-height: 560px;
  background: #020b13;
}
.left-panel,
.right-panel {
  border: 1px solid #07556a;
  background: #03141f;
}
.left-panel {
  display: flex;
  flex-direction: column;
  border-right: 0;
}
.left-panel > .tab-strip {
  flex: 0 0 auto;
  height: 28px;
  border: 1px solid #076278;
  border-bottom: 0;
  background: #03111a;
}
.left-panel > .list-mode {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.workspace-tab {
  height: 28px;
  font-size: 11px;
  padding: 0 7px;
}
.workspace-tab.active {
  background: #062234;
  border-color: #0ae4ef;
}
.history-tab {
  flex-basis: 78%;
}
.list-mode:not(:has(.detection-row)):not(:has(.history-row)) {
  min-height: 0;
}
.list-mode:not(:has(.detection-row)):not(:has(.history-row)):before {
  min-height: 304px;
}
.list-mode:not(:has(.detection-row)):not(:has(.history-row)) .pagination-bar {
  margin-top: auto;
  padding-top: 10px;
  padding-bottom: 10px;
  border-top: 1px solid #07556a;
}
.history-head {
  padding-top: 7px;
  padding-bottom: 7px;
  font-size: 10px;
}
.history-row {
  min-height: 44px;
  font-size: 9px;
}
.pagination-bar {
  padding: 14px 8px 10px;
  margin-top: auto;
}
.pagination-bar button {
  height: 22px;
  min-width: 20px;
  font-size: 10px;
}
.pagination-bar span {
  font-size: 10px;
}
.message-view,
.history-detail-view {
  min-height: 560px;
  padding: 0;
  border: 1px solid #07556a;
  background: #03141f;
}
.detail-time {
  height: 24px;
  padding: 0 12px;
}
.message-detail {
  grid-template-columns: 56% 44%;
  gap: 18px;
  min-height: 356px;
  padding: 0 10px 10px;
}
.annotated-image {
  min-height: 356px;
}
.target-selector h2 {
  font-size: 13px;
  margin-bottom: 14px;
}
.target-row {
  height: 43px;
  margin-bottom: 7px;
  padding: 0 12px;
}
.target-row b {
  font-size: 13px;
}
.authorize-button {
  height: 44px;
  font-size: 14px;
}
.event-log {
  margin: 0 10px 10px;
  min-height: 96px;
}
.event-log > strong {
  padding: 7px 12px;
  font-size: 11px;
}
.event-log-head,
.event-log-row {
  padding: 7px 12px;
  font-size: 10px;
}
.message-view:not(:has(.message-detail)) .detail-time,
.message-view:not(:has(.message-detail)) .event-log {
  display: none;
}
.summary {
  min-height: 260px;
  padding: 12px 12px 8px;
}
.operations-head,
.operation-row {
  padding: 7px 8px;
  font-size: 9px;
}
@media (max-width: 800px) {
  .workspace {
    grid-template-columns: 1fr;
  }
  .left-panel {
    border-right: 1px solid #07556a;
  }
  .robot-band {
    padding-left: 10px;
    padding-right: 10px;
  }
  .monitor-card {
    flex-basis: 190px;
  }
}
</style>
<style scoped>
.pursuit-image-preview {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  overflow: auto;
  padding: 72px 34px 34px;
  background: rgba(1, 12, 24, .9);
  backdrop-filter: blur(6px);
}
.pursuit-image-preview > img {
  display: block;
  flex: 0 0 auto;
  max-width: none;
  max-height: none;
  width: auto;
  height: auto;
  object-fit: contain;
  margin: auto;
  border: 1px solid rgba(86, 220, 255, .65);
  border-radius: 8px;
  box-shadow: 0 0 24px rgba(21, 204, 255, .3), 0 18px 60px rgba(0, 0, 0, .6);
}
.live-source-image,
.history-source-image img,
.row-thumb img,
.annotated-image img {
  cursor: zoom-in;
}
.pursuit-image-preview > img {
  cursor: zoom-out;
}
.pursuit-image-preview-close {
  position: fixed;
  top: 16px;
  right: 20px;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(137, 229, 255, .55);
  border-radius: 50%;
  color: #d9f9ff;
  background: rgba(8, 46, 68, .85);
  font-size: 24px;
  line-height: 28px;
  cursor: pointer;
  z-index: 2001;
}
.pursuit-image-preview-close:hover {
  color: #fff;
  border-color: #62e6ff;
  box-shadow: 0 0 12px rgba(98, 230, 255, .5);
}
</style>
<style scoped>
/* Final semantic lamp colors, after the shared muted rules. */
.pursuit-page .live-facts em.tone-muted.event-status-muted i { background: #e7b45c !important; box-shadow: 0 0 7px rgba(231, 180, 92, .75) !important; }
.pursuit-page .live-facts em.tone-muted.tracking-status-muted i { background: #4edec9 !important; box-shadow: 0 0 7px rgba(78, 222, 201, .75) !important; }
.pursuit-page .condition-status.condition-muted i { background: #bf9ee8 !important; box-shadow: 0 0 7px rgba(191, 158, 232, .75) !important; }
.pursuit-page .live-facts em.tone-muted.event-status-muted,
.pursuit-page .live-facts em.tone-muted.tracking-status-muted,
.pursuit-page .condition-status.condition-muted {
  animation: none !important;
  position: relative;
  overflow: hidden;
  isolation: isolate;
}
.pursuit-page .live-facts em.tone-muted.event-status-muted::after,
.pursuit-page .live-facts em.tone-muted.tracking-status-muted::after,
.pursuit-page .condition-status.condition-muted::after {
  content: '';
  position: absolute;
  z-index: 0;
  inset: -2px auto -2px -40%;
  width: 36%;
  transform: skewX(-24deg);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, .32), transparent);
  filter: blur(2px);
  animation: live-liquid-glass 3.4s ease-in-out infinite;
}
.pursuit-page .live-facts em.tone-muted.event-status-muted i,
.pursuit-page .live-facts em.tone-muted.tracking-status-muted i,
.pursuit-page .condition-status.condition-muted i {
  position: relative;
  z-index: 1;
  animation: live-missing-breathe 1.8s ease-in-out infinite;
}
.pursuit-page .live-facts em.tone-muted.event-status-muted::after,
.pursuit-page .live-facts em.tone-muted.tracking-status-muted::after {
  content: '';
  position: absolute;
  z-index: 0;
  inset: -2px auto -2px -40%;
  width: 36%;
  transform: skewX(-24deg);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, .44), transparent);
  filter: blur(2px);
  opacity: 1;
  animation: live-liquid-glass 3.4s ease-in-out infinite;
}
</style>
<style scoped>
/* Final table and live-card alignment overrides. */
.pursuit-page .history-head {
  grid-template-columns: minmax(0, 1fr) 112px 112px minmax(0, 1fr) !important;
  text-align: center !important;
}
.pursuit-page .history-row {
  grid-template-columns: minmax(0, 1fr) 112px 112px minmax(0, 1fr) 16px !important;
  text-align: center !important;
}
.pursuit-page .history-row > span,
.pursuit-page .history-row > em,
.pursuit-page .history-head > span {
  justify-self: stretch !important;
  text-align: center !important;
}
.pursuit-page .history-row > em {
  justify-content: center !important;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pursuit-page .history-row > .ivu-icon {
  justify-self: center !important;
}
.pursuit-page .history-detail .operations-head,
.pursuit-page .history-detail .operation-row {
  grid-template-columns: 1fr 1.35fr 1.25fr 1.45fr 1.25fr !important;
  text-align: center !important;
}
.pursuit-page .history-detail .operations-head > span,
.pursuit-page .history-detail .operation-row > span {
  min-width: 0 !important;
  text-align: center !important;
  justify-self: stretch !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pursuit-page .history-detail .operation-row > span > em {
  justify-content: center !important;
}
.pursuit-page .live-identity {
  display: grid !important;
  grid-template-columns: 58px minmax(0, 1fr) 104px !important;
  align-items: stretch !important;
  column-gap: 16px !important;
}
.pursuit-page .live-empty {
  display: flex !important;
  flex-direction: row;
  align-items: center !important;
  justify-content: center !important;
  gap: 32px;
  padding: 16px 6%;
  box-sizing: border-box;
  text-align: left;
  color: #9bb8c2;
  font-size: 13px;
  letter-spacing: 0;
  background: transparent;
}
.pursuit-page .live-empty-event {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: 20px;
}
.pursuit-page .live-empty-mark {
  position: relative;
  flex: 0 0 54px;
  width: 54px;
  height: 54px;
  border: 1px solid rgba(0, 220, 232, .42);
  border-radius: 50%;
  box-shadow: inset 0 0 0 6px rgba(6, 51, 69, .48), 0 0 8px rgba(0, 206, 231, .2);
  opacity: .8;
  overflow: hidden;
}
.pursuit-page .live-empty-mark::before,
.pursuit-page .live-empty-mark::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  background: rgba(18, 233, 251, .72);
  transform: translate(-50%, -50%);
}
.pursuit-page .live-empty-mark::before {
  width: 36px;
  height: 36px;
  border: 1px solid rgba(0, 220, 232, .24);
  border-radius: 50%;
  background: transparent;
}
.pursuit-page .live-empty-mark::after {
  inset: 0;
  width: auto;
  height: auto;
  border-radius: 50%;
  background: conic-gradient(from 0deg, transparent 270deg, rgba(18, 233, 251, .22) 350deg, rgba(18, 233, 251, .65) 360deg);
  transform: none;
  animation: pursuitIdleRadar 6s linear infinite;
}
@keyframes pursuitIdleRadar {
  to { transform: rotate(360deg); }
}
.pursuit-page .live-empty-mark span {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #13e9ff;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 6px 2px #00e1ff;
}
.pursuit-page .live-empty-mark i {
  position: absolute;
  inset: -4px;
  border: 1px solid rgba(0, 220, 232, .14);
  border-radius: 50%;
}
.pursuit-page .live-empty-copy {
  display: flex;
  align-items: center;
  gap: 12px;
}
.pursuit-page .live-empty-copy strong {
  color: #a5bbc8;
  font-size: 16px;
  line-height: 1.5;
  font-weight: 500;
}
.pursuit-page .live-empty-copy span {
  color: rgba(0, 220, 232, .45);
  font-size: 11px;
  letter-spacing: 3px;
}
.pursuit-page .live-empty .live-empty-condition {
  display: flex !important;
  flex: 0 1 250px;
  width: auto;
  min-width: 0;
  padding: 4px 0 4px 32px;
  border-left: 1px solid rgba(82, 134, 156, .28);
}
.pursuit-page .live-empty-condition > .live-field {
  display: flex !important;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
  gap: 8px;
}
.pursuit-page .live-empty .live-empty-condition > .live-field > label {
  flex: 0 0 auto !important;
  max-width: none !important;
  color: #8aa6b7 !important;
  font-size: 14px !important;
}
.pursuit-page .live-panel .live-empty .condition-status {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  box-sizing: border-box;
  padding: 7px 12px;
  border: 1px solid currentColor;
  border-radius: 4px !important;
  clip-path: none !important;
  font-size: 16px !important;
  line-height: 1.4;
  font-style: normal;
  overflow: hidden;
}
.pursuit-page .live-empty .condition-status > i {
  flex: 0 0 6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: pursuitChipLed 3.4s ease-in-out infinite;
}
.pursuit-page .live-panel .live-empty .live-empty-condition .condition-status.condition-green {
  color: #4ec9a4 !important;
  border-color: rgba(78, 201, 164, .48) !important;
  background: linear-gradient(180deg, rgba(22, 70, 60, .7), rgba(10, 30, 27, .76)) !important;
  box-shadow: inset 0 0 10px rgba(78, 201, 164, .1) !important;
}
.pursuit-page .live-panel .live-empty .live-empty-condition .condition-status.condition-green > i {
  background: #4ec9a4 !important;
  box-shadow: 0 0 7px rgba(78, 201, 164, .75) !important;
}
.pursuit-page .live-empty .condition-status.is-empty {
  color: #9eafbd !important;
  border-color: rgba(132, 158, 177, .3) !important;
  background: rgba(49, 72, 88, .18) !important;
  box-shadow: none !important;
}
.pursuit-page .live-empty .condition-status.is-empty > i {
  background: #8fa2b0 !important;
  box-shadow: 0 0 6px rgba(143, 162, 176, .45) !important;
}
@media (max-width: 800px) {
  .pursuit-page .live-empty { gap: 16px; padding: 16px; }
  .pursuit-page .live-empty-event { gap: 12px; }
  .pursuit-page .live-empty .live-empty-condition { flex-basis: 210px; padding-left: 16px; }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-empty-mark::after,
  .pursuit-page .live-empty .condition-status > i { animation: none !important; }
}
.pursuit-page .live-source-image {
  display: block;
  width: 104px;
  height: auto;
  max-height: 100%;
  aspect-ratio: auto;
  object-fit: contain;
  border: 1px solid rgba(0, 220, 232, .42);
  border-radius: 4px;
  background: #061624;
  box-shadow: 0 0 8px rgba(0, 220, 232, .18);
  grid-column: 3 !important;
  grid-row: 1 !important;
}
.pursuit-page .live-identity > div:last-child {
  grid-column: 2 !important;
  grid-row: 1 !important;
  left: 0 !important;
  width: 100% !important;
}
.pursuit-page .live-missing-value {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 20px;
  padding: 0 9px;
  border: 1px solid rgba(195, 245, 249, .52);
  border-radius: 2px;
  color: #d4eef0;
  background: linear-gradient(135deg, rgba(215, 255, 255, .18), rgba(72, 134, 145, .1) 45%, rgba(10, 34, 47, .52));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .25), inset 0 -1px 0 rgba(120, 228, 235, .15), 0 0 10px rgba(68, 207, 218, .16);
  backdrop-filter: blur(8px) saturate(1.4);
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  overflow: hidden;
  isolation: isolate;
}
.pursuit-page .live-missing-value::before {
  width: 6px;
  height: 6px;
  border-radius: 0;
  transform: rotate(45deg);
  background: #aebdc0;
  box-shadow: 0 0 7px rgba(221, 252, 255, .72);
}
.pursuit-page .live-missing-value::after {
  content: '';
  position: absolute;
  z-index: -1;
  inset: -2px auto -2px -40%;
  width: 36%;
  transform: skewX(-24deg);
  background: linear-gradient(90deg, transparent, rgba(238, 255, 255, .42), transparent);
  filter: blur(2px);
  animation: live-liquid-glass 3.4s ease-in-out infinite;
}
@keyframes live-liquid-glass {
  0%, 18% { left: -40%; opacity: 0; }
  42% { opacity: .9; }
  78%, 100% { left: 112%; opacity: 0; }
}
.pursuit-page .live-label-event,
.pursuit-page .live-label-event-status { color: #e7b45c !important; }
.pursuit-page .live-label-target { color: #61d2e1 !important; }
.pursuit-page .live-label-tracking { color: #4edec9 !important; }
.pursuit-page .live-label-feedback { color: #d890cf !important; }
.pursuit-page .live-label-source-time { color: #57cfe4 !important; }
.pursuit-page .live-label-start-time { color: #60d79d !important; }
.pursuit-page .live-label-condition { color: #bf9ee8 !important; }
.pursuit-page .live-label-event + strong,
.pursuit-page .live-label-event + .live-missing-value,
.pursuit-page .live-label-event-status + em { color: #e7b45c !important; }
.pursuit-page .live-facts em.event-status-muted,
.pursuit-page .live-facts em.tracking-status-muted {
  color: #e7b45c !important;
  border-color: currentColor !important;
  background: rgba(231, 180, 92, .10) !important;
}
.pursuit-page .live-facts em.tone-muted.event-status-muted { color: #e7b45c !important; border-color: #e7b45c !important; }
.pursuit-page .live-facts em.tone-muted.tracking-status-muted { color: #4edec9 !important; border-color: #4edec9 !important; }
.pursuit-page .live-facts em.tone-muted.event-status-muted i { background: #e7b45c !important; }
.pursuit-page .live-facts em.tone-muted.tracking-status-muted i { background: #4edec9 !important; }
.pursuit-page .live-facts em.tracking-status-muted {
  color: #4edec9 !important;
  background: rgba(78, 222, 201, .10) !important;
}
.pursuit-page .live-facts em.event-status-muted i,
.pursuit-page .live-facts em.tracking-status-muted i {
  background: #e7b45c !important;
  box-shadow: 0 0 5px rgba(231, 180, 92, .55) !important;
}
.pursuit-page .live-facts em.tracking-status-muted i {
  background: #4edec9 !important;
  box-shadow: 0 0 5px rgba(78, 222, 201, .55) !important;
}
.pursuit-page .live-label-target + b,
.pursuit-page .live-label-target + .live-missing-value { color: #61d2e1 !important; }
.pursuit-page .live-label-feedback + b,
.pursuit-page .live-label-feedback + .live-missing-value { color: #d890cf !important; }
.pursuit-page .live-label-source-time + b,
.pursuit-page .live-label-source-time + .live-missing-value { color: #57cfe4 !important; }
.pursuit-page .live-label-start-time + b,
.pursuit-page .live-label-start-time + .live-missing-value { color: #60d79d !important; }
.pursuit-page .live-label-condition + em { color: #bf9ee8 !important; }
.pursuit-page .live-missing-value { border-color: currentColor; }
.pursuit-page .live-facts em.tone-muted,
.pursuit-page .condition-status.condition-muted {
  font-size: 12px !important;
}
.pursuit-page .live-facts em.tone-muted,
.pursuit-page .condition-status.condition-muted {
  border-radius: 2px !important;
  color: #8d9a9e !important;
  border-color: rgba(141, 154, 158, .52) !important;
  background: rgba(93, 126, 137, .12) !important;
  box-shadow: 0 0 7px rgba(141, 154, 158, .12) !important;
  animation: none;
}
.pursuit-page .live-facts em.tone-muted i,
.pursuit-page .condition-status.condition-muted i {
  background: #8d9a9e !important;
  box-shadow: 0 0 5px rgba(167, 184, 188, .55) !important;
  animation: live-missing-breathe 1.8s ease-in-out infinite;
}
.pursuit-page .condition-status.condition-muted {
  color: #bf9ee8 !important;
  border-color: #bf9ee8 !important;
  background: linear-gradient(135deg, rgba(239, 223, 255, .2), rgba(145, 106, 177, .1) 45%, rgba(34, 23, 50, .54)) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .22), inset 0 -1px 0 rgba(211, 178, 242, .14), 0 0 10px rgba(188, 146, 225, .18) !important;
  backdrop-filter: blur(8px) saturate(1.4);
  position: relative;
  overflow: hidden;
}
.pursuit-page .condition-status.condition-muted::after {
  content: '';
  position: absolute;
  z-index: 0;
  inset: -2px auto -2px -40%;
  width: 36%;
  transform: skewX(-24deg);
  background: linear-gradient(90deg, transparent, rgba(255, 250, 255, .44), transparent);
  filter: blur(2px);
  animation: live-liquid-glass 3.4s ease-in-out infinite;
}
.pursuit-page .condition-status.condition-muted i {
  background: #bf9ee8 !important;
  box-shadow: 0 0 6px rgba(191, 158, 232, .65) !important;
}
.pursuit-page .live-missing-value::before {
  content: '';
  position: static;
  display: inline-block;
  flex: 0 0 6px;
  width: 6px;
  height: 6px;
  margin-right: 5px;
  border-radius: 50%;
  background: #8d9a9e;
  box-shadow: 0 0 5px rgba(167, 184, 188, .55);
  transform: none;
  animation: live-missing-breathe 1.8s ease-in-out infinite;
}
.pursuit-page .live-missing-value::after {
  content: none;
}
@keyframes live-missing-breathe {
  0%, 100% { opacity: .38; transform: scale(.82); }
  50% { opacity: 1; transform: scale(1.08); }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-missing-value::before,
  .pursuit-page .live-missing-value::after { animation: none; }
}
.pursuit-page .live-identity > div:last-child > .live-missing-value:nth-child(2) {
  grid-column: 2 !important;
  grid-row: 1 !important;
  justify-self: start;
}
.pursuit-page .live-identity > div:last-child > .live-missing-value:nth-child(4) {
  grid-column: 2 !important;
  grid-row: 2 !important;
  justify-self: start;
}
@media (max-width: 1100px) {
  .pursuit-page .live-identity {
    grid-template-columns: 44px minmax(0, 1fr) 84px !important;
    column-gap: 10px !important;
  }
  .pursuit-page .live-source-image {
    width: 84px;
    height: auto;
  }
}
</style>
<style scoped>
.history-source-image {
  grid-column: 1;
  grid-row: 1;
  display: block;
  width: max-content;
  max-width: 100%;
  height: auto;
  align-self: start;
  min-height: 0;
  margin: 0;
  border: 0;
  overflow: hidden;
}
.history-source-image img {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: min(46vh, 480px);
  box-sizing: border-box;
  object-fit: contain;
  border: 1px solid rgba(0, 220, 232, .42);
  border-radius: 5px;
}
.history-detail .summary:has(.history-source-image) {
  grid-template-columns: minmax(0, .9fr) minmax(0, 1fr) minmax(0, 1fr);
  min-height: 0;
  padding: 8px 12px;
}
.history-detail .summary:has(.history-source-image) > div {
  min-width: 0;
  padding-right: 10px;
}
.history-detail .summary:has(.history-source-image) > .history-source-image {
  grid-column: 1;
  grid-row: 1;
  padding: 0 !important;
  box-sizing: border-box !important;
}
.history-detail .summary:has(.history-source-image) > div:nth-child(2) {
  grid-column: 2;
}
.history-detail .summary:has(.history-source-image) > div:nth-child(3) {
  grid-column: 3;
  padding-left: 12px;
}
.history-detail .summary p {
  gap: 8px;
  padding: 4px 0;
}
.history-detail .summary label {
  flex: 0 0 38%;
  min-width: 0;
}
.history-detail .summary p > b,
.history-detail .summary p > em {
  flex: 1 1 0;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
}
@container pursuit-details (max-width: 720px) {
  .pursuit-page .history-detail .summary:has(.history-source-image) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
  .pursuit-page .history-detail .summary:has(.history-source-image) > .history-source-image {
    grid-column: 1 / -1;
  }
  .pursuit-page .history-detail .summary:has(.history-source-image) > div:nth-child(2) {
    grid-column: 1;
    padding-left: 0;
    border-left: 0;
  }
  .pursuit-page .history-detail .summary:has(.history-source-image) > div:nth-child(3) {
    grid-column: 2;
  }
}
@container pursuit-details (max-width: 460px) {
  .pursuit-page .history-detail .summary:has(.history-source-image) {
    grid-template-columns: minmax(0, 1fr);
  }
  .pursuit-page .history-detail .summary:has(.history-source-image) > div:nth-child(3) {
    grid-column: 1;
    padding-left: 0;
    border-left: 0;
  }
}
/* Narrow viewport guard: keep every live-event column inside its cell. */
.pursuit-page .live-grid {
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1.45fr) minmax(0, .75fr) minmax(0, .65fr) !important;
  min-width: 0 !important;
}
.pursuit-page .live-grid > *,
.pursuit-page .live-identity,
.pursuit-page .live-facts,
.pursuit-page .condition-block,
.pursuit-page .cancel-block { min-width: 0 !important; box-sizing: border-box; }
.pursuit-page .live-identity { gap: 7px !important; padding-right: 7px !important; }
.pursuit-page .live-identity { gap: 13px !important; }
.pursuit-page .live-identity > div:last-child {
  display: grid !important;
  grid-template-columns: minmax(0, 42%) minmax(0, 58%) !important;
  width: 100% !important;
  min-width: 0 !important;
  column-gap: 7px !important;
}
.pursuit-page .live-identity > div:last-child > label,
.pursuit-page .live-identity > div:last-child > strong,
.pursuit-page .live-identity > div:last-child > b {
  min-width: 0 !important;
  max-width: 100% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}
.pursuit-page .live-identity > div:last-child > label { font-size: clamp(9px, .72vw, 12px) !important; }
.pursuit-page .live-identity > div:last-child > strong { font-size: clamp(11px, 1.05vw, 17px) !important; line-height: 1.2 !important; }
.pursuit-page .live-identity > div:last-child > b { font-size: clamp(10px, .78vw, 13px) !important; }
.pursuit-page .live-facts > div { min-width: 0 !important; gap: 4px !important; }
.pursuit-page .live-facts label { max-width: 48% !important; overflow: hidden !important; text-overflow: ellipsis !important; white-space: nowrap !important; font-size: clamp(9px, .7vw, 12px) !important; }
.pursuit-page .live-facts b,
.pursuit-page .live-facts em,
.pursuit-page .condition-block em,
.pursuit-page .cancel-button { min-width: 0 !important; max-width: 100% !important; overflow: hidden !important; text-overflow: ellipsis !important; white-space: nowrap !important; }
.pursuit-page .live-facts b { flex: 1 1 auto; font-size: clamp(9px, .72vw, 12px) !important; }
.pursuit-page .live-facts em,
.pursuit-page .condition-block em { padding: 2px 5px !important; font-size: clamp(9px, .72vw, 12px) !important; }
.pursuit-page .condition-block { padding-left: 6px !important; padding-right: 5px !important; gap: 6px !important; overflow: hidden !important; }
.pursuit-page .cancel-block { padding-left: 6px !important; gap: 5px !important; overflow: hidden !important; }
.pursuit-page .cancel-block .cancel-button { width: 100% !important; height: clamp(28px, 2.2vw, 35px) !important; padding: 0 4px !important; font-size: clamp(9px, .72vw, 12px) !important; }
@media (max-width: 760px) {
.pursuit-page .live-panel { height: auto !important; min-height: 190px !important; max-height: none !important; }
.pursuit-page .live-grid { grid-template-columns: 1fr 1fr !important; grid-auto-rows: minmax(72px, auto); height: auto !important; min-height: 150px; overflow: visible !important; }
.pursuit-page .live-identity { grid-row: span 2; }
}
</style>

<style scoped>
/* Last-word override for the rebuilt live panel (must stay last in the file). */
.pursuit-page .live-panel .live-identity > .live-identity-fields {
display: flex !important;
grid-template-columns: none !important;
grid-auto-rows: auto !important;
position: static !important;
left: auto !important;
top: auto !important;
width: auto !important;
height: auto !important;
flex: 1 1 auto !important;
flex-direction: column !important;
gap: 9px !important;
min-width: 0 !important;
padding: 0 !important;
margin: 0 !important;
column-gap: normal !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field {
display: flex !important;
flex-direction: row !important;
align-items: center !important;
grid-template-columns: none !important;
grid-column: auto !important;
grid-row: auto !important;
gap: 8px !important;
width: 100% !important;
max-width: 100% !important;
min-width: 0 !important;
height: auto !important;
padding: 0 !important;
margin: 0 !important;
position: static !important;
left: auto !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em {
position: static !important;
left: auto !important;
top: auto !important;
grid-column: auto !important;
width: auto !important;
height: auto !important;
min-width: 0 !important;
max-width: 100% !important;
padding: 0 !important;
margin: 0 !important;
box-sizing: content-box !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
flex: 0 1 auto !important;
max-width: 62% !important;
font-size: clamp(11px, .84vw, 15px) !important;
font-weight: 600;
letter-spacing: .5px;
white-space: nowrap;
overflow: hidden;
text-overflow: ellipsis;
color: #9fd4e8;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before {
content: "";
display: inline-block;
width: 2px;
height: 11px;
margin-right: 7px;
vertical-align: -1px;
border-radius: 1px;
background: #35e2ff;
box-shadow: 0 0 6px rgba(53, 226, 255, .7);
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em.live-chip {
display: inline-flex !important;
align-items: center;
gap: 6px;
flex: 0 1 auto !important;
padding: 4px 11px !important;
border-radius: 999px;
border: 1px solid rgba(53, 226, 255, .3);
background: linear-gradient(180deg, rgba(19, 72, 110, .62), rgba(8, 28, 46, .62));
font-size: clamp(11px, .82vw, 14px) !important;
font-style: normal;
font-weight: 600;
white-space: nowrap;
overflow: hidden;
text-overflow: ellipsis;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em.live-chip > i {
flex: 0 0 auto;
width: 6px;
height: 6px;
border-radius: 50%;
background: currentColor;
box-shadow: 0 0 7px currentColor;
animation: pursuitLiveDot 2.4s ease-in-out infinite;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em.live-chip.tone-cyan {
color: #62e6ff;
border-color: rgba(53, 226, 255, .42);
background: linear-gradient(180deg, rgba(16, 86, 122, .6), rgba(6, 30, 48, .6));
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em.live-chip.is-empty {
color: #8fa4b2;
border-color: rgba(143, 164, 178, .3);
background: linear-gradient(180deg, rgba(38, 56, 70, .5), rgba(16, 26, 36, .55));
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em.live-chip.is-empty > i {
color: #7d93a3;
box-shadow: 0 0 6px rgba(125, 147, 163, .7);
animation: pursuitLiveDot 3.2s ease-in-out infinite;
}
@keyframes pursuitLiveDot {
0%, 100% { opacity: 1; transform: scale(1); }
50% { opacity: .55; transform: scale(.82); }
}
</style>

<style scoped>
/* Final override: neutralizes legacy !important grid rules on the rebuilt live panel. */
.pursuit-page .live-panel .live-identity > .live-identity-fields {
display: flex !important;
grid-template-columns: none !important;
position: static !important;
left: auto !important;
width: auto !important;
flex: 1 1 auto !important;
flex-direction: column !important;
gap: 9px !important;
min-width: 0 !important;
padding: 0 !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field {
display: flex !important;
flex-direction: row !important;
align-items: center !important;
grid-template-columns: none !important;
gap: 8px !important;
width: 100% !important;
max-width: 100% !important;
min-width: 0 !important;
padding: 0 !important;
margin: 0 !important;
left: auto !important;
position: static !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em {
position: static !important;
left: auto !important;
width: auto !important;
min-width: 0 !important;
max-width: 100% !important;
padding: 0 !important;
margin: 0 !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
flex: 0 1 auto !important;
max-width: 62% !important;
font-size: clamp(11px, .84vw, 15px) !important;
font-weight: 600;
letter-spacing: .5px;
white-space: nowrap;
overflow: hidden;
text-overflow: ellipsis;
color: #9fd4e8;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before {
content: "";
display: inline-block;
width: 2px;
height: 11px;
margin-right: 7px;
vertical-align: -1px;
border-radius: 1px;
background: #35e2ff;
box-shadow: 0 0 6px rgba(53, 226, 255, .7);
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em.live-chip {
display: inline-flex !important;
align-items: center;
gap: 6px;
flex: 0 1 auto !important;
padding: 4px 11px !important;
border-radius: 999px;
border: 1px solid rgba(53, 226, 255, .3);
background: linear-gradient(180deg, rgba(19, 72, 110, .62), rgba(8, 28, 46, .62));
font-size: clamp(11px, .82vw, 14px) !important;
font-style: normal;
font-weight: 600;
white-space: nowrap;
overflow: hidden;
text-overflow: ellipsis;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em.live-chip > i {
flex: 0 0 auto;
width: 6px;
height: 6px;
border-radius: 50%;
background: currentColor;
box-shadow: 0 0 7px currentColor;
animation: pursuitLiveDot 2.4s ease-in-out infinite;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em.live-chip.tone-cyan {
color: #62e6ff;
border-color: rgba(53, 226, 255, .42);
background: linear-gradient(180deg, rgba(16, 86, 122, .6), rgba(6, 30, 48, .6));
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em.live-chip.is-empty {
color: #8fa4b2;
border-color: rgba(143, 164, 178, .3);
background: linear-gradient(180deg, rgba(38, 56, 70, .5), rgba(16, 26, 36, .55));
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em.live-chip.is-empty > i {
color: #7d93a3;
box-shadow: 0 0 6px rgba(125, 147, 163, .7);
animation: pursuitLiveDot 3.2s ease-in-out infinite;
}
@keyframes pursuitLiveDot {
0%, 100% { opacity: 1; transform: scale(1); }
50% { opacity: .55; transform: scale(.82); }
}
</style>

<style scoped>
/* Live event panel rebuilt from the sunburst design reference. */
.pursuit-page .live-panel {
position: relative;
display: flex;
flex-direction: column;
min-width: 0;
overflow: hidden;
border: 1px solid rgba(31, 214, 255, .38) !important;
border-radius: 14px !important;
background: linear-gradient(180deg, rgba(10, 34, 56, .92), rgba(4, 15, 28, .94)) !important;
box-shadow: inset 0 0 34px rgba(24, 148, 214, .16), 0 0 20px rgba(20, 190, 255, .12) !important;
}
.pursuit-page .live-panel::before {
content: "";
position: absolute;
top: 0;
left: 8%;
right: 8%;
height: 2px;
background: linear-gradient(90deg, transparent, #35e2ff, #7cf0ff, #35e2ff, transparent);
filter: drop-shadow(0 0 6px rgba(53, 226, 255, .85));
pointer-events: none;
z-index: 3;
}
.pursuit-page .live-heading {
display: flex;
align-items: center;
gap: 10px;
padding: 8px 16px !important;
background: linear-gradient(90deg, rgba(20, 96, 152, .5), rgba(10, 40, 70, .16) 62%, transparent) !important;
border-bottom: 1px solid rgba(31, 214, 255, .2) !important;
}
.pursuit-page .live-heading > b:first-child {
width: 4px;
height: 18px;
flex: 0 0 4px;
padding: 0 !important;
border-radius: 2px;
font-size: 0 !important;
background: linear-gradient(180deg, #6ff3ff, #17b6e8);
box-shadow: 0 0 8px rgba(53, 226, 255, .8);
}
.pursuit-page .live-heading strong {
font-size: clamp(15px, 1.32vw, 24px) !important;
font-weight: 700;
letter-spacing: 1px;
color: #eaf9ff !important;
text-shadow: 0 0 10px rgba(80, 220, 255, .45);
}
.pursuit-page .live-heading i {
flex: 1;
height: 1px;
background: linear-gradient(90deg, rgba(31, 214, 255, .45), transparent);
}
.pursuit-page .live-heading > b:last-child { display: none !important; }
.pursuit-page .live-grid {
display: grid;
grid-template-columns: minmax(0, 1.55fr) minmax(0, 1.25fr) minmax(0, 1.25fr) minmax(0, .78fr) !important;
align-items: stretch;
flex: 1;
gap: 0 !important;
padding: 10px 0 !important;
min-width: 0;
}
.pursuit-page .live-grid > * {
min-width: 0;
padding: 4px 16px !important;
border-right: 1px solid rgba(31, 214, 255, .22);
}
.pursuit-page .live-grid > *:last-child {
border-right: 0;
justify-content: center;
flex-direction: column;
gap: 8px;
}
/* Column 1: radar + identity + source image */
.pursuit-page .live-identity {
display: flex;
align-items: center;
gap: 14px !important;
}
.pursuit-page .radar-mark {
position: relative;
flex: 0 0 auto;
width: clamp(46px, 4.4vw, 76px);
height: clamp(46px, 4.4vw, 76px);
border-radius: 50%;
border: 1px solid rgba(53, 226, 255, .5);
background: radial-gradient(circle, rgba(23, 118, 168, .34), rgba(4, 20, 34, .1) 70%);
box-shadow: 0 0 14px rgba(31, 200, 255, .28), inset 0 0 12px rgba(31, 200, 255, .18);
}
.pursuit-page .radar-mark:before,
.pursuit-page .radar-mark:after {
content: "";
position: absolute;
background: rgba(53, 226, 255, .3);
}
.pursuit-page .radar-mark:before {
left: 8%;
right: 8%;
top: 50%;
height: 1px;
}
.pursuit-page .radar-mark:after {
top: 8%;
bottom: 8%;
left: 50%;
width: 1px;
}
.pursuit-page .radar-mark span {
position: absolute;
top: 50%;
left: 50%;
width: 34%;
height: 34%;
transform: translate(-50%, -50%);
border-radius: 50%;
background: #6ff3ff;
box-shadow: 0 0 10px rgba(80, 235, 255, .95);
}
.pursuit-page .live-identity-fields {
display: flex;
flex-direction: column;
gap: 9px;
min-width: 0;
flex: 1;
}
/* Neutralize the legacy grid rules that targeted the old identity column. */
.pursuit-page .live-panel .live-identity > .live-identity-fields {
position: static !important;
left: auto !important;
width: auto !important;
display: flex !important;
flex-direction: column !important;
grid-template-columns: none !important;
gap: 9px !important;
flex: 1 1 auto !important;
min-width: 0 !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field {
display: flex !important;
grid-template-columns: none !important;
gap: 8px !important;
width: 100% !important;
}
.pursuit-page .live-source-image {
flex: 0 0 auto;
width: auto;
height: clamp(58px, 6.4vw, 104px);
max-width: 34%;
object-fit: contain;
border: 1px solid rgba(53, 226, 255, .3);
border-radius: 8px;
background: rgba(3, 16, 28, .6);
}
/* Field rows: label on the left, glass chip on the right. */
.pursuit-page .live-field {
display: flex;
align-items: center;
gap: 8px;
min-width: 0;
}
.pursuit-page .live-grid label {
flex: 0 0 auto;
max-width: 58% !important;
font-size: clamp(11px, .84vw, 15px) !important;
font-weight: 600;
letter-spacing: .5px;
white-space: nowrap;
overflow: hidden;
text-overflow: ellipsis;
color: #9fd4e8 !important;
}
.pursuit-page .live-grid label:before {
content: "";
display: inline-block;
width: 2px;
height: 11px;
margin-right: 7px;
vertical-align: -1px;
border-radius: 1px;
background: #35e2ff;
box-shadow: 0 0 6px rgba(53, 226, 255, .7);
}
/* Glass value chips */
.pursuit-page .live-grid em.live-chip {
display: inline-flex;
align-items: center;
gap: 6px;
flex: 0 1 auto;
min-width: 0;
max-width: 100%;
padding: 4px 11px !important;
border-radius: 999px;
border: 1px solid rgba(53, 226, 255, .3);
background: linear-gradient(180deg, rgba(19, 72, 110, .62), rgba(8, 28, 46, .62));
font-size: clamp(11px, .82vw, 14px) !important;
font-style: normal;
font-weight: 600;
white-space: nowrap;
overflow: hidden;
text-overflow: ellipsis;
}
.pursuit-page .live-grid em.live-chip > i {
flex: 0 0 auto;
width: 6px;
height: 6px;
border-radius: 50%;
background: currentColor;
box-shadow: 0 0 7px currentColor;
animation: pursuitLiveDot 2.4s ease-in-out infinite;
}
@keyframes pursuitLiveDot {
0%, 100% { opacity: 1; transform: scale(1); }
50% { opacity: .55; transform: scale(.82); }
}
.pursuit-page .live-grid em.live-chip.tone-cyan {
color: #62e6ff;
border-color: rgba(53, 226, 255, .42);
background: linear-gradient(180deg, rgba(16, 86, 122, .6), rgba(6, 30, 48, .6));
}
.pursuit-page .live-grid em.live-chip.tone-green {
color: #4fe6b6;
border-color: rgba(79, 230, 182, .4);
background: linear-gradient(180deg, rgba(14, 82, 68, .58), rgba(5, 26, 24, .6));
}
.pursuit-page .live-grid em.live-chip.tone-magenta {
color: #ff6fd0;
border-color: rgba(255, 111, 208, .4);
background: linear-gradient(180deg, rgba(92, 24, 82, .56), rgba(32, 8, 30, .6));
}
.pursuit-page .live-grid em.live-chip.pursuit-detecting {
color: #4fe6b6;
border-color: rgba(79, 230, 182, .4);
background: linear-gradient(180deg, rgba(14, 82, 68, .58), rgba(5, 26, 24, .6));
}
.pursuit-page .live-grid em.live-chip.pursuit-tracking {
color: #62e6ff;
border-color: rgba(53, 226, 255, .42);
background: linear-gradient(180deg, rgba(16, 86, 122, .6), rgba(6, 30, 48, .6));
}
.pursuit-page .live-grid em.live-chip.pursuit-lost {
color: #ffab5e;
border-color: rgba(255, 171, 94, .4);
background: linear-gradient(180deg, rgba(96, 56, 18, .56), rgba(32, 18, 6, .6));
}
.pursuit-page .live-grid em.live-chip.is-empty {
color: #8fa4b2;
border-color: rgba(143, 164, 178, .3);
background: linear-gradient(180deg, rgba(38, 56, 70, .5), rgba(16, 26, 36, .55));
}
.pursuit-page .live-grid em.live-chip.is-empty > i {
color: #7d93a3;
box-shadow: 0 0 6px rgba(125, 147, 163, .7);
animation: pursuitLiveDot 3.2s ease-in-out infinite;
}
/* Middle fact columns */
.pursuit-page .live-facts {
display: flex;
flex-direction: column;
justify-content: center;
gap: 12px;
}
.pursuit-page .live-facts > div {
display: flex;
flex-direction: row !important;
align-items: center;
gap: 8px;
min-width: 0;
}
/* Action column */
.pursuit-page .cancel-block {
display: flex;
flex-direction: column;
justify-content: center;
align-items: flex-start;
gap: 10px;
}
.pursuit-page .cancel-block label {
font-size: clamp(12px, .88vw, 16px) !important;
font-weight: 700;
color: #ff7ad4 !important;
text-shadow: 0 0 10px rgba(255, 90, 200, .5);
letter-spacing: .5px;
}
.pursuit-page .cancel-block .cancel-button {
width: 100% !important;
height: clamp(34px, 2.7vw, 44px) !important;
padding: 0 10px !important;
border-radius: 10px;
border: 1px solid rgba(255, 122, 212, .75);
background: linear-gradient(180deg, rgba(196, 42, 148, .92), rgba(126, 18, 96, .92));
color: #fff2fb;
font-size: clamp(12px, .92vw, 17px) !important;
font-weight: 700;
box-shadow: 0 0 16px rgba(255, 60, 180, .4), inset 0 1px 0 rgba(255, 255, 255, .22);
transition: transform .16s ease, box-shadow .16s ease;
}
.pursuit-page .cancel-block .cancel-button:hover {
transform: translateY(-1px);
box-shadow: 0 0 22px rgba(255, 60, 180, .6), inset 0 1px 0 rgba(255, 255, 255, .28);
}
.pursuit-page .cancel-block .operation-muted {
font-size: clamp(11px, .8vw, 14px);
color: #7f93a1;
}
@media (max-width: 1180px) {
.pursuit-page .live-grid {
grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, .72fr) !important;
}
.pursuit-page .live-grid > * { padding: 4px 11px !important; }
.pursuit-page .live-source-image { max-width: 28% !important; }
}
@media (max-width: 760px) {
.pursuit-page .live-panel { height: auto !important; }
.pursuit-page .live-grid {
grid-template-columns: 1fr 1fr !important;
grid-auto-rows: minmax(78px, auto);
height: auto !important;
padding: 8px 0 !important;
}
.pursuit-page .live-grid > * { border-right: 0; border-bottom: 1px solid rgba(31, 214, 255, .18); }
.pursuit-page .live-identity { grid-row: span 2; }
}
</style>

<style scoped>
/* Final compact layout pass: the panel may be narrower than the browser
   viewport because of the application sidebar, so every cell must shrink
   without pushing its neighbors out of view. */
.live-grid {
  grid-template-columns: minmax(0, 28%) minmax(0, 38%) minmax(0, 18%) minmax(0, 16%) !important;
  min-width: 0 !important;
}

.live-identity,
.live-facts,
.condition-block,
.cancel-block {
  min-width: 0 !important;
  box-sizing: border-box;
}

.live-identity > div:last-child {
  grid-template-columns: minmax(0, 42%) minmax(0, 58%) !important;
  width: 100% !important;
  min-width: 0 !important;
}

.live-identity > div:last-child > label,
.live-identity > div:last-child > strong,
.live-identity > div:last-child > b {
  min-width: 0 !important;
  max-width: 100% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

.live-identity > div:last-child > label {
  display: block !important;
  font-size: clamp(9px, .72vw, 12px) !important;
}

.live-identity > div:last-child > strong {
  display: block !important;
  font-size: clamp(11px, 1.15vw, 19px) !important;
  line-height: 1.2 !important;
}

.live-identity > div:last-child > b {
  display: block !important;
  font-size: clamp(10px, .8vw, 13px) !important;
}

.live-facts > div {
  min-width: 0 !important;
}

.live-facts label {
  min-width: 0 !important;
  max-width: 48% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  font-size: clamp(9px, .72vw, 12px) !important;
}

.live-facts b,
.live-facts em,
.condition-block em,
.cancel-button {
  min-width: 0 !important;
  max-width: 100% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

.live-facts b {
  flex: 1 1 auto;
  font-size: clamp(9px, .75vw, 12px) !important;
}

.live-facts em,
.condition-block em {
  padding: 2px 6px !important;
  font-size: clamp(9px, .75vw, 12px) !important;
}

.cancel-block .cancel-button {
  width: 100% !important;
  height: clamp(28px, 2.3vw, 36px) !important;
  padding: 0 5px !important;
  font-size: clamp(9px, .75vw, 12px) !important;
}

@media (max-width: 1100px) {
  .live-grid {
    grid-template-columns: minmax(0, 27%) minmax(0, 37%) minmax(0, 19%) minmax(0, 17%) !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  .live-identity {
    gap: 6px !important;
    padding-right: 6px !important;
  }

  .live-identity .radar-mark {
    width: 44px !important;
    height: 44px !important;
    flex-basis: 44px !important;
  }

  .live-identity .radar-mark:before {
    height: 58px !important;
  }

  .live-identity .radar-mark:after {
    width: 58px !important;
  }

  .live-facts {
    gap: 3px 4px !important;
    padding-left: 6px !important;
    padding-right: 6px !important;
  }

  .condition-block {
    padding-left: 6px !important;
    padding-right: 4px !important;
  }

  .cancel-block {
    gap: 5px !important;
    padding-left: 6px !important;
  }
}
</style>

<style scoped>
/* Keep the live-event summary readable when the content pane is narrow. */
.live-grid,
.live-identity,
.live-facts,
.condition-block,
.cancel-block {
  min-width: 0;
}

.live-identity > div:last-child {
  min-width: 0 !important;
}

.live-identity > div:last-child > strong,
.live-identity > div:last-child > b {
  min-width: 0 !important;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.live-identity > div:last-child > strong {
  font-size: clamp(13px, 1.2vw, 19px);
}

.live-facts > div,
.condition-block,
.cancel-block {
  overflow: hidden;
}

.live-facts em,
.condition-block em,
.cancel-button {
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 1100px) {
  .live-grid {
    grid-template-columns: 28% 38% 18% 16%;
    padding-left: 14px;
    padding-right: 14px;
  }

  .live-identity {
    gap: 9px;
    padding-right: 9px;
  }

  .live-identity .radar-mark {
    width: 52px;
    height: 52px;
    flex: 0 0 52px;
  }

  .live-identity .radar-mark:before {
    height: 68px;
  }

  .live-identity .radar-mark:after {
    width: 68px;
  }

  .live-identity strong {
    font-size: 14px !important;
    line-height: 17px !important;
  }

  .live-grid label,
  .cancel-block label {
    font-size: 10px;
  }

  .live-identity b {
    font-size: 11px !important;
  }

  .live-facts {
    gap: 4px 5px;
    padding: 0 8px;
  }

  .live-facts > div {
    gap: 4px;
  }

  .live-facts em,
  .condition-block em {
    padding: 2px 5px;
    font-size: 10px;
  }

  .condition-block {
    gap: 6px;
    padding-left: 7px;
    padding-right: 6px;
  }

  .cancel-block {
    gap: 6px;
    padding-left: 7px;
  }

  .cancel-block .cancel-button {
    height: 31px;
    padding: 0 7px;
    font-size: 10px;
  }
}

@media (max-width: 760px) {
  .live-panel {
    height: auto;
    min-height: 190px;
    max-height: none;
  }

  .live-grid {
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: minmax(72px, auto);
    height: auto;
    min-height: 150px;
    overflow: visible;
    padding: 4px 12px 8px;
  }

  .live-identity {
    grid-row: span 2;
  }

  .live-facts {
    border-right: 0;
    padding-right: 0;
  }

  .condition-block,
  .cancel-block {
    padding-left: 8px;
  }
}
</style>
<style scoped>
.title-strip {
  height: 50px;
  padding: 0 30px 0 38px;
  box-sizing: border-box;
  background: #020d17;
  border-bottom: 1px solid #00dce8;
}
.title-strip:before {
  left: 0;
  top: 0;
  width: 208px;
  height: 100%;
  background: #071923;
  border: 0;
  clip-path: polygon(0 0, 100% 0, 91% 100%, 0 100%);
  z-index: 0;
}
.title-strip:after {
  left: 218px;
  right: 20px;
  top: 25px;
  height: 1px;
  background: #00dce8;
  box-shadow: 0 0 6px rgba(0, 220, 232, 0.65);
  z-index: 0;
}
.title-shield {
  width: 25px;
  height: 28px;
  margin-right: 10px;
  z-index: 1;
}
.title-strip h1 {
  font-size: 22px;
  letter-spacing: 0;
  z-index: 2;
}
.title-rule {
  left: 230px;
  right: 54px;
  top: 19px;
  height: 2px;
  background: repeating-linear-gradient(
    135deg,
    #11dce8 0 22px,
    transparent 22px 28px
  );
  opacity: 0.9;
  z-index: 1;
}
.title-slashes {
  right: 27px;
  top: 15px;
  font-size: 10px;
  letter-spacing: 4px;
  z-index: 2;
}
.title-slashes:before {
  right: 26px;
  top: 5px;
  width: 42px;
  height: 5px;
  background: repeating-linear-gradient(
    135deg,
    #08dce9 0 7px,
    transparent 7px 12px
  );
}
</style>
<style scoped>
.title-strip {
  background-image: url("~@/assets/pursuit-title-decoration.png");
  background-repeat: no-repeat;
  background-position: 190px 0;
  background-size: calc(100% - 190px) 50px;
}
.title-strip:after {
  display: none;
}
.title-strip:before {
  z-index: 0;
}
.title-shield,
.title-strip h1 {
  z-index: 3;
}
</style>

<style scoped>
/* The history filter no longer needs a status selector, so give the detail panel more room. */
.workspace {
  grid-template-columns: 34% 66%;
}
</style>

<style lang="less">
.right-empty-state {
  position: relative !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex: 1 1 auto !important;
  width: 100% !important;
  min-height: 0 !important;
  height: auto !important;
  overflow: hidden !important;
  color: #a8c6ce !important;
  font-size: 15px !important;
  font-weight: 500;
  letter-spacing: 1px;
  text-shadow: 0 0 8px rgba(0, 225, 240, 0.2);
  background: linear-gradient(
    135deg,
    rgba(0, 24, 40, 0.72),
    rgba(2, 12, 22, 0.82)
  ) !important;
  animation: pursuit-empty-breathe 3.2s ease-in-out infinite;
}
.right-empty-state:before {
  content: "";
  position: absolute;
  left: 50%;
  bottom: 21%;
  width: 76px;
  height: 2px;
  transform: translateX(-50%);
  border-radius: 2px;
  background: linear-gradient(90deg, transparent, #11cddd, transparent);
  box-shadow: 0 0 7px rgba(17, 205, 221, 0.45);
  animation: pursuit-empty-scan 3.4s ease-in-out infinite;
  pointer-events: none;
}
.right-empty-state:after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: calc(21% - 5px);
  width: 24px;
  height: 8px;
  transform: translateX(-50%);
  border-left: 1px solid rgba(42, 204, 218, 0.3);
  border-right: 1px solid rgba(42, 204, 218, 0.3);
  opacity: 0.5;
  pointer-events: none;
}
@keyframes pursuit-empty-scan {
  0%,
  100% {
    opacity: 0.25;
    transform: translateX(-90px);
  }
  50% {
    opacity: 0.8;
    transform: translateX(14px);
  }
}
@keyframes pursuit-empty-breathe {
  0%,
  100% {
    box-shadow: inset 0 0 0 rgba(0, 240, 255, 0);
  }
  50% {
    box-shadow: inset 0 0 26px rgba(0, 190, 230, 0.07);
  }
}
</style>

<style lang="less">
.ivu-date-picker-transfer.pursuit-date-picker-popup {
  z-index: 9999 !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup.ivu-select-dropdown,
.ivu-date-picker-transfer.pursuit-date-picker-popup .ivu-select-dropdown,
.ivu-date-picker-transfer.pursuit-date-picker-popup .ivu-picker-panel {
  overflow: hidden;
  border: 1px solid rgba(0, 240, 255, 0.4) !important;
  border-radius: 6px;
  background: rgba(0, 21, 41, 0.98) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 20px rgba(0, 240, 255, 0.3) !important;
  backdrop-filter: blur(15px);
}
.ivu-date-picker-transfer.pursuit-date-picker-popup .ivu-picker-panel-body,
.ivu-date-picker-transfer.pursuit-date-picker-popup .ivu-picker-panel-content {
  background: transparent !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup .ivu-date-picker-header {
  border-bottom: 1px solid rgba(0, 240, 255, 0.2) !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-header-label {
  color: #00f0ff !important;
  font-weight: 600;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-header-label:hover {
  color: #fff !important;
  background: rgba(0, 240, 255, 0.1) !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-header
  .ivu-icon {
  color: rgba(0, 240, 255, 0.8) !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-cells-header
  span {
  color: rgba(0, 240, 255, 0.8) !important;
  font-weight: 600 !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-cells-cell {
  color: rgba(255, 255, 255, 0.7) !important;
  transition: all 0.2s ease !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-cells-cell:hover {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.15) !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-cells-cell-selected {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.25) !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-cells-cell-selected
  em {
  color: #00f0ff !important;
  background: transparent !important;
  font-weight: 700 !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-cells-cell-today
  em {
  color: #ffa500 !important;
  font-weight: 600 !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-cells-cell-disabled {
  color: rgba(255, 255, 255, 0.2) !important;
  background: transparent !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-cells-cell-prev-month,
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-cells-cell-next-month {
  color: rgba(255, 255, 255, 0.3) !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-cells-cell-range {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.08) !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-date-picker-cells-cell-range:before {
  background: none !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup .ivu-picker-panel-footer {
  border-top: 1px solid rgba(0, 240, 255, 0.2) !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup .ivu-picker-confirm-time {
  color: #fff !important;
  background: transparent !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-picker-confirm-time:hover {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.15) !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-picker-panel-footer
  .ivu-btn,
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-picker-panel-footer
  button {
  border: 1px solid rgba(0, 240, 255, 0.45) !important;
  border-radius: 4px !important;
  color: #00f0ff !important;
  background: rgba(0, 40, 80, 0.8) !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-picker-panel-footer
  .ivu-btn:hover,
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-picker-panel-footer
  button:hover {
  border-color: #00f0ff !important;
  color: #fff !important;
  background: rgba(0, 100, 150, 0.8) !important;
  box-shadow: 0 0 8px rgba(0, 240, 255, 0.35);
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-picker-confirm
  .ivu-btn-primary {
  border-color: #00f0ff !important;
  color: #03141f !important;
  background: #00dce8 !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-picker-confirm
  .ivu-btn-primary:hover {
  color: #fff !important;
  background: #10f3ff !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-time-picker-cells
  ul
  li {
  color: rgba(255, 255, 255, 0.7) !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-time-picker-cells
  ul
  li:hover {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.15) !important;
}
.ivu-date-picker-transfer.pursuit-date-picker-popup
  .ivu-time-picker-cells
  ul
  li.ivu-time-picker-cells-cell-selected {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.25) !important;
  font-weight: 600 !important;
}
/* iView can replace the transfer node after every range/time selection. Keep
   the active page theme applied to the live transfer node as a fallback. */
body.pursuit-date-theme-active .ivu-date-picker-transfer {
  z-index: 9999 !important;
  border: 1px solid rgba(0, 240, 255, 0.4) !important;
  border-radius: 6px !important;
  background: rgba(0, 21, 41, 0.98) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 20px rgba(0, 240, 255, 0.3) !important;
}
body.pursuit-date-theme-active .ivu-date-picker-transfer .ivu-picker-panel,
body.pursuit-date-theme-active .ivu-date-picker-transfer .ivu-select-dropdown {
  background: rgba(0, 21, 41, 0.98) !important;
  color: rgba(255, 255, 255, 0.78) !important;
}
body.pursuit-date-theme-active
  .ivu-date-picker-transfer
  .ivu-picker-panel-footer {
  border-top-color: rgba(0, 240, 255, 0.2) !important;
}
body.pursuit-date-theme-active
  .ivu-date-picker-transfer
  .ivu-picker-confirm
  .ivu-btn-primary {
  border-color: #00f0ff !important;
  color: #03141f !important;
  background: #00dce8 !important;
}
</style>
<style scoped>
.title-strip {
  background-image: url("~@/assets/pursuit-title-decoration-generated.png");
  background-repeat: no-repeat;
  background-position: 190px 50%;
  background-size: calc(100% - 190px) auto;
}
.title-strip {
  height: 50px;
  background-image: url("~@/assets/pursuit-title-decoration.png");
  background-repeat: no-repeat;
  background-position: 220px center;
  background-size: calc(100% - 220px) 50px;
}
.title-strip:after,
.title-rule,
.title-slashes:before {
  display: none;
}
.title-shield {
  display: inline-flex;
  position: relative;
  z-index: 4;
  flex: 0 0 auto;
}
.title-strip h1 {
  display: block;
  position: relative;
  z-index: 4;
  margin: 0;
  color: #effcff;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0;
  text-shadow: 0 0 8px rgba(0, 226, 241, 0.25);
}
.monitor-card {
  border: 0;
  background: #041926 url("~@/assets/monitor-card-reference.png") center/100%
    100% no-repeat;
  clip-path: none;
  position: relative;
}
.robot-header-row > .monitor-card {
  flex: 0 0 132px !important;
  width: 132px !important;
  min-width: 132px !important;
  padding-left: 7px !important;
  padding-right: 7px !important;
}
.robot-header-row > .monitor-card .monitor-copy {
  padding-left: 3px !important;
}
.robot-header-row > .monitor-card .monitor-copy strong {
  font-size: 14px !important;
  line-height: 18px !important;
}
.robot-header-row > .monitor-card .monitor-copy span {
  font-size: 11px !important;
  line-height: 15px !important;
}
.robot-header-row > .monitor-card {
  justify-content: center !important;
  text-align: center !important;
}
.robot-header-row > .monitor-card .monitor-copy {
  padding-left: 0 !important;
  justify-content: center !important;
  text-align: center !important;
}
.robot-header-row > .monitor-card .monitor-copy > div {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
}
.robot-header-row > .monitor-card .monitor-copy span {
  justify-content: center !important;
}
.monitor-card:after {
  display: none;
}
.monitor-copy {
  padding-left: 42px;
  gap: 0;
}
.monitor-copy > .ivu-icon {
  display: none;
}
.monitor-copy strong {
  font-size: 16px;
}
.monitor-copy span {
  font-size: 11px;
}
.refresh-button {
  position: relative;
  z-index: 2;
  width: 76px;
  height: 34px;
  padding: 0 9px;
  border: 1px solid #00f0ff;
  border-radius: 7px;
  color: #eaffff;
  background: linear-gradient(
    180deg,
    rgba(0, 54, 92, 0.98),
    rgba(0, 24, 48, 0.98)
  );
  box-shadow: 0 0 4px rgba(0, 240, 255, 0.45),
    inset 0 0 0 1px rgba(0, 180, 255, 0.22);
  font-size: 12px;
  font-weight: 600;
}
.refresh-button:before {
  content: "";
  position: absolute;
  inset: 3px;
  border: 1px solid rgba(0, 190, 255, 0.28);
  border-radius: 4px;
  pointer-events: none;
}
.refresh-button:hover {
  color: #fff;
  background: linear-gradient(
    180deg,
    rgba(0, 88, 144, 0.98),
    rgba(0, 38, 72, 0.98)
  );
  box-shadow: 0 0 8px rgba(0, 240, 255, 0.75),
    inset 0 0 0 1px rgba(0, 220, 255, 0.42);
}
.refresh-button.refreshing .ivu-icon {
  animation: refresh-spin 0.8s linear infinite;
}
.refresh-button:disabled {
  cursor: wait;
  opacity: 0.9;
}
@keyframes refresh-spin {
  to {
    transform: rotate(360deg);
  }
}
.live-panel .live-heading {
  height: 45px;
  padding: 0 0 0 70px;
  gap: 0;
  position: relative;
  z-index: 2;
  color: #12e5ed;
  font-size: 27px;
  font-weight: 600;
  line-height: 1;
  font-family: Arial, "Microsoft YaHei", sans-serif;
}
.live-panel .live-heading > b,
.live-panel .live-heading > i {
  display: none;
}
.live-panel .live-heading strong {
  display: block;
  margin: 0;
  transform: translateY(-1px);
  letter-spacing: 0;
}
.tab-strip {
  height: 42px;
  padding: 0;
  background: #03111a;
  border: 0;
  gap: 0;
  display: block;
  position: relative;
}
.workspace-tab {
  height: 42px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  color: #d7edf2;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
}
.workspace-tab:first-child {
  position: absolute;
  left: 0;
  top: 0;
  width: 173px;
  background: #032033 url("~@/assets/pursuit-tab-live.png") left center/100%
    42px no-repeat;
  border: 1px solid #00dce8;
  border-radius: 4px;
  box-sizing: border-box;
}
.workspace-tab.history-tab {
  position: absolute;
  left: 173px;
  top: 0;
  width: 191px;
  background: #032033 url("~@/assets/pursuit-tab-history.png") left center/100%
    42px no-repeat;
  border: 1px solid #0b6880;
  border-radius: 4px;
  box-sizing: border-box;
}
.workspace-tab:first-child.active {
  border-color: #00f0ff;
  box-shadow: 0 0 6px rgba(0, 240, 255, 0.55),
    inset 0 0 8px rgba(0, 210, 255, 0.2);
}
.workspace-tab.history-tab.active {
  border-color: #00dce8;
  box-shadow: 0 0 6px rgba(0, 220, 232, 0.4),
    inset 0 0 8px rgba(0, 210, 255, 0.14);
}
.tab-strip {
  height: 42px;
  padding: 0;
  background: #020d17;
  border: 0;
  display: block;
  position: relative;
}
.workspace-tab:first-child,
.workspace-tab.history-tab {
  top: 0;
  height: 42px;
  box-sizing: border-box;
  border: 1px solid #08718a;
  border-radius: 0;
  background: linear-gradient(180deg, #08283a 0%, #041824 100%);
  clip-path: polygon(
    8px 0,
    calc(100% - 8px) 0,
    100% 8px,
    100% calc(100% - 8px),
    calc(100% - 8px) 100%,
    8px 100%,
    0 calc(100% - 8px),
    0 8px
  );
  box-shadow: inset 0 0 0 1px rgba(0, 150, 190, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.workspace-tab:first-child {
  left: 0;
  width: 173px;
}
.workspace-tab.history-tab {
  left: 173px;
  width: 191px;
}
.workspace-tab:first-child:before,
.workspace-tab.history-tab:before {
  content: "";
  position: absolute;
  left: 14px;
  right: 14px;
  top: 4px;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    #05e7f0 18%,
    #05e7f0 82%,
    transparent
  );
  opacity: 0.45;
}
.workspace-tab:first-child:after,
.workspace-tab.history-tab:after {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  line-height: 1;
  pointer-events: none;
}
.workspace-tab:first-child:after {
  content: "检测消息";
  color: #0cebf2;
}
.workspace-tab.history-tab:after {
  content: "历史授权事件";
  color: #9bbbc4;
}
.workspace-tab:first-child.active,
.workspace-tab.history-tab.active {
  border-color: #00efff;
  background: linear-gradient(180deg, #08445b 0%, #062535 100%);
  box-shadow: 0 0 10px rgba(0, 235, 250, 0.48),
    inset 0 0 16px rgba(0, 180, 230, 0.2);
}
.workspace-tab:first-child.active:before,
.workspace-tab.history-tab.active:before {
  opacity: 1;
  box-shadow: 0 0 7px #00e9f4;
}
.workspace-tab.history-tab.active:after {
  color: #ecfeff;
}
.tab-strip .workspace-tab {
  font-size: 12px !important;
  color: #d7edf2 !important;
  text-shadow: none !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
  padding: 0 !important;
  line-height: 1 !important;
}
.tab-strip .workspace-tab:after {
  display: none !important;
  position: absolute !important;
  inset: 0 !important;
  align-items: center !important;
  justify-content: center !important;
  margin: 0 !important;
  padding: 0 !important;
  font-size: 13px !important;
  line-height: 1 !important;
  text-align: center !important;
  color: inherit !important;
}
.tab-strip .workspace-tab:first-child:after {
  color: #0cebf2 !important;
}
.tab-strip .workspace-tab.history-tab:after {
  color: #9bbbc4 !important;
}
.tab-strip .workspace-tab:first-child.active:after,
.tab-strip .workspace-tab.history-tab.active:after {
  color: #ecfeff !important;
}
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 6px 10px;
  margin-top: auto;
  background: rgba(16, 32, 58, 0.7);
  border: 1px solid rgba(0, 191, 255, 0.3);
  border-radius: 16px;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}
.pagination-bar button {
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(0, 191, 255, 0.3);
  font-size: 14px;
  line-height: 1;
  transition: all 0.3s ease;
}
.pagination-bar button:hover {
  background: rgba(0, 191, 255, 0.5);
  transform: scale(1.08);
  box-shadow: 0 0 15px rgba(0, 191, 255, 0.5);
}
.pagination-bar button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
.pagination-bar .page-active {
  border: 0;
  color: rgba(0, 191, 255, 0.95);
  background: transparent;
  box-shadow: none;
  font-weight: 600;
}
.pagination-bar span {
  margin-left: 0;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  opacity: 0.85;
  white-space: nowrap;
}
.workspace-tab b {
  display: none;
}
.workspace-tab:first-child,
.workspace-tab.history-tab {
  font-size: 0;
}
.workspace-tab:first-child:after {
  content: "检测消息";
  display: block;
  font-size: 13px;
  color: #0cebf2;
  text-align: center;
}
.workspace-tab.history-tab:after {
  content: "历史授权事件";
  display: block;
  font-size: 13px;
  color: #d7edf2;
  text-align: center;
}
.workspace-tab b {
  display: none;
}
.workspace-tab.active {
  color: #0cebf2;
  background: transparent;
  border: 0;
}
.workspace-tab b {
  color: #00e5ef;
  font-size: 16px;
}
.history-tab {
  flex-basis: 52%;
}
.live-panel {
  position: relative;
  background: #061624 url("~@/assets/live-event-frame.png") center top/100% 100%
    no-repeat;
  clip-path: none;
}
.live-grid {
  grid-template-columns: 28% 38% 18% 16%;
  padding: 8px 30px 15px;
  position: relative;
}
.live-identity {
  border-right: 1px solid #1c6577;
  padding-right: 18px;
}
.live-facts {
  border-right: 1px solid #1c6577;
  padding: 0 18px;
}
.condition-block {
  position: relative;
  border-right: 1px solid #1c6577;
  padding-left: 18px;
  padding-right: 16px;
}
.cancel-block {
  display: flex;
  flex-direction: column;
  gap: 13px;
  padding-left: 18px;
  min-width: 0;
}
.cancel-block label {
  display: block;
  color: #79a5b1;
  font-size: 15px;
}
.cancel-block .cancel-button {
  min-width: 0;
  width: 100%;
  height: 50px;
}
.operation-muted {
  color: #78929a;
  font-size: 12px;
  padding-top: 13px;
}
.target-selector {
  position: relative;
}
.target-selector h2 {
  display: flex;
  align-items: baseline;
  gap: 7px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 225, 239, 0.32);
  text-shadow: 0 0 8px rgba(0, 225, 239, 0.45);
}
.target-selector h2:before {
  content: "◎";
  color: #00f0ff;
  font-size: 18px;
  line-height: 1;
}
.target-selector h2 small {
  color: #72abb5;
}
.target-row {
  position: relative;
  overflow: hidden;
  border: 1px solid #245263;
  border-radius: 7px;
  background: linear-gradient(105deg, #071b29, #06131f 72%);
  box-shadow: inset 0 0 0 1px rgba(0, 191, 224, 0.08),
    0 0 0 rgba(0, 225, 240, 0);
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease,
    transform 0.2s ease;
}
.target-row:before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #0b6173;
  opacity: 0.8;
}
.target-row:after {
  content: "";
  position: absolute;
  right: -18px;
  top: -22px;
  width: 72px;
  height: 48px;
  border-top: 1px solid rgba(0, 225, 240, 0.35);
  border-right: 1px solid rgba(0, 225, 240, 0.35);
  transform: skewX(-25deg);
  opacity: 0.45;
}
.target-row:hover {
  border-color: #00dce9;
  background: linear-gradient(105deg, #0a3040, #071d2b);
  box-shadow: 0 0 12px rgba(0, 225, 240, 0.25),
    inset 0 0 14px rgba(0, 170, 210, 0.12);
  transform: translateX(2px);
}
.target-row.selected {
  border-color: #00f0ff;
  background: linear-gradient(105deg, #08495b, #082534 72%);
  box-shadow: 0 0 14px rgba(0, 239, 255, 0.36),
    inset 0 0 18px rgba(0, 209, 232, 0.18);
}
.target-row.selected:before {
  background: #00f0ff;
  box-shadow: 0 0 10px #00f0ff;
}
.radio-dot {
  position: relative;
  z-index: 1;
  width: 17px;
  height: 17px;
  border: 2px solid #668d99;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}
.target-row:hover .radio-dot {
  border-color: #00e8f2;
  box-shadow: 0 0 8px rgba(0, 232, 242, 0.5);
}
.target-row.selected .radio-dot {
  border-color: #00f5ff;
  background: #00e7f0;
  box-shadow: inset 0 0 0 4px #082534, 0 0 9px rgba(0, 240, 255, 0.9);
}
.target-row b {
  position: relative;
  z-index: 1;
  color: #d9f7fa;
  letter-spacing: 0.3px;
}
.authorize-button,
.cancel-button {
  position: relative;
  overflow: hidden;
  border: 1px solid #ff31bd;
  border-radius: 9px;
  clip-path: none;
  background: #321331;
  color: #ff78d9;
  font-weight: 700;
  letter-spacing: 0.3px;
  box-shadow: 0 0 7px rgba(255, 27, 186, 0.24),
    inset 0 0 0 1px rgba(255, 108, 220, 0.12);
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease,
    box-shadow 0.2s ease, transform 0.2s ease;
}
.authorize-button:before,
.cancel-button:before,
.authorize-button:after,
.cancel-button:after {
  display: none;
}
.authorize-button:hover,
.cancel-button:hover {
  color: #fff5fd;
  border-color: #ff72d4;
  background: #55164d;
  box-shadow: 0 0 13px rgba(255, 27, 186, 0.55),
    inset 0 0 0 1px rgba(255, 192, 239, 0.2);
  transform: translateY(-1px);
}
.authorize-button:active,
.cancel-button:active {
  transform: translateY(1px);
  box-shadow: 0 0 6px rgba(255, 27, 186, 0.35),
    inset 0 0 8px rgba(255, 27, 186, 0.2);
}
.authorize-button:disabled {
  opacity: 0.38;
  cursor: not-allowed;
  filter: saturate(0.55);
  transform: none;
  box-shadow: none;
}
.tab-strip {
  height: 42px !important;
  min-height: 42px !important;
  display: flex !important;
  align-items: stretch !important;
  gap: 6px !important;
  padding: 5px 6px !important;
  box-sizing: border-box !important;
  position: relative !important;
  background: #03131f !important;
  border: 0 !important;
}
.tab-strip .workspace-tab,
.tab-strip .workspace-tab:first-child,
.tab-strip .workspace-tab.history-tab {
  position: relative !important;
  left: auto !important;
  top: auto !important;
  width: auto !important;
  height: 32px !important;
  min-height: 32px !important;
  flex: 1 1 0 !important;
  flex-basis: 0 !important;
  margin: 0 !important;
  padding: 0 10px !important;
  box-sizing: border-box !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: 1px solid #17495b !important;
  border-radius: 8px !important;
  clip-path: none !important;
  background: #071c2b !important;
  color: #83abb4 !important;
  box-shadow: inset 0 0 0 1px rgba(0, 163, 190, 0.08) !important;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease,
    box-shadow 0.2s ease, transform 0.2s ease !important;
}
.tab-strip .workspace-tab:before {
  display: none !important;
}
.tab-strip .workspace-tab:after {
  position: static !important;
  inset: auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: auto !important;
  height: auto !important;
  margin: 0 !important;
  padding: 0 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.15px !important;
  line-height: 1 !important;
  color: #83abb4 !important;
}
.tab-strip .workspace-tab:first-child:after {
  content: "检测消息";
}
.tab-strip .workspace-tab.history-tab:after {
  content: "历史授权事件";
}
.tab-strip .workspace-tab:hover {
  border-color: #1fa8b8 !important;
  color: #c9faff !important;
  background: #0a2637 !important;
  box-shadow: 0 0 8px rgba(0, 210, 225, 0.18),
    inset 0 0 12px rgba(0, 180, 210, 0.1) !important;
  transform: translateY(-1px) !important;
}
.tab-strip .workspace-tab.active {
  border-color: #00dce9 !important;
  background: linear-gradient(180deg, #0b4154, #082b3b) !important;
  color: #efffff !important;
  box-shadow: 0 0 10px rgba(0, 225, 240, 0.35),
    inset 0 0 14px rgba(0, 210, 230, 0.16) !important;
}
.tab-strip .workspace-tab.active:after {
  color: #efffff !important;
  text-shadow: 0 0 7px rgba(0, 239, 255, 0.65) !important;
}
.tab-strip .workspace-tab b {
  display: none !important;
}
.message-view > .event-log {
  display: none !important;
}
.condition-block .tone-cyan {
  box-shadow: 0 0 10px rgba(0, 225, 220, 0.18);
}
.condition-status {
  transition: color 0.2s ease, border-color 0.2s ease, background 0.2s ease,
    box-shadow 0.2s ease;
}
.condition-status i {
  transition: background 0.2s ease, box-shadow 0.2s ease;
}
.condition-green {
  color: #27e59a !important;
  border-color: #1cbf81 !important;
  background: #073b31 !important;
  box-shadow: 0 0 11px rgba(39, 229, 154, 0.25) !important;
}
.tone-green {
  color: #27e59a !important;
  border-color: #1cbf81 !important;
  background: #073b31 !important;
  box-shadow: 0 0 11px rgba(39, 229, 154, 0.25) !important;
}
.condition-green i {
  background: #27e59a !important;
  box-shadow: 0 0 8px #27e59a;
}
.condition-amber {
  color: #ffc04d !important;
  border-color: #d99025 !important;
  background: #3a2a13 !important;
  box-shadow: 0 0 11px rgba(255, 192, 77, 0.22) !important;
}
.condition-amber i {
  background: #ffc04d !important;
  box-shadow: 0 0 8px #ffc04d;
}
.condition-cyan {
  color: #04e7df !important;
  border-color: #00a9b6 !important;
  background: #06353b !important;
  box-shadow: 0 0 10px rgba(0, 225, 220, 0.2) !important;
}
.condition-cyan i {
  background: #04e7df !important;
  box-shadow: 0 0 8px #04e7df;
}
.condition-muted {
  color: #a7bbc0 !important;
  border-color: #546a71 !important;
  background: #26343a !important;
}
.condition-status.condition-muted i {
  background: #a7bbc0 !important;
}
.time-source,
.time-source-label {
  color: #49d8ee !important;
  text-shadow: 0 0 7px rgba(73, 216, 238, 0.28);
}
.time-start,
.time-start-label {
  color: #42e69a !important;
  text-shadow: 0 0 7px rgba(66, 230, 154, 0.25);
}
.time-close,
.time-close-label {
  color: #ffb14e !important;
  text-shadow: 0 0 7px rgba(255, 177, 78, 0.24);
}
.live-facts > div:nth-child(4) > label {
  color: #62bbca !important;
}
.live-facts > div:nth-child(5) > label {
  color: #63c99a !important;
}
.summary > div:nth-child(2) > p:nth-child(1) > label {
  color: #62bbca !important;
}
.summary > div:nth-child(2) > p:nth-child(2) > label {
  color: #63c99a !important;
}
.summary > div:nth-child(2) > p:nth-child(3) > label {
  color: #d99a4c !important;
}
.pursuit-tracking {
  color: #04e7df !important;
  border-color: #00a9b6 !important;
  background: #06353b !important;
  box-shadow: 0 0 10px rgba(0, 225, 220, 0.22) !important;
  animation: status-pulse-cyan 2.4s ease-in-out infinite;
}
.pursuit-tracking i {
  background: #04e7df !important;
  box-shadow: 0 0 8px #04e7df;
}
.pursuit-detecting {
  color: #ffc04d !important;
  border-color: #d99025 !important;
  background: #3a2a13 !important;
  box-shadow: 0 0 10px rgba(255, 192, 77, 0.22) !important;
  animation: status-pulse-amber 1.7s ease-in-out infinite;
}
.pursuit-detecting i {
  background: #ffc04d !important;
  box-shadow: 0 0 8px #ffc04d;
}
.pursuit-lost {
  color: #ff7184 !important;
  border-color: #cb4b63 !important;
  background: #421d2b !important;
  box-shadow: 0 0 10px rgba(255, 113, 132, 0.24) !important;
  animation: status-pulse-red 1.35s ease-in-out infinite;
}
.pursuit-lost i {
  background: #ff7184 !important;
  box-shadow: 0 0 8px #ff7184;
}
.active-live {
  color: #27e59a !important;
  border-color: #1cbf81 !important;
  background: #073b31 !important;
  box-shadow: 0 0 10px rgba(39, 229, 154, 0.24) !important;
}
.active-closed {
  color: #a7bbc0 !important;
  border-color: #546a71 !important;
  background: #26343a !important;
}
.feedback-success {
  color: #27e59a !important;
}
.feedback-error {
  color: #ff7184 !important;
}
.feedback-neutral {
  color: #a7bbc0 !important;
}
@keyframes status-pulse-cyan {
  0%,
  100% {
    box-shadow: 0 0 5px rgba(0, 225, 220, 0.16);
  }
  50% {
    box-shadow: 0 0 13px rgba(0, 225, 220, 0.55);
  }
}
@keyframes status-pulse-amber {
  0%,
  100% {
    box-shadow: 0 0 5px rgba(255, 192, 77, 0.16);
  }
  50% {
    box-shadow: 0 0 13px rgba(255, 192, 77, 0.55);
  }
}
@keyframes status-pulse-red {
  0%,
  100% {
    box-shadow: 0 0 5px rgba(255, 113, 132, 0.16);
  }
  50% {
    box-shadow: 0 0 13px rgba(255, 113, 132, 0.58);
  }
}
.authorize-button {
  border-color: #23e394;
  color: #77ffc0;
  background: #123c32;
  box-shadow: 0 0 7px rgba(35, 227, 148, 0.24),
    inset 0 0 0 1px rgba(133, 255, 202, 0.12);
}
.authorize-button:hover {
  color: #effff7;
  border-color: #70ffc0;
  background: #19604a;
  box-shadow: 0 0 13px rgba(35, 227, 148, 0.55),
    inset 0 0 0 1px rgba(196, 255, 225, 0.2);
}
.authorize-button:active {
  box-shadow: 0 0 6px rgba(35, 227, 148, 0.35),
    inset 0 0 8px rgba(35, 227, 148, 0.2);
}
</style>
<style lang="less" scoped>
/* Match the dashboard history pager: rectangular cyber controls with the same information hierarchy. */
.pagination-bar.cyber-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
  min-height: 46px;
  margin-top: auto;
  padding: 5px 8px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 240, 255, 0.3);
  border-radius: 6px;
  background: linear-gradient(
    135deg,
    rgba(0, 21, 41, 0.95),
    rgba(0, 40, 80, 0.82)
  );
  box-shadow: inset 0 1px 0 rgba(0, 240, 255, 0.16),
    0 4px 12px rgba(0, 0, 0, 0.24);
}
.pagination-bar.cyber-pagination:before {
  content: "";
  position: absolute;
  left: -100%;
  top: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #00f0ff, transparent);
  animation: pagination-scan 3s linear infinite;
}
.cyber-pagination .pagination-info {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  padding: 4px 7px;
  border: 1px solid rgba(0, 240, 255, 0.25);
  border-radius: 4px;
  background: rgba(0, 240, 255, 0.045);
  font-family: Consolas, "Courier New", monospace;
  white-space: nowrap;
}
.cyber-pagination .info-label {
  font-size: 9px;
  color: rgba(0, 240, 255, 0.62);
  font-weight: 700;
  letter-spacing: 0.5px;
}
.cyber-pagination .info-value {
  font-size: 14px;
  color: #00f0ff;
  font-weight: 700;
  text-shadow: 0 0 8px rgba(0, 240, 255, 0.7);
}
.cyber-pagination .info-divider {
  margin: 0 1px;
  color: rgba(0, 240, 255, 0.35);
}
.cyber-pagination .info-text {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
}
.cyber-pagination .pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  flex: 1;
  min-width: 0;
}
.cyber-pagination .page-btn,
.cyber-pagination .page-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 23px;
  min-width: 23px;
  height: 23px;
  padding: 0;
  border: 1px solid rgba(0, 240, 255, 0.3);
  border-radius: 3px;
  color: rgba(0, 240, 255, 0.85);
  background: rgba(0, 40, 80, 0.6);
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
}
.cyber-pagination .page-btn:hover:not(:disabled),
.cyber-pagination .page-number:hover:not(.active) {
  border-color: #00f0ff;
  color: #00f0ff;
  background: rgba(0, 80, 120, 0.72);
  box-shadow: 0 0 8px rgba(0, 240, 255, 0.35);
}
.cyber-pagination .page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.cyber-pagination .page-numbers {
  display: flex;
  align-items: center;
  gap: 3px;
}
.cyber-pagination .page-number.active {
  border-color: #00f0ff;
  color: #fff;
  background: linear-gradient(
    135deg,
    rgba(0, 240, 255, 0.28),
    rgba(0, 180, 255, 0.35)
  );
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.45),
    inset 0 0 8px rgba(0, 240, 255, 0.18);
}
.cyber-pagination .page-jumper {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 5px;
  border: 1px solid rgba(0, 240, 255, 0.2);
  border-radius: 4px;
  background: rgba(0, 240, 255, 0.04);
}
.cyber-pagination .jumper-label {
  font-size: 9px;
  color: rgba(0, 240, 255, 0.62);
  font-family: Consolas, "Courier New", monospace;
}
.cyber-pagination .jumper-input {
  width: 28px;
  height: 22px;
  padding: 0 2px;
  box-sizing: border-box;
  border: 1px solid rgba(0, 240, 255, 0.3);
  border-radius: 3px;
  outline: 0;
  color: #fff;
  background: rgba(0, 30, 60, 0.7);
  font: 600 10px Consolas, "Courier New", monospace;
  text-align: center;
}
.cyber-pagination .jumper-input:focus {
  border-color: #00f0ff;
  box-shadow: 0 0 7px rgba(0, 240, 255, 0.35);
}
.cyber-pagination .jumper-input::-webkit-inner-spin-button,
.cyber-pagination .jumper-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
@keyframes pagination-scan {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}
@media (max-width: 1100px) {
  .pagination-bar.cyber-pagination {
    gap: 4px;
    padding-left: 5px;
    padding-right: 5px;
  }
  .cyber-pagination .pagination-info {
    padding-left: 4px;
    padding-right: 4px;
  }
  .cyber-pagination .info-label {
    display: none;
  }
  .cyber-pagination .page-btn,
  .cyber-pagination .page-number {
    width: 20px;
    min-width: 20px;
    height: 21px;
    font-size: 10px;
  }
  .cyber-pagination .jumper-label {
    display: none;
  }
  .cyber-pagination .jumper-input {
    width: 24px;
    height: 21px;
  }
}
.workspace {
  height: 500px;
  min-height: 500px;
  max-height: 500px;
  overflow: hidden;
}
.pursuit-page {
  height: 100%;
  min-height: 0;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}
.pursuit-page > .title-strip,
.pursuit-page > .robot-band,
.pursuit-page > .live-panel {
  flex: 0 0 auto;
}
.pursuit-page {
  width: 100%;
  box-sizing: border-box;
}
.live-panel {
  height: 165px;
  min-height: 165px;
  max-height: 165px;
  overflow: hidden;
  flex: 0 0 165px;
}
.live-panel .live-heading {
  height: 34px !important;
  font-size: 20px !important;
}
.live-grid {
  min-height: 0;
  height: 124px;
  padding-top: 5px;
  padding-bottom: 7px;
  overflow: hidden;
}
.live-identity .radar-mark {
  width: 66px;
  height: 66px;
  box-shadow: inset 0 0 0 9px #063345, 0 0 12px #00cee7;
}
.live-identity .radar-mark:before {
  height: 86px;
}
.live-identity .radar-mark:after {
  width: 86px;
}
.live-identity .radar-mark span {
  width: 12px;
  height: 12px;
}
.live-identity strong {
  font-size: 19px;
  margin: 3px 0 5px;
}
.live-grid label {
  font-size: 12px;
}
.live-identity b {
  font-size: 13px;
  margin: 1px 0 4px;
}
.live-facts {
  gap: 6px 9px;
  padding: 0 14px;
}
.live-facts em,
.condition-block em {
  padding: 3px 8px;
  font-size: 12px;
}
.condition-block {
  gap: 8px;
  padding-left: 14px;
  padding-right: 10px;
}
.cancel-block {
  gap: 8px;
  padding-left: 12px;
}
.cancel-block label {
  font-size: 12px;
}
.cancel-block .cancel-button {
  height: 36px;
  font-size: 12px;
}
.workspace {
  height: auto;
  min-height: 0;
  max-height: none;
  flex: 1 1 auto;
  overflow: hidden;
}
.content-wrapper:has(.pursuit-page) {
  overflow: hidden !important;
  padding: 18px !important;
  box-sizing: border-box;
}
.pursuit-page > .workspace {
  min-height: 0;
}
.message-detail {
  min-height: 0;
  height: calc(100% - 34px);
}
.annotated-image {
  min-height: 0;
  height: 100%;
}
.tab-container {
  flex: 0 0 42px;
  height: 42px;
  min-height: 42px;
  position: relative;
  overflow: visible;
  background: #020d17;
  border-bottom: 1px solid #07556a;
  z-index: 3;
}
.left-panel > .tab-strip {
  flex: 0 0 42px !important;
  height: 42px !important;
  min-height: 42px !important;
}
.tab-container .tab-strip {
  height: 42px !important;
  min-height: 42px !important;
  position: relative !important;
  overflow: visible !important;
}
.left-panel > .list-mode {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-height: 0;
  margin-top: 0;
  background: #03141f;
  overflow: hidden;
}
.left-panel > .list-mode .history-head {
  position: relative;
  z-index: 1;
  margin-top: 0;
  background: #03141f;
}
.left-panel,
.right-panel {
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;
}
.left-panel > .list-mode {
  overflow: hidden;
}
.message-view,
.history-detail-view {
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  overflow: auto;
}
.message-view {
  overflow-x: hidden;
  overflow-y: auto;
}
.message-detail {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  gap: 18px;
}
.message-detail > * {
  min-width: 0;
}
.left-panel > .list-mode:has(.detection-row) {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
}
.left-panel > .list-mode:has(.detection-row) {
  scrollbar-color: #08dce8 #03121c;
  scrollbar-gutter: stable;
}
.left-panel > .list-mode:has(.detection-row)::-webkit-scrollbar {
  width: 8px;
  background: #03121c;
}
.left-panel > .list-mode:has(.detection-row)::-webkit-scrollbar-track {
  background: linear-gradient(180deg, #020d17, #061b2a);
  border-left: 1px solid rgba(0, 220, 232, 0.2);
}
.left-panel > .list-mode:has(.detection-row)::-webkit-scrollbar-thumb {
  min-height: 46px;
  border: 2px solid #03121c;
  border-radius: 2px;
  background: linear-gradient(180deg, #00f0ff, #087eaa);
  box-shadow: inset 0 0 3px rgba(255, 255, 255, 0.42),
    0 0 7px rgba(0, 229, 240, 0.7);
}
.left-panel > .list-mode:has(.detection-row)::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #6cffff, #00a9d4);
  box-shadow: inset 0 0 4px rgba(255, 255, 255, 0.7),
    0 0 11px rgba(0, 240, 255, 0.95);
}
.left-panel > .list-mode:has(.detection-row) .detection-row {
  flex: 0 0 72px;
}
.left-panel > .list-mode:has(.detection-row) .detection-row:first-child {
  margin-top: 0;
}
.left-panel > .list-mode:has(.detection-row) .pagination-bar {
  flex: 0 0 46px;
  margin-top: auto;
  position: sticky;
  bottom: 0;
  z-index: 4;
  background: #071b2b;
}
.left-panel > .list-mode:has(.history-row) {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #08dce8 #03121c;
  scrollbar-gutter: stable;
}
.left-panel > .list-mode:has(.history-row)::-webkit-scrollbar {
  width: 8px;
  background: #03121c;
}
.left-panel > .list-mode:has(.history-row)::-webkit-scrollbar-track {
  background: linear-gradient(180deg, #020d17, #061b2a);
  border-left: 1px solid rgba(0, 220, 232, 0.2);
}
.left-panel > .list-mode:has(.history-row)::-webkit-scrollbar-thumb {
  min-height: 46px;
  border: 2px solid #03121c;
  border-radius: 2px;
  background: linear-gradient(180deg, #00f0ff, #087eaa);
  box-shadow: inset 0 0 3px rgba(255, 255, 255, 0.42),
    0 0 7px rgba(0, 229, 240, 0.7);
}
.left-panel > .list-mode:has(.history-row)::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #6cffff, #00a9d4);
  box-shadow: inset 0 0 4px rgba(255, 255, 255, 0.7),
    0 0 11px rgba(0, 240, 255, 0.95);
}
.left-panel > .list-mode:has(.history-row) .history-row {
  flex: 0 0 54px;
}
.left-panel > .list-mode:has(.history-row) .pagination-bar {
  flex: 0 0 46px;
  margin-top: auto;
  position: sticky;
  bottom: 0;
  z-index: 4;
  background: #071b2b;
}
.left-panel > .list-mode:has(.history-row) .time-filter {
  order: 1;
}
.left-panel > .list-mode:has(.history-row) .history-head {
  order: 2;
  flex: 0 0 auto;
}
.left-panel > .list-mode:has(.history-row) .history-row {
  order: 3;
}
.left-panel > .list-mode:has(.history-row) .pagination-bar {
  order: 4;
}
.left-panel
  > .list-mode:not(:has(.detection-row)):not(:has(.history-row))
  .time-filter {
  order: 1;
}
.left-panel
  > .list-mode:not(:has(.detection-row)):not(:has(.history-row))
  .history-head {
  order: 2;
}
.left-panel
  > .list-mode:not(:has(.detection-row)):not(:has(.history-row))
  .history-head {
  display: none;
}
.left-panel
  > .list-mode:not(:has(.detection-row)):not(:has(.history-row)):before {
  order: 3;
}
.left-panel
  > .list-mode:not(:has(.detection-row)):not(:has(.history-row))
  .pagination-bar {
  order: 4;
}
.live-identity > div:last-child {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  column-gap: 18px;
  align-content: center;
  min-width: 0;
}
.live-identity > div:last-child > label:first-of-type,
.live-identity > div:last-child > strong {
  grid-column: 1 / -1;
}
.live-identity > div:last-child > label:nth-of-type(2),
.live-identity > div:last-child > b:nth-of-type(1) {
  grid-column: 1;
}
.live-identity > div:last-child > label:nth-of-type(3),
.live-identity > div:last-child > b:nth-of-type(2) {
  grid-column: 2;
}
.live-identity > div:last-child {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-auto-flow: row;
}
.live-identity > div:last-child > label:first-child,
.live-identity > div:last-child > strong:nth-child(2) {
  grid-column: 1 / 3;
}
.live-identity > div:last-child > label:nth-child(3),
.live-identity > div:last-child > b:nth-child(4) {
  grid-column: 1;
}
.live-identity > div:last-child > label:nth-child(5),
.live-identity > div:last-child > b:nth-child(6) {
  grid-column: 2;
}
.live-identity > div:last-child {
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  column-gap: 0;
}
.live-identity > div:last-child > label:first-child,
.live-identity > div:last-child > strong:nth-child(2) {
  width: 100%;
}
.live-identity > div:last-child > label:nth-child(3),
.live-identity > div:last-child > b:nth-child(4),
.live-identity > div:last-child > label:nth-child(5),
.live-identity > div:last-child > b:nth-child(6) {
  width: 50%;
  box-sizing: border-box;
  min-width: 0;
}
.live-identity > div:last-child > b {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.live-identity > div:last-child {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  grid-template-rows: auto auto auto auto;
  column-gap: 9px;
  row-gap: 2px;
  align-content: center;
}
.live-identity > div:last-child > label:first-child {
  grid-column: 1 / 3;
  grid-row: 1;
}
.live-identity > div:last-child > strong:nth-child(2) {
  grid-column: 1 / 3;
  grid-row: 2;
}
.live-identity > div:last-child > label:nth-child(3) {
  grid-column: 1;
  grid-row: 3;
}
.live-identity > div:last-child > b:nth-child(4) {
  grid-column: 2;
  grid-row: 3;
}
.live-identity > div:last-child > label:nth-child(5) {
  grid-column: 1;
  grid-row: 4;
}
.live-identity > div:last-child > b:nth-child(6) {
  grid-column: 2;
  grid-row: 4;
}
.live-identity > div:last-child {
  display: grid !important;
  grid-template-columns: max-content minmax(0, 1fr) !important;
  grid-template-rows: auto auto auto auto !important;
  column-gap: 8px !important;
  row-gap: 2px !important;
  align-content: center !important;
}
.live-identity > div:last-child > label:first-child {
  grid-column: 1 / 3 !important;
  grid-row: 1 !important;
  width: auto !important;
}
.live-identity > div:last-child > strong:nth-child(2) {
  grid-column: 1 / 3 !important;
  grid-row: 2 !important;
  width: auto !important;
}
.live-identity > div:last-child > label:nth-child(3) {
  grid-column: 1 !important;
  grid-row: 3 !important;
  width: auto !important;
}
.live-identity > div:last-child > b:nth-child(4) {
  grid-column: 2 !important;
  grid-row: 3 !important;
  width: auto !important;
  min-width: 0 !important;
}
.live-identity > div:last-child > label:nth-child(5) {
  grid-column: 1 !important;
  grid-row: 4 !important;
  width: auto !important;
}
.live-identity > div:last-child > b:nth-child(6) {
  grid-column: 2 !important;
  grid-row: 4 !important;
  width: auto !important;
  min-width: 0 !important;
}
.live-identity > div:last-child {
  grid-template-columns: max-content minmax(0, 1fr) !important;
  grid-template-rows: auto auto auto auto !important;
}
.live-identity > div:last-child {
  display: grid !important;
  flex-wrap: initial !important;
}
.live-identity > div:last-child {
  display: grid !important;
  grid-template-columns: max-content minmax(0, 1fr) !important;
  grid-template-rows: auto auto auto !important;
  align-content: center !important;
  column-gap: 10px !important;
  row-gap: 3px !important;
}
.live-identity > div:last-child > label:first-child {
  grid-column: 1 !important;
  grid-row: 1 !important;
  width: auto !important;
}
.live-identity > div:last-child > strong:nth-child(2) {
  grid-column: 2 !important;
  grid-row: 1 !important;
  width: auto !important;
}
.live-identity > div:last-child > label:nth-child(3) {
  grid-column: 1 !important;
  grid-row: 2 !important;
  width: auto !important;
}
.live-identity > div:last-child > b:nth-child(4) {
  grid-column: 2 !important;
  grid-row: 2 !important;
  width: auto !important;
}
.live-identity > div:last-child > label:nth-child(5) {
  grid-column: 1 !important;
  grid-row: 3 !important;
  width: auto !important;
}
.live-identity > div:last-child > b:nth-child(6) {
  grid-column: 2 !important;
  grid-row: 3 !important;
  width: auto !important;
}
.live-identity > div:last-child {
  grid-template-columns: max-content minmax(0, 1fr) !important;
  justify-content: initial !important;
  text-align: left !important;
}
.live-identity > div:last-child > label,
.live-identity > div:last-child > b,
.live-identity > div:last-child > strong {
  justify-self: initial !important;
  text-align: left !important;
}
.live-identity > div:last-child > label:first-child {
  line-height: 19px;
  height: 19px;
  display: block;
}
.live-identity > div:last-child > strong:nth-child(2) {
  margin: 0 !important;
  line-height: 20px;
  height: 20px;
  align-self: center;
}
.live-identity > div:last-child > label {
  margin-top: 0;
}
.message-view {
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: hidden;
}
.message-view > .detail-time {
  flex: 0 0 24px;
}
.message-view > .message-detail {
  flex: 1 1 auto;
  height: auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}
.message-view > .message-detail .annotated-image {
  flex: 2 1 420px;
  min-height: 0;
  height: auto;
}
.history-detail {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  height: 100%;
}
.history-detail .history-title {
  flex: 0 0 40px;
}
.history-detail .summary {
  flex: 0 0 auto;
  min-height: 0;
}
.history-detail h3 {
  flex: 0 0 auto;
  margin-top: 8px;
}
.history-detail h3 {
  height: 40px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 0 14px;
  margin: 8px 0 0;
  border: 1px solid #075a6c;
  border-bottom: 0;
  color: #11e5ed;
  font-size: 16px;
  line-height: 1;
}
.history-title {
  padding-left: 14px !important;
}
.history-title strong,
.history-detail h3 {
  letter-spacing: 0.2px;
}
.history-detail .operations-head {
  flex: 0 0 auto;
}
.history-detail .operation-row {
  flex: 0 0 auto;
}
.history-detail .operations-head {
  font-size: 13px !important;
  line-height: 1.35 !important;
  font-weight: 600 !important;
  color: #18e5ed !important;
}
.history-detail .operation-row > span {
  font-size: 13px !important;
  line-height: 1.35 !important;
  color: #d7edf2 !important;
}
.history-detail .operation-row > span:nth-child(2) {
  font-size: 11px !important;
}
.history-detail .operation-row > span:nth-child(2) em {
  font-size: 11px !important;
}
.history-detail .operations-head,
.history-detail .operation-row {
  grid-template-columns: 100px 150px 155px minmax(160px, 1fr) 155px minmax(
      150px,
      1fr
    ) !important;
  column-gap: 8px !important;
}
.history-detail .operations-head > span,
.history-detail .operation-row > span {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.history-detail .operation-row > span:nth-child(3),
.history-detail .operation-row > span:nth-child(5),
.history-detail .operation-row > span:nth-child(6) {
  white-space: nowrap !important;
  overflow: visible;
  text-overflow: clip;
}
.history-detail .operation-row > span:nth-child(4) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.left-panel > .list-mode:not(:has(.detection-row)):not(:has(.history-row)) {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.left-panel
  > .list-mode:not(:has(.detection-row)):not(:has(.history-row))
  .time-filter {
  order: 1;
  flex: 0 0 auto;
}
.left-panel
  > .list-mode:not(:has(.detection-row)):not(:has(.history-row)):before {
  order: 2;
  flex: 1 1 auto;
  min-height: 0;
  content: "";
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5f8792;
  font-size: 12px;
}
.left-panel
  > .list-mode:not(:has(.detection-row)):not(:has(.history-row))
  .pagination-bar {
  order: 3;
  flex: 0 0 46px;
  margin-top: 0;
}
.history-detail .operations-head {
  flex: 0 0 auto;
}
.history-detail .operations-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #08dce8 #03121c;
  scrollbar-gutter: stable;
}
.history-detail .operations-scroll::-webkit-scrollbar {
  width: 8px;
  background: #03121c;
}
.history-detail .operations-scroll::-webkit-scrollbar-track {
  background: linear-gradient(180deg, #020d17, #061b2a);
  border-left: 1px solid rgba(0, 220, 232, 0.2);
}
.history-detail .operations-scroll::-webkit-scrollbar-thumb {
  min-height: 46px;
  border: 2px solid #03121c;
  border-radius: 2px;
  background: linear-gradient(180deg, #00f0ff, #087eaa);
  box-shadow: inset 0 0 3px rgba(255, 255, 255, 0.42),
    0 0 7px rgba(0, 229, 240, 0.7);
}
.history-detail .operations-scroll::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #6cffff, #00a9d4);
}
.history-detail-view {
  overflow: hidden !important;
}
.right-empty-state {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  height: auto !important;
}
.message-view:has(.right-empty-state),
.history-detail-view:has(.right-empty-state) {
  display: flex;
  flex-direction: column;
  overflow: hidden !important;
}
.right-panel .message-view:has(> .right-empty-state),
.right-panel .history-detail-view:has(> .right-empty-state) {
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
  min-height: 0 !important;
  overflow: hidden !important;
}
.right-panel .message-view > .right-empty-state,
.right-panel .history-detail-view > .right-empty-state {
  display: flex !important;
  position: relative !important;
  flex: 1 1 auto !important;
  width: 100% !important;
  height: auto !important;
  min-height: 0 !important;
  align-items: center !important;
  justify-content: center !important;
}
.time-filter {
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  min-width: 0;
  padding: 6px 8px;
  border-bottom: 1px solid rgba(0, 210, 225, 0.3);
  background: #041824;
  box-sizing: border-box;
}
.time-filter label {
  flex: 0 0 auto;
  color: #68c5d0;
  font-size: 11px;
  white-space: nowrap;
}
.time-filter .pursuit-date-picker {
  flex: 1 1 auto;
  min-width: 0;
  width: 0 !important;
}
.time-filter /deep/ .ivu-date-picker {
  display: block;
  width: 100% !important;
  min-width: 0;
}
.time-filter /deep/ .ivu-date-picker-rel,
.time-filter /deep/ .ivu-input-wrapper {
  width: 100% !important;
  min-width: 0;
}
.time-filter /deep/ .ivu-input {
  height: 28px;
  padding: 0 24px 0 8px;
  border: 1px solid #176073;
  border-radius: 4px;
  color: #d9f6f8;
  background: #06121c;
  font-size: 10px;
}
.time-filter /deep/ .ivu-input:focus {
  border-color: #08dce8;
  box-shadow: 0 0 5px rgba(0, 220, 232, 0.3);
}
.time-filter /deep/ .ivu-input-icon {
  height: 28px;
  line-height: 28px;
  color: #58bbc7;
}
.time-filter button {
  flex: 0 0 auto;
  height: 26px;
  padding: 0 8px;
  border: 1px solid #0a9eac;
  border-radius: 4px;
  color: #d9fcff;
  background: #07303b;
  font-size: 11px;
  cursor: pointer;
}
.time-filter button:hover {
  border-color: #1de7ef;
  background: #0b4551;
}
.time-filter button.filter-reset {
  padding: 0 6px;
  border-color: #35535a;
  color: #a9c0c4;
  background: #10222a;
}
.time-filter .event-status-filter {
  flex: 0 0 92px;
  width: 92px;
}
.history-status-filter .event-status-filter {
  flex: 1 1 auto;
  width: auto;
  min-width: 0;
}
.history-status-filter .history-time-label {
  flex: 0 0 auto;
  margin-left: 3px;
}
.history-status-filter {
  flex-wrap: nowrap;
  row-gap: 0;
}
.history-status-filter .event-status-filter {
  flex: 0 0 118px;
  width: 118px;
}
.history-status-filter .history-time-label {
  flex: 0 0 auto;
  margin-left: 0;
}
.history-status-filter .history-date-picker {
  flex: 1 1 280px !important;
  min-width: 0;
  width: 0 !important;
}
.history-status-filter .history-date-picker .ivu-date-picker-rel {
  width: 100% !important;
}
.history-status-filter .history-date-picker .ivu-input {
  font-size: 10px !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
}
.history-status-filter {
  gap: 3px !important;
  padding-left: 5px;
  padding-right: 5px;
}
.history-status-filter > label {
  font-size: 10px;
}
.history-status-filter .event-status-filter {
  flex: 0 0 82px;
  width: 82px;
}
.history-status-filter .history-time-label {
  flex: 0 0 48px;
}
.history-status-filter .history-date-picker {
  flex: 1 1 auto !important;
}
.history-status-filter > button {
  padding-left: 5px;
  padding-right: 5px;
  font-size: 10px;
}
.time-filter /deep/ .event-status-filter .ivu-select-selection {
  height: 32px;
  border: 1px solid rgba(0, 240, 255, 0.3);
  border-radius: 4px;
  background: rgba(0, 30, 60, 0.6);
}
.time-filter /deep/ .event-status-filter .ivu-select-selected-value,
.time-filter /deep/ .event-status-filter .ivu-select-placeholder {
  height: 30px;
  line-height: 30px;
  color: #fff;
  font-size: 10px;
}
.time-filter /deep/ .event-status-filter .ivu-select-arrow {
  color: rgba(0, 240, 255, 0.7);
}
.time-filter /deep/ .ivu-select-dropdown {
  z-index: 10000 !important;
  background: rgba(0, 21, 41, 0.98) !important;
  border: 1px solid rgba(0, 240, 255, 0.4) !important;
  color: #fff !important;
}
.time-filter /deep/ .ivu-select-item {
  color: rgba(255, 255, 255, 0.75) !important;
}
.time-filter /deep/ .ivu-select-item:hover,
.time-filter /deep/ .ivu-select-item-selected {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.16) !important;
}
.pursuit-date-theme-active .ivu-select-dropdown {
  background: rgba(0, 21, 41, 0.98) !important;
  border: 1px solid rgba(0, 240, 255, 0.4) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.55), 0 0 14px rgba(0, 240, 255, 0.25) !important;
}
.pursuit-date-theme-active .ivu-select-dropdown::-webkit-scrollbar {
  width: 7px;
  background: #03121c;
}
.pursuit-date-theme-active .ivu-select-dropdown::-webkit-scrollbar-track {
  background: linear-gradient(180deg, #020d17, #061b2a);
  border-left: 1px solid rgba(0, 220, 232, 0.2);
}
.pursuit-date-theme-active .ivu-select-dropdown::-webkit-scrollbar-thumb {
  border: 1px solid #03121c;
  border-radius: 4px;
  background: linear-gradient(180deg, #00f0ff, #087eaa);
  box-shadow: 0 0 6px rgba(0, 229, 240, 0.65);
}
.pursuit-date-theme-active .ivu-select-dropdown::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #6cffff, #00a9d4);
}
.pursuit-date-theme-active .ivu-select-dropdown {
  scrollbar-width: thin;
  scrollbar-color: #08dce8 #03121c;
}
.pursuit-date-theme-active .ivu-select-dropdown .ivu-select-dropdown-list {
  scrollbar-width: thin;
  scrollbar-color: #08dce8 #03121c;
}
.pursuit-date-theme-active
  .ivu-select-dropdown
  .ivu-select-dropdown-list::-webkit-scrollbar {
  width: 7px;
  background: #03121c;
}
.pursuit-date-theme-active
  .ivu-select-dropdown
  .ivu-select-dropdown-list::-webkit-scrollbar-track {
  background: linear-gradient(180deg, #020d17, #061b2a);
  border-left: 1px solid rgba(0, 220, 232, 0.2);
}
.pursuit-date-theme-active
  .ivu-select-dropdown
  .ivu-select-dropdown-list::-webkit-scrollbar-thumb {
  border: 1px solid #03121c;
  border-radius: 4px;
  background: linear-gradient(180deg, #00f0ff, #087eaa);
  box-shadow: 0 0 6px rgba(0, 229, 240, 0.65);
}
.pursuit-date-theme-active
  .ivu-select-dropdown
  .ivu-select-dropdown-list::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #6cffff, #00a9d4);
}
.left-panel > .list-mode:has(.detection-row) .time-filter,
.left-panel > .list-mode:has(.history-row) .time-filter {
  position: sticky;
  top: 0;
  z-index: 3;
}
.time-filter {
  position: relative;
  z-index: 8;
  overflow: visible;
}
.time-filter .pursuit-date-picker {
  position: relative;
  z-index: 9;
}
.time-filter /deep/ .ivu-date-picker-rel {
  position: relative;
}
.time-filter /deep/ .ivu-date-picker .ivu-input {
  height: 32px !important;
  padding: 0 26px 0 9px !important;
  border: 1px solid rgba(0, 240, 255, 0.3) !important;
  border-radius: 4px !important;
  color: #fff !important;
  background: rgba(0, 30, 60, 0.6) !important;
  font-size: 11px !important;
}
.time-filter /deep/ .ivu-date-picker .ivu-input:hover {
  border-color: rgba(0, 240, 255, 0.5) !important;
}
.time-filter /deep/ .ivu-date-picker .ivu-input:focus {
  border-color: #00f0ff !important;
  box-shadow: 0 0 8px rgba(0, 240, 255, 0.4) !important;
}
.time-filter /deep/ .ivu-date-picker .ivu-input::-webkit-input-placeholder {
  color: rgba(255, 255, 255, 0.4) !important;
}
.time-filter /deep/ .ivu-date-picker .ivu-icon {
  color: rgba(0, 240, 255, 0.7) !important;
}

.time-filter /deep/ .ivu-picker-panel {
  overflow: hidden;
  border: 1px solid rgba(0, 240, 255, 0.4) !important;
  border-radius: 6px;
  background: rgba(0, 21, 41, 0.98) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 20px rgba(0, 240, 255, 0.3) !important;
  backdrop-filter: blur(15px);
}
.time-filter /deep/ .ivu-picker-panel-body {
  background: transparent !important;
}
.time-filter /deep/ .ivu-picker-panel-content {
  background: transparent !important;
}
.time-filter /deep/ .ivu-date-picker-header {
  border-bottom: 1px solid rgba(0, 240, 255, 0.2) !important;
}
.time-filter /deep/ .ivu-date-picker-header-label {
  color: #00f0ff !important;
  font-weight: 600;
}
.time-filter /deep/ .ivu-date-picker-header-label:hover {
  color: #fff !important;
  background: rgba(0, 240, 255, 0.1) !important;
}
.time-filter /deep/ .ivu-date-picker-header .ivu-icon {
  color: rgba(0, 240, 255, 0.8) !important;
}
.time-filter /deep/ .ivu-date-picker-cells-header span {
  color: rgba(0, 240, 255, 0.8) !important;
  font-weight: 600 !important;
}
.time-filter /deep/ .ivu-date-picker-cells-cell {
  color: rgba(255, 255, 255, 0.7) !important;
  transition: background 0.2s, color 0.2s;
}
.time-filter /deep/ .ivu-date-picker-cells-cell:hover {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.15) !important;
}
.time-filter /deep/ .ivu-date-picker-cells-cell-selected {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.25) !important;
}
.time-filter /deep/ .ivu-date-picker-cells-cell-selected em {
  color: #00f0ff !important;
  background: transparent !important;
  font-weight: 700 !important;
}
.time-filter /deep/ .ivu-date-picker-cells-cell-today em {
  color: #ffa500 !important;
  font-weight: 600 !important;
}
.time-filter /deep/ .ivu-date-picker-cells-cell-disabled {
  color: rgba(255, 255, 255, 0.2) !important;
  background: transparent !important;
}
.time-filter /deep/ .ivu-date-picker-cells-cell-prev-month,
.time-filter /deep/ .ivu-date-picker-cells-cell-next-month {
  color: rgba(255, 255, 255, 0.3) !important;
}
.time-filter /deep/ .ivu-date-picker-cells-cell-range {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.08) !important;
}
.time-filter /deep/ .ivu-date-picker-cells-cell-range:before {
  background: none !important;
}
.time-filter /deep/ .ivu-picker-panel-footer {
  border-top: 1px solid rgba(0, 240, 255, 0.2) !important;
}
.time-filter /deep/ .ivu-picker-confirm-time {
  color: #fff !important;
  background: transparent !important;
}
.time-filter /deep/ .ivu-picker-confirm-time:hover {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.15) !important;
}
.time-filter /deep/ .ivu-time-picker-cells ul li {
  color: rgba(255, 255, 255, 0.7) !important;
}
.time-filter /deep/ .ivu-time-picker-cells ul li:hover {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.15) !important;
}
.time-filter
  /deep/
  .ivu-time-picker-cells
  ul
  li.ivu-time-picker-cells-cell-selected {
  color: #00f0ff !important;
  background: rgba(0, 240, 255, 0.25) !important;
  font-weight: 600 !important;
}
.pursuit-date-theme-active
  .ivu-select-dropdown[style*="z-index"]:not(.ivu-date-picker-transfer),
.pursuit-date-theme-active .ivu-select-dropdown .ivu-select-dropdown-list {
  scrollbar-width: thin;
  scrollbar-color: #08dce8 #03121c;
}
.pursuit-date-theme-active
  .ivu-select-dropdown[style*="z-index"]:not(
    .ivu-date-picker-transfer
  )::-webkit-scrollbar,
.pursuit-date-theme-active
  .ivu-select-dropdown
  .ivu-select-dropdown-list::-webkit-scrollbar {
  width: 7px;
  background: #03121c;
}
.pursuit-date-theme-active
  .ivu-select-dropdown[style*="z-index"]:not(
    .ivu-date-picker-transfer
  )::-webkit-scrollbar-track,
.pursuit-date-theme-active
  .ivu-select-dropdown
  .ivu-select-dropdown-list::-webkit-scrollbar-track {
  background: linear-gradient(180deg, #020d17, #061b2a);
  border-left: 1px solid rgba(0, 220, 232, 0.2);
}
.pursuit-date-theme-active
  .ivu-select-dropdown[style*="z-index"]:not(
    .ivu-date-picker-transfer
  )::-webkit-scrollbar-thumb,
.pursuit-date-theme-active
  .ivu-select-dropdown
  .ivu-select-dropdown-list::-webkit-scrollbar-thumb {
  border: 1px solid #03121c;
  border-radius: 4px;
  background: linear-gradient(180deg, #00f0ff, #087eaa);
  box-shadow: 0 0 6px rgba(0, 229, 240, 0.65);
}
.pursuit-date-theme-active
  .ivu-select-dropdown[style*="z-index"]:not(
    .ivu-date-picker-transfer
  )::-webkit-scrollbar-thumb:hover,
.pursuit-date-theme-active
  .ivu-select-dropdown
  .ivu-select-dropdown-list::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #6cffff, #00a9d4);
}
</style>

<style scoped>
.tab-strip .workspace-tab:after {
  content: none !important;
  display: none !important;
}
.tab-strip .workspace-tab {
  font-size: 12px !important;
  color: #d7edf2 !important;
}
.tab-strip .workspace-tab b {
  display: none !important;
}
.list-empty-state {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5f8792;
  font-size: 14px;
  letter-spacing: 0.4px;
}
.left-panel > .list-mode .list-empty-state {
  order: 2;
  flex: 1 1 auto;
}
.left-panel > .list-mode .pagination-bar {
  order: 3;
}
.tab-strip .workspace-tab,
.tab-strip .workspace-tab:first-child,
.tab-strip .workspace-tab.history-tab {
  font-size: 12px !important;
  color: #d7edf2 !important;
  text-indent: 0 !important;
}
.tab-strip .workspace-tab:after,
.tab-strip .workspace-tab:first-child:after,
.tab-strip .workspace-tab.history-tab:after {
  display: none !important;
  content: none !important;
}
</style>

<style scoped>
/* Keep the four live-event regions inside the available content width. */
.pursuit-page .live-grid { grid-template-columns: minmax(0, 1.25fr) minmax(0, 1.75fr) minmax(0, .62fr) minmax(0, .48fr) !important; min-width: 0 !important; }
.pursuit-page .live-grid > *, .pursuit-page .live-identity, .pursuit-page .live-facts, .pursuit-page .condition-block, .pursuit-page .cancel-block { min-width: 0 !important; box-sizing: border-box; }
.pursuit-page .live-identity { gap: 7px !important; padding-right: 7px !important; }
.pursuit-page .live-identity > div:last-child { display: grid !important; grid-template-columns: max-content minmax(0, 1fr) !important; width: 100% !important; min-width: 0 !important; column-gap: 6px !important; }
.pursuit-page .live-identity > div:last-child > label, .pursuit-page .live-identity > div:last-child > strong, .pursuit-page .live-identity > div:last-child > b { min-width: 0 !important; max-width: 100% !important; overflow: hidden !important; text-overflow: ellipsis !important; white-space: nowrap !important; }
.pursuit-page .live-identity > div:last-child > label { font-size: clamp(9px, .72vw, 12px) !important; }
.pursuit-page .live-identity > div:last-child > strong { font-size: clamp(11px, 1.05vw, 17px) !important; line-height: 1.2 !important; }
.pursuit-page .live-identity > div:last-child > b { font-size: clamp(10px, .78vw, 13px) !important; }
.pursuit-page .live-facts > div { min-width: 0 !important; gap: 4px !important; }
.pursuit-page .live-facts label { max-width: 48% !important; overflow: hidden !important; text-overflow: ellipsis !important; white-space: nowrap !important; font-size: clamp(9px, .7vw, 12px) !important; }
.pursuit-page .live-facts b, .pursuit-page .live-facts em, .pursuit-page .condition-block em, .pursuit-page .cancel-button { min-width: 0 !important; max-width: 100% !important; overflow: hidden !important; text-overflow: ellipsis !important; white-space: nowrap !important; }
.pursuit-page .live-facts b { flex: 1 1 auto; font-size: clamp(9px, .72vw, 12px) !important; }
.pursuit-page .live-facts em, .pursuit-page .condition-block em { padding: 2px 5px !important; font-size: clamp(9px, .72vw, 12px) !important; }
.pursuit-page .condition-block { padding-left: 6px !important; padding-right: 5px !important; gap: 6px !important; overflow: hidden !important; }
.pursuit-page .cancel-block { padding-left: 6px !important; gap: 5px !important; overflow: hidden !important; }
.pursuit-page .cancel-block .cancel-button { width: 100% !important; height: clamp(28px, 2.2vw, 35px) !important; padding: 0 4px !important; font-size: clamp(9px, .72vw, 12px) !important; }
@media (max-width: 760px) {
  .pursuit-page .live-panel { height: auto !important; min-height: 190px !important; max-height: none !important; }
  .pursuit-page .live-grid { grid-template-columns: 1fr 1fr !important; grid-auto-rows: minmax(72px, auto); height: auto !important; min-height: 150px; overflow: visible !important; }
  .pursuit-page .live-identity { grid-row: span 2; }
}
</style>

<style scoped>
/* Final responsive overrides for the compact live-event frame. */
.pursuit-page .history-head {
  grid-template-columns: 1.05fr 1.15fr 1.15fr .95fr !important;
}
.pursuit-page .history-row {
  grid-template-columns: 1.05fr 1.15fr 1.15fr .95fr 16px !important;
}
.pursuit-page .live-grid {
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 1.75fr) minmax(0, .62fr) minmax(0, .48fr) !important;
  min-width: 0 !important;
}
.pursuit-page .live-grid > *,
.pursuit-page .live-identity,
.pursuit-page .live-facts,
.pursuit-page .condition-block,
.pursuit-page .cancel-block { min-width: 0 !important; box-sizing: border-box; }
.pursuit-page .live-identity { gap: 13px !important; padding-right: 7px !important; }
.pursuit-page .live-identity > div:last-child {
  display: grid !important;
  grid-template-columns: max-content minmax(0, 1fr) !important;
  width: calc(100% - 10px) !important;
  position: relative !important;
  left: 10px !important;
  min-width: 0 !important;
  column-gap: 6px !important;
}
.pursuit-page .live-identity > div:last-child > label,
.pursuit-page .live-identity > div:last-child > strong,
.pursuit-page .live-identity > div:last-child > b {
  min-width: 0 !important;
  max-width: 100% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}
.pursuit-page .live-identity > div:last-child > label { font-size: clamp(9px, .72vw, 12px) !important; }
.pursuit-page .live-identity > div:last-child > strong { font-size: clamp(11px, 1.05vw, 17px) !important; line-height: 1.2 !important; }
.pursuit-page .live-identity > div:last-child > b { font-size: clamp(10px, .78vw, 13px) !important; }
.pursuit-page .live-facts > div { min-width: 0 !important; gap: 4px !important; }
.pursuit-page .live-facts label { max-width: 48% !important; overflow: hidden !important; text-overflow: ellipsis !important; white-space: nowrap !important; font-size: clamp(9px, .7vw, 12px) !important; }
.pursuit-page .live-facts b,
.pursuit-page .live-facts em,
.pursuit-page .condition-block em,
.pursuit-page .cancel-button { min-width: 0 !important; max-width: 100% !important; overflow: hidden !important; text-overflow: ellipsis !important; white-space: nowrap !important; }
.pursuit-page .live-facts b { flex: 1 1 auto; font-size: clamp(9px, .72vw, 12px) !important; }
.pursuit-page .live-facts em,
.pursuit-page .condition-block em { padding: 2px 5px !important; font-size: clamp(9px, .72vw, 12px) !important; }
.pursuit-page .condition-block { padding-left: 6px !important; padding-right: 5px !important; gap: 6px !important; overflow: hidden !important; }
.pursuit-page .cancel-block { padding-left: 6px !important; gap: 5px !important; overflow: hidden !important; }
.pursuit-page .cancel-block .cancel-button { width: 100% !important; height: clamp(28px, 2.2vw, 35px) !important; padding: 0 4px !important; font-size: clamp(9px, .72vw, 12px) !important; }
@media (max-width: 760px) {
  .pursuit-page .live-panel { height: auto !important; min-height: 190px !important; max-height: none !important; }
  .pursuit-page .live-grid { grid-template-columns: 1fr 1fr !important; grid-auto-rows: minmax(72px, auto); height: auto !important; min-height: 150px; overflow: visible !important; }
  .pursuit-page .live-identity { grid-row: span 2; }
}
</style>

<style scoped>
/* Final column ratios for the rebuilt live panel (must remain last in the file). */
.pursuit-page .live-panel .live-grid {
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr) minmax(0, 1.15fr) minmax(0, .62fr) !important;
}
.pursuit-page .live-panel .live-grid > * { min-width: 0 !important; }
.pursuit-page .live-panel .live-grid label { max-width: 56% !important; }
.pursuit-page .live-panel .live-grid em.live-chip {
  flex: 0 1 auto !important;
  min-width: 0 !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-grid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, .6fr) !important;
  }
}
@media (max-width: 760px) {
  .pursuit-page .live-panel .live-grid { grid-template-columns: 1fr 1fr !important; }
}
</style>
<style scoped>
/* Last-word: keep field labels from being squeezed by chip shrink. */
.pursuit-page .live-panel .live-grid label {
  flex: 0 0 auto !important;
  max-width: 58% !important;
}
.pursuit-page .live-panel .live-grid em.live-chip {
  flex: 0 1 auto !important;
}
</style>
<style scoped>
/* Last-word: stop legacy label rules from shrinking field titles. */
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
  flex: 0 0 auto !important;
  max-width: none !important;
  width: auto !important;
}
.pursuit-page .live-panel .live-facts > .live-field > label {
  flex: 0 0 auto !important;
  max-width: none !important;
  width: auto !important;
}
.pursuit-page .live-panel .live-grid em.live-chip {
  flex: 0 1 auto !important;
  min-width: 0 !important;
}
</style>
<style scoped>
/* Last-word: restore flex layout on the identity column of the rebuilt live panel. */
.pursuit-page .live-panel .live-identity {
  display: flex !important;
  align-items: center !important;
  gap: 14px !important;
  padding: 4px 16px !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields {
  display: flex !important;
  flex-direction: column !important;
  gap: 9px !important;
  flex: 1 1 auto !important;
  width: auto !important;
  min-width: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 8px !important;
  width: 100% !important;
  min-width: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
  flex: 0 0 auto !important;
  max-width: none !important;
  width: auto !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em.live-chip {
  flex: 0 1 auto !important;
  min-width: 0 !important;
}
.pursuit-page .live-panel .live-identity > .radar-mark { flex: 0 0 auto !important; }
</style>
<style scoped>
/* Animated radar sweep for the live-event panel (must remain last in the file). */
.pursuit-page .live-panel .live-identity > .radar-mark {
  position: relative !important;
  flex: 0 0 auto !important;
  width: clamp(50px, 4.6vw, 68px) !important;
  height: clamp(50px, 4.6vw, 68px) !important;
  border-radius: 50% !important;
  border: 1px solid rgba(53, 226, 255, .45) !important;
  overflow: hidden !important;
  background:
    radial-gradient(circle, rgba(23, 118, 168, .26), rgba(4, 20, 34, .04) 72%),
    linear-gradient(90deg,
      transparent calc(50% - .5px), rgba(53, 226, 255, .2) calc(50% - .5px),
      rgba(53, 226, 255, .2) calc(50% + .5px), transparent calc(50% + .5px)),
    linear-gradient(0deg,
      transparent calc(50% - .5px), rgba(53, 226, 255, .2) calc(50% - .5px),
      rgba(53, 226, 255, .2) calc(50% + .5px), transparent calc(50% + .5px)) !important;
  box-shadow: 0 0 14px rgba(31, 200, 255, .3), inset 0 0 16px rgba(31, 200, 255, .16) !important;
}
/* rotating sweep beam */
.pursuit-page .live-panel .live-identity > .radar-mark:before {
  content: "" !important;
  position: absolute !important;
  inset: 0 !important;
  width: auto !important;
  height: auto !important;
  left: auto !important;
  top: auto !important;
  border-radius: 50% !important;
  background: conic-gradient(from 0deg,
    rgba(110, 245, 255, .5) 0deg,
    rgba(53, 226, 255, .16) 40deg,
    rgba(53, 226, 255, .04) 74deg,
    transparent 96deg,
    transparent 360deg) !important;
  animation: pursuitRadarSweep 3.6s linear infinite !important;
}
/* expanding ping ring */
.pursuit-page .live-panel .live-identity > .radar-mark:after {
  content: "" !important;
  position: absolute !important;
  inset: 24% !important;
  width: auto !important;
  height: auto !important;
  left: auto !important;
  top: auto !important;
  border-radius: 50% !important;
  border: 1px solid rgba(53, 226, 255, .4) !important;
  background: none !important;
  animation: pursuitRadarPing 2.6s ease-out infinite !important;
}
/* breathing core */
.pursuit-page .live-panel .live-identity > .radar-mark span {
  position: absolute !important;
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
  width: 8px !important;
  height: 8px !important;
  border-radius: 50% !important;
  background: #9dfbff !important;
  box-shadow: 0 0 10px rgba(120, 240, 255, .95), 0 0 20px rgba(53, 226, 255, .6) !important;
  animation: pursuitRadarCore 2.4s ease-in-out infinite !important;
}
@keyframes pursuitRadarSweep {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes pursuitRadarPing {
  0% { transform: scale(.45); opacity: .9; }
  70% { transform: scale(1.5); opacity: 0; }
  100% { transform: scale(1.5); opacity: 0; }
}
@keyframes pursuitRadarCore {
  0%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: .6; transform: translate(-50%, -50%) scale(.78); }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .live-identity > .radar-mark:before,
  .pursuit-page .live-panel .live-identity > .radar-mark:after,
  .pursuit-page .live-panel .live-identity > .radar-mark span { animation: none !important; }
}
</style>
<style scoped>
/* Last-word: widen column 1 for sourceAnnotatedImageUrl, tighten columns 2-4. */
.pursuit-page .live-panel .live-grid {
  grid-template-columns: minmax(0, 1.85fr) minmax(0, .92fr) minmax(0, .98fr) minmax(0, .55fr) !important;
}
.pursuit-page .live-panel .live-identity {
  gap: 12px !important;
  padding-right: 14px !important;
}
.pursuit-page .live-panel .live-source-image {
  max-width: 40% !important;
  height: clamp(62px, 6.8vw, 108px) !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-grid {
    grid-template-columns: minmax(0, 1.5fr) minmax(0, .9fr) minmax(0, .95fr) minmax(0, .58fr) !important;
  }
  .pursuit-page .live-panel .live-source-image { max-width: 34% !important; }
}
</style>
<style scoped>
/* Last-word: rebalance so the robot-condition chip fits without clipping. */
.pursuit-page .live-panel .live-grid {
  grid-template-columns: minmax(0, 1.7fr) minmax(0, .88fr) minmax(0, 1.12fr) minmax(0, .55fr) !important;
}
.pursuit-page .live-panel .live-grid label { font-size: clamp(10px, .78vw, 14px) !important; }
.pursuit-page .live-panel .live-grid em.live-chip {
  font-size: clamp(10px, .76vw, 13px) !important;
  padding: 4px 9px !important;
}
.pursuit-page .live-panel .live-source-image { max-width: 38% !important; }
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-grid {
    grid-template-columns: minmax(0, 1.42fr) minmax(0, .86fr) minmax(0, 1.06fr) minmax(0, .56fr) !important;
  }
}
</style>
<style scoped>
/* Last-word: fit the longest chip (暂无工况快照) without clipping. */
.pursuit-page .live-panel .live-grid {
  grid-template-columns: minmax(0, 1.62fr) minmax(0, .86fr) minmax(0, 1.2fr) minmax(0, .56fr) !important;
}
.pursuit-page .live-panel .live-grid em.live-chip {
  flex: 0 0 auto !important;
  max-width: 100% !important;
}
.pursuit-page .live-panel .live-source-image { max-width: 36% !important; }
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-grid {
    grid-template-columns: minmax(0, 1.36fr) minmax(0, .84fr) minmax(0, 1.14fr) minmax(0, .57fr) !important;
  }
}
</style>

<style scoped>
/* Tech-style placeholder for the missing source annotated image. */
.pursuit-page .live-panel .live-source-placeholder {
  position: relative !important;
  flex: 0 0 auto !important;
  width: clamp(74px, 7.2vw, 118px) !important;
  height: clamp(62px, 6.8vw, 108px) !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid rgba(53, 226, 255, .34) !important;
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(11, 44, 70, .72), rgba(4, 17, 30, .82)),
    repeating-linear-gradient(0deg, rgba(53, 226, 255, .07) 0 1px, transparent 1px 6px);
  box-shadow: inset 0 0 16px rgba(31, 200, 255, .14) !important;
}
/* corner brackets */
.pursuit-page .live-panel .live-source-placeholder .live-source-frame {
  position: absolute;
  inset: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  background:
    linear-gradient(90deg, #35e2ff, #35e2ff) left top / 9px 1.5px no-repeat,
    linear-gradient(90deg, #35e2ff, #35e2ff) left top / 1.5px 9px no-repeat,
    linear-gradient(90deg, #35e2ff, #35e2ff) right top / 9px 1.5px no-repeat,
    linear-gradient(90deg, #35e2ff, #35e2ff) right top / 1.5px 9px no-repeat,
    linear-gradient(90deg, #35e2ff, #35e2ff) left bottom / 9px 1.5px no-repeat,
    linear-gradient(90deg, #35e2ff, #35e2ff) left bottom / 1.5px 9px no-repeat,
    linear-gradient(90deg, #35e2ff, #35e2ff) right bottom / 9px 1.5px no-repeat,
    linear-gradient(90deg, #35e2ff, #35e2ff) right bottom / 1.5px 9px no-repeat;
  opacity: .85;
  animation: pursuitFrameGlow 2.8s ease-in-out infinite;
}
/* vertical scan sweep */
.pursuit-page .live-panel .live-source-placeholder .live-source-scan {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 34%;
  background: linear-gradient(180deg,
    rgba(53, 226, 255, 0),
    rgba(53, 226, 255, .3) 55%,
    rgba(120, 245, 255, .55) 92%,
    rgba(53, 226, 255, 0));
  box-shadow: 0 0 12px rgba(53, 226, 255, .5);
  animation: pursuitImageScan 2.6s cubic-bezier(.45, 0, .55, 1) infinite;
}
/* center reticle */
.pursuit-page .live-panel .live-source-placeholder .live-source-cross:before,
.pursuit-page .live-panel .live-source-placeholder .live-source-cross:after {
  content: "";
  position: absolute;
  background: rgba(53, 226, 255, .3);
}
.pursuit-page .live-panel .live-source-placeholder .live-source-cross {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 26px;
  height: 26px;
  transform: translate(-50%, -50%);
  border: 1px solid rgba(53, 226, 255, .34);
  border-radius: 50%;
  animation: pursuitFrameGlow 2.8s ease-in-out infinite .4s;
}
.pursuit-page .live-panel .live-source-placeholder .live-source-cross:before {
  left: 50%;
  top: -14px;
  bottom: -14px;
  width: 1px;
  transform: translateX(-50%);
}
.pursuit-page .live-panel .live-source-placeholder .live-source-cross:after {
  top: 50%;
  left: -14px;
  right: -14px;
  height: 1px;
  transform: translateY(-50%);
}
.pursuit-page .live-panel .live-source-placeholder > em {
  position: relative;
  z-index: 2;
  font-style: normal;
  font-size: clamp(10px, .74vw, 13px);
  font-weight: 600;
  color: #7fa5ba;
  text-shadow: 0 0 8px rgba(53, 226, 255, .35);
  animation: pursuitEmptyBreath 3.2s ease-in-out infinite;
}
@keyframes pursuitImageScan {
  0% { transform: translateY(-40%); opacity: 0; }
  18% { opacity: 1; }
  82% { opacity: 1; }
  100% { transform: translateY(320%); opacity: 0; }
}
@keyframes pursuitFrameGlow {
  0%, 100% { opacity: .5; }
  50% { opacity: 1; }
}
@keyframes pursuitEmptyBreath {
  0%, 100% { opacity: .62; }
  50% { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .live-source-placeholder .live-source-frame,
  .pursuit-page .live-panel .live-source-placeholder .live-source-scan,
  .pursuit-page .live-panel .live-source-placeholder .live-source-cross,
  .pursuit-page .live-panel .live-source-placeholder > em { animation: none !important; }
}
</style>
<style scoped>
/* Last-word: the placeholder is the last child of .live-identity, so legacy
   `.live-identity > div:last-child` width rules (calc(100% - 10px) !important)
   outrank the base placeholder rule. Neutralize them at matching specificity. */
.pursuit-page .live-panel .live-identity > .live-source-placeholder {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  grid-template-columns: none !important;
  grid-auto-rows: auto !important;
  position: relative !important;
  left: auto !important;
  top: auto !important;
  width: clamp(78px, 7vw, 112px) !important;
  max-width: 26% !important;
  height: clamp(62px, 6.8vw, 104px) !important;
  flex: 0 0 auto !important;
  align-self: center !important;
  gap: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}
</style>
<style scoped>
/* Cyberpunk restyle for the live-event field labels and value chips. */
.pursuit-page .live-panel .live-grid label {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px 3px 8px;
  border: 1px solid rgba(53, 226, 255, .3);
  border-radius: 4px;
  background: linear-gradient(180deg, rgba(14, 52, 80, .72), rgba(6, 24, 40, .78));
  font-size: clamp(10px, .76vw, 13px) !important;
  font-weight: 600;
  letter-spacing: .6px;
  color: #b6e6f7;
  white-space: nowrap;
  clip-path: polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px));
  text-shadow: 0 0 8px rgba(53, 226, 255, .45);
}
.pursuit-page .live-panel .live-grid label:before {
  content: "";
  width: 3px;
  height: 3px;
  margin-right: 0;
  border-radius: 50%;
  background: #6ff3ff;
  box-shadow: 0 0 7px #35e2ff, 0 0 12px rgba(53, 226, 255, .8);
  animation: pursuitChipLed 2.4s ease-in-out infinite;
  flex: 0 0 auto;
}
@keyframes pursuitChipLed {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: .5; transform: scale(.72); }
}

/* Value chips: angular glass with a running light along the edge. */
.pursuit-page .live-panel .live-grid em.live-chip {
  position: relative;
  overflow: hidden;
  padding: 4px 12px !important;
  border-radius: 4px;
  clip-path: polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px));
  font-size: clamp(10px, .78vw, 13px) !important;
  font-weight: 700;
  letter-spacing: .5px;
}
.pursuit-page .live-panel .live-grid em.live-chip:before {
  content: "";
  position: absolute;
  top: 0;
  left: -40%;
  width: 40%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, .28), transparent);
  transform: skewX(-18deg);
  animation: pursuitChipRun 3.4s linear infinite;
  pointer-events: none;
}
@keyframes pursuitChipRun {
  0% { left: -40%; }
  55%, 100% { left: 120%; }
}
.pursuit-page .live-panel .live-grid em.live-chip > i {
  width: 5px;
  height: 5px;
  flex: 0 0 auto;
}
/* Empty chips: muted, dashed edge, slow breathing. */
.pursuit-page .live-panel .live-grid em.live-chip.is-empty {
  border-style: dashed !important;
  border-color: rgba(143, 164, 178, .42) !important;
  background: linear-gradient(180deg, rgba(30, 46, 60, .58), rgba(13, 24, 34, .62)) !important;
  color: #93a8b6 !important;
  animation: pursuitChipBreath 3.4s ease-in-out infinite;
}
.pursuit-page .live-panel .live-grid em.live-chip.is-empty:before {
  animation: none;
  opacity: 0;
}
.pursuit-page .live-panel .live-grid em.live-chip.is-empty > i {
  animation: pursuitChipLed 3.4s ease-in-out infinite !important;
}
@keyframes pursuitChipBreath {
  0%, 100% { border-color: rgba(143, 164, 178, .42); box-shadow: none; }
  50% { border-color: rgba(143, 164, 178, .68); box-shadow: 0 0 10px rgba(143, 164, 178, .16); }
}
/* Semantic states keep their hue but gain the angular glass treatment. */
.pursuit-page .live-panel .live-grid em.live-chip.tone-cyan {
  color: #6feaff;
  border-color: rgba(53, 226, 255, .5);
  background: linear-gradient(180deg, rgba(13, 74, 108, .78), rgba(5, 27, 44, .8));
  box-shadow: 0 0 12px rgba(53, 226, 255, .16), inset 0 0 10px rgba(53, 226, 255, .12);
}
.pursuit-page .live-panel .live-grid em.live-chip.tone-green {
  color: #5ff0c2;
  border-color: rgba(79, 230, 182, .48);
  background: linear-gradient(180deg, rgba(12, 72, 60, .76), rgba(4, 24, 22, .8));
  box-shadow: 0 0 12px rgba(79, 230, 182, .16), inset 0 0 10px rgba(79, 230, 182, .1);
}
.pursuit-page .live-panel .live-grid em.live-chip.tone-magenta {
  color: #ff85d8;
  border-color: rgba(255, 111, 208, .5);
  background: linear-gradient(180deg, rgba(86, 22, 78, .76), rgba(30, 7, 28, .8));
  box-shadow: 0 0 12px rgba(255, 111, 208, .18), inset 0 0 10px rgba(255, 111, 208, .12);
}
.pursuit-page .live-panel .live-grid em.live-chip.pursuit-detecting {
  color: #5ff0c2;
  border-color: rgba(79, 230, 182, .48);
  background: linear-gradient(180deg, rgba(12, 72, 60, .76), rgba(4, 24, 22, .8));
}
.pursuit-page .live-panel .live-grid em.live-chip.pursuit-tracking {
  color: #6feaff;
  border-color: rgba(53, 226, 255, .5);
  background: linear-gradient(180deg, rgba(13, 74, 108, .78), rgba(5, 27, 44, .8));
}
.pursuit-page .live-panel .live-grid em.live-chip.pursuit-lost {
  color: #ffb268;
  border-color: rgba(255, 171, 94, .48);
  background: linear-gradient(180deg, rgba(88, 50, 16, .76), rgba(30, 16, 5, .8));
}
.pursuit-page .live-panel .live-grid em.live-chip.mono {
  letter-spacing: 0;
}
.pursuit-page .live-panel .cancel-block label {
  padding: 3px 9px 3px 8px;
  border: 1px solid rgba(255, 122, 212, .4);
  border-radius: 4px;
  background: linear-gradient(180deg, rgba(84, 22, 74, .7), rgba(38, 8, 34, .76));
  clip-path: polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px));
  letter-spacing: .6px;
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .live-grid label:before,
  .pursuit-page .live-panel .live-grid em.live-chip:before,
  .pursuit-page .live-panel .live-grid em.live-chip.is-empty,
  .pursuit-page .live-panel .live-grid em.live-chip.is-empty > i { animation: none !important; }
}
</style>
<style scoped>
/* Last-word: force the angular chip/label shape over legacy pill rules. */
.pursuit-page .live-panel .live-grid > * > em.live-chip,
.pursuit-page .live-panel .live-identity-fields > .live-field > em.live-chip,
.pursuit-page .live-panel .live-facts > .live-field > em.live-chip {
  border-radius: 4px !important;
  clip-path: polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px)) !important;
  position: relative !important;
  overflow: hidden !important;
  padding: 4px 12px !important;
  letter-spacing: .5px;
}
.pursuit-page .live-panel .live-grid > * > em.live-chip:before,
.pursuit-page .live-panel .live-identity-fields > .live-field > em.live-chip:before,
.pursuit-page .live-panel .live-facts > .live-field > em.live-chip:before {
  content: "" !important;
  position: absolute !important;
  top: 0 !important;
  left: -40% !important;
  width: 40% !important;
  height: 100% !important;
  transform: skewX(-18deg) !important;
  animation: pursuitChipRun 3.4s linear infinite !important;
}
.pursuit-page .live-panel .live-grid > * > em.live-chip.is-empty:before {
  animation: none !important;
  opacity: 0 !important;
}
.pursuit-page .live-panel .live-grid label {
  border-radius: 4px !important;
  clip-path: polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 7px 100%, 0 calc(100% - 7px)) !important;
}
</style>

<style scoped>
/* Last-word: keep the run light off for empty chips (暂无数据 / 暂无状态 / 暂无工况快照). */
.pursuit-page .live-panel .live-grid em.live-chip.is-empty:before,
.pursuit-page .live-panel .live-grid > * > em.live-chip.is-empty:before,
.pursuit-page .live-panel .live-identity-fields > .live-field > em.live-chip.is-empty:before,
.pursuit-page .live-panel .live-facts > .live-field > em.live-chip.is-empty:before {
  animation: none !important;
  opacity: 0 !important;
  content: "" !important;
}
</style>
<style scoped>
/* Last-word: liquid-glass sweep on every empty chip (暂无数据 / 暂无状态 / 暂无工况快照). */
.pursuit-page .live-panel .live-grid em.live-chip.is-empty::after {
  content: "" !important;
  position: absolute !important;
  z-index: 0 !important;
  inset: -2px auto -2px -40% !important;
  width: 36% !important;
  transform: skewX(-24deg) !important;
  background: linear-gradient(90deg, transparent, rgba(214, 234, 255, .32), transparent) !important;
  filter: blur(2px) !important;
  animation: pursuitEmptyGlass 3.6s ease-in-out infinite !important;
  pointer-events: none !important;
}
.pursuit-page .live-panel .live-grid em.live-chip.is-empty > i,
.pursuit-page .live-panel .live-grid em.live-chip.is-empty {
  position: relative;
  z-index: 1;
}
@keyframes pursuitEmptyGlass {
  0% { left: -40%; }
  55%, 100% { left: 120%; }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .live-grid em.live-chip.is-empty::after { animation: none !important; }
}
</style>
<style scoped>
/* Last-word: uniform right-aligned labels + gray LED on empty condition chip. */
.pursuit-page .live-panel .live-grid label {
  flex: 0 0 auto !important;
  width: 100% !important;
  min-width: 0 !important;
  max-width: none !important;
  justify-content: flex-end !important;
  text-align: right !important;
}
.pursuit-page .live-panel .live-grid > * > label,
.pursuit-page .live-panel .live-identity-fields > .live-field > label,
.pursuit-page .live-panel .live-facts > .live-field > label,
.pursuit-page .live-panel .live-facts-side > .live-field > label {
  flex: 0 0 88px !important;
  width: 88px !important;
}
/* Empty condition chip: gray LED, no purple residue. */
.pursuit-page .live-panel .live-grid em.live-chip.condition-status.is-empty,
.pursuit-page .live-panel .live-grid em.live-chip.condition-status.condition-muted.is-empty {
  color: #93a8b6 !important;
  border-color: rgba(143, 164, 178, .42) !important;
  background: linear-gradient(180deg, rgba(30, 46, 60, .58), rgba(13, 24, 34, .62)) !important;
  box-shadow: none !important;
}
.pursuit-page .live-panel .live-grid em.live-chip.condition-status.is-empty > i,
.pursuit-page .live-panel .live-grid em.live-chip.condition-status.condition-muted.is-empty > i,
.pursuit-page .condition-status.condition-muted.is-empty i {
  background: #7d93a3 !important;
  box-shadow: 0 0 6px rgba(125, 147, 163, .7) !important;
  animation: pursuitChipLed 3.4s ease-in-out infinite !important;
}
.pursuit-page .live-panel .live-grid em.live-chip.condition-status.is-empty::after {
  background: linear-gradient(90deg, transparent, rgba(214, 234, 255, .32), transparent) !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-grid > * > label,
  .pursuit-page .live-panel .live-identity-fields > .live-field > label,
  .pursuit-page .live-panel .live-facts > .live-field > label,
  .pursuit-page .live-panel .live-facts-side > .live-field > label {
    flex: 0 0 78px !important;
    width: 78px !important;
  }
}
</style>
<style scoped>
/* Last-word: identity-column labels need one more class to outrank the
   `.live-identity > .live-identity-fields > ...` rules set earlier. */
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
  flex: 0 0 88px !important;
  width: 88px !important;
  min-width: 0 !important;
  max-width: none !important;
  justify-content: flex-end !important;
  text-align: right !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
    flex: 0 0 78px !important;
    width: 78px !important;
  }
}
</style>
<style scoped>
/* Last-word: unify box-sizing so every label box is exactly 88px wide. */
.pursuit-page .live-panel .live-grid label,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
  box-sizing: border-box !important;
  flex: 0 0 88px !important;
  width: 88px !important;
  justify-content: flex-end !important;
  text-align: right !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-grid label,
  .pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
    flex: 0 0 78px !important;
    width: 78px !important;
  }
}
</style>
<style scoped>
/* Last-word: label width sized to the longest title (来源检测时间 = 6 chars),
   with the round breathing LED restored on every column including the identity one. */
.pursuit-page .live-panel .live-grid label,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label,
.pursuit-page .live-panel .live-facts > .live-field > label,
.pursuit-page .live-panel .live-facts-side > .live-field > label,
.pursuit-page .live-panel .cancel-block > label {
  box-sizing: border-box !important;
  flex: 0 0 104px !important;
  width: 104px !important;
  min-width: 0 !important;
  max-width: none !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 6px !important;
  padding: 3px 9px 3px 8px !important;
  text-align: right !important;
  white-space: nowrap !important;
}
/* Round breathing LED on every title, overriding the old 2x11px bar. */
.pursuit-page .live-panel .live-grid label:before,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before,
.pursuit-page .live-panel .cancel-block > label:before {
  content: "" !important;
  display: inline-block !important;
  flex: 0 0 auto !important;
  width: 6px !important;
  height: 6px !important;
  margin-right: 0 !important;
  border-radius: 50% !important;
  background: #6ff3ff !important;
  box-shadow: 0 0 6px #35e2ff, 0 0 11px rgba(53, 226, 255, .8) !important;
  animation: pursuitChipLed 2.4s ease-in-out infinite !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-grid label,
  .pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label,
  .pursuit-page .live-panel .live-facts > .live-field > label,
  .pursuit-page .live-panel .live-facts-side > .live-field > label,
  .pursuit-page .live-panel .cancel-block > label {
    flex: 0 0 94px !important;
    width: 94px !important;
  }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .live-grid label:before { animation: none !important; }
}
</style>
<style scoped>
/* Last-word: the action column is a column flex container, so flex-basis there
   controls HEIGHT. Lock only the width for its label and keep the button visible. */
.pursuit-page .live-panel .cancel-block > label {
  flex: 0 0 auto !important;
  width: 104px !important;
  height: auto !important;
  align-self: flex-start !important;
  box-sizing: border-box !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 6px !important;
  padding: 3px 9px 3px 8px !important;
  text-align: right !important;
  white-space: nowrap !important;
}
.pursuit-page .live-panel .cancel-block > label:before {
  content: "" !important;
  display: inline-block !important;
  flex: 0 0 auto !important;
  width: 6px !important;
  height: 6px !important;
  border-radius: 50% !important;
  background: #ff9ade !important;
  box-shadow: 0 0 6px #ff5fc4, 0 0 11px rgba(255, 95, 196, .8) !important;
  animation: pursuitChipLed 2.4s ease-in-out infinite !important;
}
.pursuit-page .live-panel .cancel-block .cancel-button {
  flex: 0 0 auto !important;
  height: clamp(30px, 2.4vw, 38px) !important;
  width: 100% !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .cancel-block > label { width: 94px !important; }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .cancel-block > label:before { animation: none !important; }
}
</style>
<style scoped>
/* Last-word: pin every breathing LED to the left edge of its label box,
   so the dot no longer shifts with the title length. Text stays right-aligned. */
.pursuit-page .live-panel .live-grid label,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label,
.pursuit-page .live-panel .live-facts > .live-field > label,
.pursuit-page .live-panel .live-facts-side > .live-field > label,
.pursuit-page .live-panel .cancel-block > label {
  position: relative !important;
  justify-content: flex-end !important;
  gap: 0 !important;
}
.pursuit-page .live-panel .live-grid label:before,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before,
.pursuit-page .live-panel .live-facts > .live-field > label:before,
.pursuit-page .live-panel .live-facts-side > .live-field > label:before,
.pursuit-page .live-panel .cancel-block > label:before {
  position: absolute !important;
  left: 9px !important;
  right: auto !important;
  top: 50% !important;
  bottom: auto !important;
  margin: 0 !important;
  transform: translateY(-50%) !important;
  flex: 0 0 auto !important;
  width: 6px !important;
  height: 6px !important;
  border-radius: 50% !important;
  background: #6ff3ff !important;
  box-shadow: 0 0 6px #35e2ff, 0 0 11px rgba(53, 226, 255, .8) !important;
  animation: pursuitChipLed 2.4s ease-in-out infinite !important;
}
.pursuit-page .live-panel .cancel-block > label:before {
  background: #ff9ade !important;
  box-shadow: 0 0 6px #ff5fc4, 0 0 11px rgba(255, 95, 196, .8) !important;
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .live-grid label:before,
  .pursuit-page .live-panel .cancel-block > label:before { animation: none !important; }
}
</style>
<style scoped>
/* Last-word: replace the round breathing LED with an angular chevron marker.
   Same footprint (left: 9px), so the right-aligned titles stay untouched. */
.pursuit-page .live-panel .live-grid label:before,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before,
.pursuit-page .live-panel .live-facts > .live-field > label:before,
.pursuit-page .live-panel .live-facts-side > .live-field > label:before,
.pursuit-page .live-panel .cancel-block > label:before {
  position: absolute !important;
  left: 8px !important;
  right: auto !important;
  top: 50% !important;
  bottom: auto !important;
  margin: 0 !important;
  transform: translateY(-50%) !important;
  width: 8px !important;
  height: 11px !important;
  border-radius: 0 !important;
  background: linear-gradient(180deg, #9dfbff, #21b8e6) !important;
  clip-path: polygon(0 0, 42% 0, 100% 50%, 42% 100%, 0 100%, 58% 50%) !important;
  box-shadow: none !important;
  animation: pursuitLabelMark 2.8s ease-in-out infinite !important;
}
.pursuit-page .live-panel .cancel-block > label:before {
  background: linear-gradient(180deg, #ffc2ec, #ff5fc4) !important;
}
@keyframes pursuitLabelMark {
  0%, 100% { opacity: .55; transform: translateY(-50%) translateX(0); }
  50% { opacity: 1; transform: translateY(-50%) translateX(2px); }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .live-grid label:before,
  .pursuit-page .live-panel .cancel-block > label:before { animation: none !important; }
}
</style>
<style scoped>
/* Last-word: replace the chevron with a 3-segment level tick mark. */
.pursuit-page .live-panel .live-grid label:before,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before,
.pursuit-page .live-panel .live-facts > .live-field > label:before,
.pursuit-page .live-panel .live-facts-side > .live-field > label:before,
.pursuit-page .live-panel .cancel-block > label:before {
  position: absolute !important;
  left: 9px !important;
  right: auto !important;
  top: 50% !important;
  bottom: auto !important;
  margin: 0 !important;
  transform: translateY(-50%) !important;
  width: 7px !important;
  height: 12px !important;
  border-radius: 0 !important;
  clip-path: none !important;
  background: repeating-linear-gradient(180deg,
    #9dfbff 0px, #9dfbff 3px,
    transparent 3px, transparent 5px) !important;
  box-shadow: none !important;
  animation: pursuitLabelTicks 2.6s ease-in-out infinite !important;
}
.pursuit-page .live-panel .cancel-block > label:before {
  background: repeating-linear-gradient(180deg,
    #ffc2ec 0px, #ffc2ec 3px,
    transparent 3px, transparent 5px) !important;
}
@keyframes pursuitLabelTicks {
  0%, 100% { opacity: .5; }
  50% { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .live-grid label:before,
  .pursuit-page .live-panel .cancel-block > label:before { animation: none !important; }
}
</style>
<style scoped>
/* Last-word: replace the 3-segment ticks with a flush left accent bar
   that reads as part of the label frame instead of a floating icon. */
.pursuit-page .live-panel .live-grid label:before,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before,
.pursuit-page .live-panel .live-facts > .live-field > label:before,
.pursuit-page .live-panel .live-facts-side > .live-field > label:before,
.pursuit-page .live-panel .cancel-block > label:before {
  position: absolute !important;
  left: 0 !important;
  right: auto !important;
  top: 4px !important;
  bottom: 4px !important;
  margin: 0 !important;
  width: 2.5px !important;
  height: auto !important;
  border-radius: 0 2px 2px 0 !important;
  clip-path: none !important;
  background: linear-gradient(180deg, #9dfbff, #21b8e6) !important;
  box-shadow: 0 0 7px rgba(53, 226, 255, .85) !important;
  animation: pursuitLabelBar 3s ease-in-out infinite !important;
}
.pursuit-page .live-panel .cancel-block > label:before {
  background: linear-gradient(180deg, #ffc2ec, #ff5fc4) !important;
  box-shadow: 0 0 7px rgba(255, 95, 196, .85) !important;
}
@keyframes pursuitLabelBar {
  0%, 100% { opacity: .55; }
  50% { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .live-grid label:before,
  .pursuit-page .live-panel .cancel-block > label:before { animation: none !important; }
}
</style>
<style scoped>
/* Last-word: back to the 3-segment tick mark, vertically centered in the label box. */
.pursuit-page .live-panel .live-grid label:before,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before,
.pursuit-page .live-panel .live-facts > .live-field > label:before,
.pursuit-page .live-panel .live-facts-side > .live-field > label:before,
.pursuit-page .live-panel .cancel-block > label:before {
  position: absolute !important;
  left: 9px !important;
  right: auto !important;
  top: 50% !important;
  bottom: auto !important;
  margin: 0 !important;
  transform: translateY(-50%) !important;
  width: 7px !important;
  height: 12px !important;
  border-radius: 0 !important;
  clip-path: none !important;
  background: repeating-linear-gradient(180deg,
    #9dfbff 0px, #9dfbff 3px,
    transparent 3px, transparent 5px) !important;
  box-shadow: none !important;
  animation: pursuitLabelTicks 2.6s ease-in-out infinite !important;
}
.pursuit-page .live-panel .cancel-block > label:before {
  background: repeating-linear-gradient(180deg,
    #ffc2ec 0px, #ffc2ec 3px,
    transparent 3px, transparent 5px) !important;
}
@keyframes pursuitLabelTicks {
  0%, 100% { opacity: .5; }
  50% { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .live-grid label:before,
  .pursuit-page .live-panel .cancel-block > label:before { animation: none !important; }
}
</style>
<style scoped>
/* Last-word: replace the 3-segment ticks with a single vertical bar. */
.pursuit-page .live-panel .live-grid label:before,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before,
.pursuit-page .live-panel .live-facts > .live-field > label:before,
.pursuit-page .live-panel .live-facts-side > .live-field > label:before,
.pursuit-page .live-panel .cancel-block > label:before {
  position: absolute !important;
  left: 9px !important;
  right: auto !important;
  top: 50% !important;
  bottom: auto !important;
  margin: 0 !important;
  transform: translateY(-50%) !important;
  width: 2px !important;
  height: 12px !important;
  border-radius: 1px !important;
  clip-path: none !important;
  background: #6ff3ff !important;
  box-shadow: 0 0 6px rgba(53, 226, 255, .85) !important;
  animation: pursuitLabelTicks 2.6s ease-in-out infinite !important;
}
.pursuit-page .live-panel .cancel-block > label:before {
  background: #ff9ade !important;
  box-shadow: 0 0 6px rgba(255, 95, 196, .85) !important;
}
@keyframes pursuitLabelTicks {
  0%, 100% { opacity: .5; }
  50% { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .live-grid label:before,
  .pursuit-page .live-panel .cancel-block > label:before { animation: none !important; }
}
</style>
<style scoped>
/* Last-word: the action column is narrower than a fixed 102px label, so the
   title drifted left of the button. Let it span the column and sit inline. */
.pursuit-page .live-panel .cancel-block > label {
  box-sizing: border-box !important;
  flex: 0 0 auto !important;
  width: 100% !important;
  max-width: none !important;
  height: auto !important;
  align-self: stretch !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  gap: 7px !important;
  padding: 3px 9px 3px 8px !important;
  text-align: left !important;
  white-space: nowrap !important;
}
.pursuit-page .live-panel .cancel-block > label:before {
  position: static !important;
  left: auto !important;
  top: auto !important;
  margin: 0 !important;
  transform: none !important;
  flex: 0 0 auto !important;
  width: 2px !important;
  height: 12px !important;
  border-radius: 1px !important;
  background: #ff9ade !important;
  box-shadow: 0 0 6px rgba(255, 95, 196, .85) !important;
  animation: pursuitLabelTicks 2.6s ease-in-out infinite !important;
}
</style>

<style scoped>
/* Last-word: rebuilt radar - dial ticks, concentric rings, sweep beam, target blips. */
.pursuit-page .live-panel .live-identity > .radar-mark {
  position: relative !important;
  flex: 0 0 auto !important;
  width: clamp(56px, 5vw, 74px) !important;
  height: clamp(56px, 5vw, 74px) !important;
  border-radius: 50% !important;
  overflow: hidden !important;
  border: 1px solid rgba(53, 226, 255, .4) !important;
  background:
    radial-gradient(circle, rgba(23, 118, 168, .3), rgba(3, 14, 26, .55) 72%),
    repeating-conic-gradient(rgba(53, 226, 255, .5) 0deg 1.1deg, transparent 1.1deg 9deg) !important;
  box-shadow: 0 0 16px rgba(31, 200, 255, .3), inset 0 0 18px rgba(31, 200, 255, .16) !important;
}
/* concentric rings */
.pursuit-page .live-panel .live-identity > .radar-mark .radar-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: repeating-radial-gradient(circle,
    transparent 0 7px, rgba(53, 226, 255, .17) 7px 8px);
  -webkit-mask: radial-gradient(circle, #000 0 74%, transparent 76%);
  mask: radial-gradient(circle, #000 0 74%, transparent 76%);
}
/* rotating sweep with a fading tail */
.pursuit-page .live-panel .live-identity > .radar-mark .radar-sweep {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(from 0deg,
    rgba(120, 245, 255, .8) 0deg,
    rgba(53, 226, 255, .34) 20deg,
    rgba(53, 226, 255, .12) 48deg,
    transparent 82deg,
    transparent 360deg);
  animation: pursuitRadarSweep 4s linear infinite;
}
/* target blips */
.pursuit-page .live-panel .live-identity > .radar-mark .radar-blip {
  position: absolute;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #7dffcf;
  box-shadow: 0 0 7px rgba(125, 255, 207, .95);
}
.pursuit-page .live-panel .live-identity > .radar-mark .blip-a {
  top: 27%;
  left: 62%;
  animation: pursuitRadarBlip 2.2s ease-in-out infinite;
}
.pursuit-page .live-panel .live-identity > .radar-mark .blip-b {
  top: 66%;
  left: 31%;
  background: #ffd166;
  box-shadow: 0 0 7px rgba(255, 209, 102, .95);
  animation: pursuitRadarBlip 3.1s ease-in-out infinite .5s;
}
/* centre core */
.pursuit-page .live-panel .live-identity > .radar-mark span {
  position: absolute !important;
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
  width: 7px !important;
  height: 7px !important;
  border-radius: 50% !important;
  background: #c8fbff !important;
  box-shadow: 0 0 10px rgba(120, 240, 255, .95), 0 0 20px rgba(53, 226, 255, .6) !important;
  animation: pursuitRadarCore 2.4s ease-in-out infinite !important;
}
.pursuit-page .live-panel .live-identity > .radar-mark:before,
.pursuit-page .live-panel .live-identity > .radar-mark:after {
  display: none !important;
}
@keyframes pursuitRadarSweep {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes pursuitRadarCore {
  0%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: .6; transform: translate(-50%, -50%) scale(.78); }
}
@keyframes pursuitRadarBlip {
  0%, 100% { opacity: .35; transform: scale(.8); }
  50% { opacity: 1; transform: scale(1.25); }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .live-panel .live-identity > .radar-mark .radar-sweep,
  .pursuit-page .live-panel .live-identity > .radar-mark .radar-ring,
  .pursuit-page .live-panel .live-identity > .radar-mark span,
  .pursuit-page .live-panel .live-identity > .radar-mark .radar-blip { animation: none !important; }
}
</style>

<style scoped>
/* Last-word: cyberpunk signal-loss animation for every "暂无数据" empty state. */
.pursuit-page .list-empty-state,
.pursuit-page .right-empty-state {
  display: flex !important;
  flex-direction: column !important;
  align-items: center;
  justify-content: center;
  gap: 16px;
}
.pursuit-page .empty-signal {
  position: relative;
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
}
/* expanding arcs */
.pursuit-page .empty-arc {
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  border: 1px solid rgba(53, 226, 255, .55);
  transform: translate(-50%, -50%) scale(.35);
  opacity: 0;
  animation: emptyArcOut 3s cubic-bezier(.22, .61, .36, 1) infinite;
}
.pursuit-page .empty-arc.arc-2 { animation-delay: 1s; }
.pursuit-page .empty-arc.arc-3 { animation-delay: 2s; }
@keyframes emptyArcOut {
  0% { transform: translate(-50%, -50%) scale(.3); opacity: 0; }
  18% { opacity: .85; }
  100% { transform: translate(-50%, -50%) scale(1); opacity: 0; }
}
/* dashed outer ring, slow rotation */
.pursuit-page .empty-signal::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px dashed rgba(53, 226, 255, .28);
  animation: emptyRingSpin 14s linear infinite;
}
@keyframes emptyRingSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
/* scanning sweep */
.pursuit-page .empty-signal::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(from 0deg,
    rgba(53, 226, 255, .34) 0deg,
    rgba(53, 226, 255, .08) 26deg,
    transparent 60deg,
    transparent 360deg);
  -webkit-mask: radial-gradient(circle, transparent 0 40%, #000 44% 50%, transparent 54%);
  mask: radial-gradient(circle, transparent 0 40%, #000 44% 50%, transparent 54%);
  animation: emptyRingSpin 4.5s linear infinite;
}
/* core dot */
.pursuit-page .empty-core {
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #7df0ff;
  box-shadow: 0 0 10px rgba(90, 235, 255, .95), 0 0 22px rgba(53, 226, 255, .55);
  animation: emptyCorePulse 2.2s ease-in-out infinite;
}
@keyframes emptyCorePulse {
  0%, 100% { opacity: .45; transform: scale(.8); }
  50% { opacity: 1; transform: scale(1.15); }
}
.pursuit-page .empty-text {
  color: #6f95a3;
  font-size: 13px;
  letter-spacing: 1.4px;
  animation: emptyTextBreath 3.4s ease-in-out infinite;
}
@keyframes emptyTextBreath {
  0%, 100% { opacity: .6; }
  50% { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .pursuit-page .empty-arc,
  .pursuit-page .empty-signal::before,
  .pursuit-page .empty-signal::after,
  .pursuit-page .empty-core,
  .pursuit-page .empty-text { animation: none !important; }
}
</style>
<style scoped>
/* Last-word A: fit real values (no clipping) and rebalance columns. */
.pursuit-page .live-panel .live-grid {
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1.02fr) minmax(0, 1.18fr) minmax(0, .55fr) !important;
}
.pursuit-page .live-panel .live-grid label,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label,
.pursuit-page .live-panel .live-facts > .live-field > label,
.pursuit-page .live-panel .live-facts-side > .live-field > label {
  flex: 0 0 94px !important;
  width: 94px !important;
}
.pursuit-page .live-panel .live-grid em.live-chip {
  padding: 4px 9px !important;
  letter-spacing: .2px;
}
.pursuit-page .live-panel .live-grid em.live-chip.mono {
  font-size: clamp(10px, .72vw, 12px) !important;
  letter-spacing: 0 !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-grid {
    grid-template-columns: minmax(0, 1.32fr) minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, .56fr) !important;
  }
  .pursuit-page .live-panel .live-grid label,
  .pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label,
  .pursuit-page .live-panel .live-facts > .live-field > label,
  .pursuit-page .live-panel .live-facts-side > .live-field > label {
    flex: 0 0 86px !important;
    width: 86px !important;
  }
}
</style>

<style scoped>
/* Last-word B: harmonious palette - one hue family, unified lightness, softer glow. */
.pursuit-page .live-panel .live-grid em.live-chip.tone-cyan,
.pursuit-page .live-panel .live-grid em.live-chip.pursuit-tracking {
  color: #6ccfd8 !important;
  border-color: rgba(108, 207, 216, .42) !important;
  background: linear-gradient(180deg, rgba(28, 74, 88, .7), rgba(12, 34, 44, .76)) !important;
  box-shadow: inset 0 0 10px rgba(108, 207, 216, .1) !important;
}
.pursuit-page .live-panel .live-grid em.live-chip.tone-green,
.pursuit-page .live-panel .live-grid em.live-chip.pursuit-detecting {
  color: #4ec9a4 !important;
  border-color: rgba(78, 201, 164, .42) !important;
  background: linear-gradient(180deg, rgba(22, 70, 60, .7), rgba(10, 30, 27, .76)) !important;
  box-shadow: inset 0 0 10px rgba(78, 201, 164, .1) !important;
}
.pursuit-page .live-panel .live-grid em.live-chip.tone-magenta {
  color: #e07ab8 !important;
  border-color: rgba(224, 122, 184, .42) !important;
  background: linear-gradient(180deg, rgba(74, 26, 60, .7), rgba(32, 10, 26, .76)) !important;
  box-shadow: inset 0 0 10px rgba(224, 122, 184, .1) !important;
}
.pursuit-page .live-panel .live-grid em.live-chip.pursuit-lost {
  color: #d4a468 !important;
  border-color: rgba(212, 164, 104, .42) !important;
  background: linear-gradient(180deg, rgba(76, 54, 24, .7), rgba(32, 22, 10, .76)) !important;
  box-shadow: inset 0 0 10px rgba(212, 164, 104, .1) !important;
}
.pursuit-page .live-panel .live-grid em.live-chip.condition-green,
.pursuit-page .live-panel .live-grid em.live-chip.condition-cyan {
  color: #4ec9a4 !important;
  border-color: rgba(78, 201, 164, .42) !important;
  background: linear-gradient(180deg, rgba(22, 70, 60, .7), rgba(10, 30, 27, .76)) !important;
  box-shadow: inset 0 0 10px rgba(78, 201, 164, .1) !important;
}
.pursuit-page .live-panel .live-grid em.live-chip.condition-amber {
  color: #d4a468 !important;
  border-color: rgba(212, 164, 104, .42) !important;
  background: linear-gradient(180deg, rgba(76, 54, 24, .7), rgba(32, 22, 10, .76)) !important;
  box-shadow: inset 0 0 10px rgba(212, 164, 104, .1) !important;
}
.pursuit-page .live-panel .live-grid em.live-chip.is-empty {
  color: #8aa0ad !important;
  border-color: rgba(138, 160, 173, .34) !important;
  background: linear-gradient(180deg, rgba(32, 46, 58, .58), rgba(14, 24, 33, .64)) !important;
  box-shadow: none !important;
}
.pursuit-page .live-panel .live-grid em.live-chip > i {
  box-shadow: 0 0 5px currentColor !important;
}
.pursuit-page .live-panel .cancel-block .cancel-button {
  background: linear-gradient(180deg, rgba(178, 52, 132, .92), rgba(116, 22, 88, .92)) !important;
  border-color: rgba(224, 122, 184, .6) !important;
  box-shadow: 0 0 12px rgba(200, 60, 155, .28) !important;
}
</style>
<style scoped>
/* Last-word C: column 1 needs more room for its values - trim label and image. */
.pursuit-page .live-panel .live-identity {
  gap: 10px !important;
  padding-right: 12px !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
  flex: 0 0 76px !important;
  width: 76px !important;
  padding: 3px 6px 3px 8px !important;
  font-size: clamp(10px, .72vw, 12.5px) !important;
  letter-spacing: 0 !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before {
  left: 6px !important;
}
.pursuit-page .live-panel .live-source-image {
  max-width: 26% !important;
  height: clamp(54px, 5.8vw, 92px) !important;
}
.pursuit-page .live-panel .live-identity > .radar-mark {
  width: clamp(46px, 4vw, 60px) !important;
  height: clamp(46px, 4vw, 60px) !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field {
  gap: 6px !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-source-image { max-width: 22% !important; }
}
</style>
<style scoped>
/* Last-word: the label's bottom-left notch was clipping the vertical bar.
   Keep only the top-right notch so the bar renders at full height. */
.pursuit-page .live-panel .live-grid label,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label,
.pursuit-page .live-panel .live-facts > .live-field > label,
.pursuit-page .live-panel .live-facts-side > .live-field > label {
  clip-path: polygon(0 0, calc(100% - 7px) 0, 100% 7px, 100% 100%, 0 100%) !important;
}
.pursuit-page .live-panel .live-grid label:before,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before,
.pursuit-page .live-panel .live-facts > .live-field > label:before,
.pursuit-page .live-panel .live-facts-side > .live-field > label:before {
  top: 50% !important;
  bottom: auto !important;
  transform: translateY(-50%) !important;
  height: 12px !important;
}
</style>
<style scoped>
/* Last-word D: taller accent bar + wider, uniform title boxes. */
.pursuit-page .live-panel .live-grid label,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label,
.pursuit-page .live-panel .live-facts > .live-field > label,
.pursuit-page .live-panel .live-facts-side > .live-field > label {
  flex: 0 0 92px !important;
  width: 92px !important;
  padding: 3px 7px 3px 8px !important;
}
.pursuit-page .live-panel .live-grid label:before,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before,
.pursuit-page .live-panel .live-facts > .live-field > label:before,
.pursuit-page .live-panel .live-facts-side > .live-field > label:before {
  left: 7px !important;
  width: 2.5px !important;
  height: 15px !important;
  border-radius: 2px !important;
  background: linear-gradient(180deg, #9dfbff, #21b8e6) !important;
  box-shadow: 0 0 6px rgba(53, 226, 255, .8) !important;
}
/* column 1: reclaim room for the wider title */
.pursuit-page .live-panel .live-source-image { max-width: 22% !important; }
.pursuit-page .live-panel .live-identity { gap: 9px !important; }
.pursuit-page .live-panel .live-identity > .radar-mark {
  width: clamp(44px, 3.8vw, 56px) !important;
  height: clamp(44px, 3.8vw, 56px) !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-grid label,
  .pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label,
  .pursuit-page .live-panel .live-facts > .live-field > label,
  .pursuit-page .live-panel .live-facts-side > .live-field > label {
    flex: 0 0 84px !important;
    width: 84px !important;
  }
}
</style>
<style scoped>
/* Last-word E: the label frame clips a 7px corner at bottom-left, which cut the
   accent bar. Move the bar clear of that notch so it renders in full. */
.pursuit-page .live-panel .live-grid label,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label,
.pursuit-page .live-panel .live-facts > .live-field > label,
.pursuit-page .live-panel .live-facts-side > .live-field > label {
  padding: 3px 7px 3px 18px !important;
}
.pursuit-page .live-panel .live-grid label:before,
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before,
.pursuit-page .live-panel .live-facts > .live-field > label:before,
.pursuit-page .live-panel .live-facts-side > .live-field > label:before {
  left: 11px !important;
  width: 2.5px !important;
  height: 15px !important;
  border-radius: 2px !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-grid label,
  .pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label,
  .pursuit-page .live-panel .live-facts > .live-field > label,
  .pursuit-page .live-panel .live-facts-side > .live-field > label {
    padding-left: 16px !important;
  }
  .pursuit-page .live-panel .live-grid label:before,
  .pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before,
  .pursuit-page .live-panel .live-facts > .live-field > label:before,
  .pursuit-page .live-panel .live-facts-side > .live-field > label:before {
    left: 9px !important;
  }
}
</style>
<style scoped>
/* Last-word F: size title boxes so the longest title clears the accent bar.
   Column 1 holds only 4-char titles, so it stays narrower. */
.pursuit-page .live-panel .live-facts > .live-field > label,
.pursuit-page .live-panel .live-facts-side > .live-field > label {
  flex: 0 0 102px !important;
  width: 102px !important;
  padding: 3px 7px 3px 19px !important;
  font-size: clamp(10px, .74vw, 13px) !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
  flex: 0 0 84px !important;
  width: 84px !important;
  padding: 3px 7px 3px 19px !important;
  font-size: clamp(10px, .72vw, 12.5px) !important;
}
.pursuit-page .live-panel .live-grid {
  grid-template-columns: minmax(0, 1.42fr) minmax(0, 1.1fr) minmax(0, 1.16fr) minmax(0, .55fr) !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-facts > .live-field > label,
  .pursuit-page .live-panel .live-facts-side > .live-field > label {
    flex: 0 0 94px !important;
    width: 94px !important;
  }
  .pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
    flex: 0 0 78px !important;
    width: 78px !important;
  }
}
</style>
<style scoped>
/* Last-word G: let the source image fill the free space in column 1. */
.pursuit-page .live-panel .live-source-image {
  flex: 1 1 auto !important;
  width: auto !important;
  max-width: none !important;
  min-width: 0 !important;
  height: 100% !important;
  max-height: 100% !important;
  object-fit: cover !important;
  border-radius: 6px;
}
.pursuit-page .live-panel .live-identity {
  align-items: stretch !important;
}
</style>
<style scoped>
/* Last-word H: fields keep their natural width; the image absorbs the remainder. */
.pursuit-page .live-panel .live-identity > .live-identity-fields {
  flex: 0 0 auto !important;
  width: auto !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
  flex: 0 0 82px !important;
  width: 82px !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field {
  flex: 0 0 auto !important;
  width: auto !important;
}
.pursuit-page .live-panel .live-source-image {
  flex: 1 1 auto !important;
  min-width: 0 !important;
}
/deep/ .ivu-input-wrapper-small .ivu-input-prefix i, /deep/ .ivu-input-wrapper-small .ivu-input-suffix i {
    font-size: 14px;
    line-height: 32px;
}
</style>
<style scoped>
/* Last-word I: time fields get their own amber tone, distinct from status chips. */
.pursuit-page .live-panel .live-grid em.live-chip.tone-time {
  color: #e0a86a !important;
  border-color: rgba(224, 168, 106, .44) !important;
  background: linear-gradient(180deg, rgba(78, 56, 26, .72), rgba(32, 23, 11, .78)) !important;
  box-shadow: inset 0 0 10px rgba(224, 168, 106, .1) !important;
}
</style>
<style scoped>
/* Last-word J: keep the source image inside the panel when the viewport is short.
   height:100% + stretch let the image grow past the panel edge; cap it and
   let the flex item shrink instead. */
.pursuit-page .live-panel .live-identity {
  align-items: center !important;
  min-height: 0 !important;
  overflow: hidden !important;
}
.pursuit-page .live-panel .live-source-image {
  flex: 1 1 auto !important;
  align-self: center !important;
  height: 100% !important;
  max-height: clamp(52px, 8.4vh, 104px) !important;
  min-height: 0 !important;
  width: auto !important;
  max-width: none !important;
  object-fit: cover !important;
  overflow: hidden !important;
}
.pursuit-page .live-panel .live-grid {
  min-height: 0 !important;
}
</style>
<style scoped>
/* Last-word K: keep the source image at its native aspect ratio.
   flex-grow was stretching it into a wide letterbox strip. */
.pursuit-page .live-panel .live-identity > .live-source-image {
  flex: 0 1 auto !important;
  align-self: center !important;
  height: 100% !important;
  max-height: clamp(52px, 8.4vh, 104px) !important;
  width: auto !important;
  max-width: 100% !important;
  object-fit: contain !important;
}
</style>
<style scoped>
/* Last-word L: scale the radar with the viewport (width and height), capped so it
   never exceeds the panel content height. */
.pursuit-page .live-panel .live-identity > .radar-mark {
  width: clamp(46px, min(4.8vw, 9.5vh), 82px) !important;
  height: clamp(46px, min(4.8vw, 9.5vh), 82px) !important;
}
.pursuit-page .live-panel .live-source-image {
  max-height: clamp(58px, min(10.5vh, 9vw), 128px) !important;
}
@media (min-width: 1920px) {
  .pursuit-page .live-panel .live-identity > .radar-mark {
    width: clamp(64px, min(4.4vw, 9vh), 96px) !important;
    height: clamp(64px, min(4.4vw, 9vh), 96px) !important;
  }
}
</style>
<style scoped>
/* Last-word M: pin the image to the right edge; the identity fields absorb the
   remaining width and stay left-aligned. */
.pursuit-page .live-panel .live-identity {
  justify-content: flex-end !important;
  align-items: center !important;
}
.pursuit-page .live-panel .live-identity > .radar-mark {
  margin-right: auto !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields {
  flex: 1 1 auto !important;
  width: auto !important;
  max-width: none !important;
  min-width: 0 !important;
}
.pursuit-page .live-panel .live-identity > .live-source-image,
.pursuit-page .live-panel .live-identity > .live-source-placeholder {
  flex: 0 0 auto !important;
  align-self: center !important;
  margin-left: auto !important;
  height: 100% !important;
  max-height: clamp(58px, min(10.5vh, 9vw), 128px) !important;
  width: auto !important;
  max-width: none !important;
  min-width: 0 !important;
}
</style>
<style scoped>
/* Last-word N: fields size to their content; the image yields width instead of
   squeezing the value chips. */
.pursuit-page .live-panel .live-identity > .live-identity-fields {
  flex: 0 1 auto !important;
  width: auto !important;
  min-width: 0 !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field {
  flex: 0 0 auto !important;
  width: auto !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > em.live-chip {
  flex: 0 0 auto !important;
}
.pursuit-page .live-panel .live-source-image,
.pursuit-page .live-panel .live-identity > .live-source-placeholder {
  max-height: clamp(50px, min(8vh, 7vw), 96px) !important;
}
</style>
<style scoped>
/* Last-word O: constrain the image by BOTH axes so it can never grow into the
   fields. Width is capped as a share of the column; contain letterboxes the
   remainder instead of stretching or overflowing. */
.pursuit-page .live-panel .live-identity {
  overflow: hidden !important;
}
.pursuit-page .live-panel .live-identity > .live-source-image,
.pursuit-page .live-panel .live-identity > .live-source-placeholder {
  flex: 0 1 auto !important;
  align-self: center !important;
  margin-left: auto !important;
  min-width: 0 !important;
  width: auto !important;
  max-width: 30% !important;
  height: 100% !important;
  max-height: clamp(46px, min(8vh, 7vw), 96px) !important;
  object-fit: contain !important;
}
@media (min-width: 1920px) {
  .pursuit-page .live-panel .live-identity > .live-source-image,
  .pursuit-page .live-panel .live-identity > .live-source-placeholder {
    max-width: 32% !important;
  }
}
</style>
<style scoped>
/* Last-word P: guarantee the image is flush right at any width.
   Right-alignment comes from the container's justify-content, not from an
   auto margin on the image, so it holds even when max-width kicks in. */
.pursuit-page .live-panel .live-identity {
  justify-content: flex-end !important;
  align-items: center !important;
  overflow: hidden !important;
}
.pursuit-page .live-panel .live-identity > .radar-mark {
  margin-right: auto !important;
  margin-left: 0 !important;
  flex: 0 0 auto !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields {
  flex: 0 1 auto !important;
  width: auto !important;
  min-width: 0 !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}
.pursuit-page .live-panel .live-identity > .live-source-image,
.pursuit-page .live-panel .live-identity > .live-source-placeholder {
  flex: 0 1 auto !important;
  align-self: center !important;
  margin: 0 0 0 auto !important;
  min-width: 0 !important;
  width: auto !important;
  max-width: 32% !important;
  height: 100% !important;
  max-height: clamp(46px, min(8vh, 7vw), 104px) !important;
  object-fit: contain !important;
}
@media (min-width: 1920px) {
  .pursuit-page .live-panel .live-identity > .live-source-image,
  .pursuit-page .live-panel .live-identity > .live-source-placeholder {
    max-width: 34% !important;
  }
}
</style>
<style scoped>
/* Last-word Q: the radar is height-driven - fill the row height, derive a square
   width from it, and never let flex squeeze it into an ellipse. */
.pursuit-page .live-panel .live-identity > .radar-mark {
  flex: 0 0 auto !important;
  align-self: center !important;
  height: 100% !important;
  width: auto !important;
  aspect-ratio: 1 / 1 !important;
  min-width: 0 !important;
  max-width: 22% !important;
  max-height: clamp(46px, min(11vh, 8vw), 104px) !important;
}
@media (min-width: 1920px) {
  .pursuit-page .live-panel .live-identity > .radar-mark {
    max-height: clamp(60px, min(11vh, 8vw), 120px) !important;
    max-width: 24% !important;
  }
}
</style>
<style scoped>
/* Last-word R: height is the single source of truth for the radar.
   Drop the vh cap and the percentage width cap so the width is derived
   purely from aspect-ratio, giving a true circle at any panel height. */
.pursuit-page .live-panel .live-identity > .radar-mark {
  flex: 0 0 auto !important;
  align-self: stretch !important;
  height: auto !important;
  max-height: none !important;
  width: auto !important;
  min-width: 0 !important;
  max-width: none !important;
  aspect-ratio: 1 / 1 !important;
}
</style>
<style scoped>
/* Last-word S: enlarge the history list text; widen the time column to match. */
.pursuit-page .history-head {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr) minmax(0, 1.05fr) minmax(0, 1.6fr) 16px !important;
}
.pursuit-page .history-row {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr) minmax(0, 1.05fr) minmax(0, 1.6fr) 16px !important;
}
.pursuit-page .history-head span {
  font-size: clamp(11px, .82vw, 14px) !important;
  line-height: 1.4 !important;
  font-weight: 700 !important;
}
.pursuit-page .history-row > span {
  font-size: clamp(11px, .84vw, 14px) !important;
  line-height: 1.4 !important;
}
.pursuit-page .history-row > em {
  font-size: clamp(11px, .82vw, 14px) !important;
  line-height: 1.4 !important;
  padding: 4px 8px !important;
}
.pursuit-page .history-row > span.mono {
  font-size: clamp(10px, .78vw, 13px) !important;
  letter-spacing: 0 !important;
}
.pursuit-page .history-row {
  min-height: 40px !important;
  padding: 6px 8px !important;
}
</style>
<style scoped>
/* Last-word T: rebalance columns so the enlarged status chips fit. */
.pursuit-page .history-head {
  grid-template-columns: minmax(0, .92fr) minmax(0, 1.12fr) minmax(0, 1.12fr) minmax(0, 1.5fr) 16px !important;
}
.pursuit-page .history-row {
  grid-template-columns: minmax(0, .92fr) minmax(0, 1.12fr) minmax(0, 1.12fr) minmax(0, 1.5fr) 16px !important;
}
.pursuit-page .history-row > em {
  padding: 4px 6px !important;
}
</style>
<style scoped>
.pursuit-page .live-panel .live-grid .live-field > label:before {
  left: 5px !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label:before {
  left: 5px !important;
}
</style>
<style scoped>
/* Keep narrow history lists readable: compact enum columns and clip long identifiers. */
.pursuit-page > .workspace {
  grid-template-columns: minmax(0, 42%) minmax(0, 58%) !important;
}
.pursuit-page .history-head {
  grid-template-columns: minmax(0, 1.1fr) 136px 80px minmax(0, 1.3fr) !important;
  gap: 4px !important;
}
.pursuit-page .history-row {
  grid-template-columns: minmax(0, 1.1fr) 136px 80px minmax(0, 1.3fr) 12px !important;
  gap: 4px !important;
}
.pursuit-page .history-row > span,
.pursuit-page .history-row > em,
.pursuit-page .history-head > span {
  min-width: 0 !important;
}
.pursuit-page .history-row > span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pursuit-page .history-row > em {
  box-sizing: border-box;
  padding: 4px 6px !important;
  font-size: clamp(10px, 0.74vw, 12px) !important;
}
/* Long tracking-state enums must remain readable inside the compact column. */
.pursuit-page .history-row > em:nth-of-type(2) {
  width: 100% !important;
  min-width: 0 !important;
  min-height: 26px;
  height: auto !important;
  justify-content: center !important;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  overflow-wrap: anywhere !important;
  word-break: break-word !important;
  line-height: 1.25 !important;
}
</style>
<style scoped>
/* Give the long tracking label room by compacting its neighboring columns. */
.pursuit-page .history-head {
  grid-template-columns: minmax(0, 1fr) 104px minmax(108px, 1.15fr) 96px 12px !important;
  margin: 0 12px !important;
  padding: 6px 8px !important;
  box-sizing: border-box !important;
}
.pursuit-page .history-head::after {
  content: '';
}
.pursuit-page .history-row {
  grid-template-columns: minmax(0, 1fr) 104px minmax(108px, 1.15fr) 96px 12px !important;
}
.pursuit-page .history-row > span.mono {
  align-self: center;
  white-space: normal !important;
  overflow-wrap: normal !important;
  word-break: normal !important;
  line-height: 1.25 !important;
  font-size: clamp(9px, 0.68vw, 11px) !important;
}
</style>
<style scoped>
/* Keep the long feedback readable while allowing target identifiers to wrap. */
.pursuit-page .live-panel .live-grid {
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1.25fr) minmax(0, 0.85fr) minmax(0, 0.45fr) !important;
}
.pursuit-page .live-panel .live-identity-fields,
.pursuit-page .live-panel .live-identity-fields > .live-field {
  min-width: 0 !important;
  max-width: 100% !important;
}
.pursuit-page .live-panel .live-identity-fields {
  flex: 1 1 0 !important;
  width: 0 !important;
}
.pursuit-page .live-panel .live-identity-fields > .live-field {
  display: flex !important;
  align-items: flex-start !important;
  width: 100% !important;
}
.pursuit-page .live-panel .live-identity-fields .live-chip {
  min-width: 0 !important;
  max-width: 100% !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields {
  flex: 1 1 0 !important;
  width: 0 !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field {
  display: flex !important;
  align-items: flex-start !important;
  flex: 1 1 auto !important;
  width: 100% !important;
}
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > .live-label-target + em.live-chip {
  flex: 1 1 auto !important;
}
.pursuit-page .live-panel .live-label-target + .live-chip {
  flex: 1 1 auto !important;
  box-sizing: border-box !important;
  white-space: normal !important;
  overflow-wrap: anywhere !important;
  word-break: break-word !important;
  line-height: 1.25 !important;
  text-align: left !important;
}
.pursuit-page .live-panel .live-label-feedback + .live-chip {
  display: inline-flex !important;
  min-width: 0 !important;
  max-width: 100% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}
@media (max-width: 1180px) {
  .pursuit-page .live-panel .live-grid {
    grid-template-columns: minmax(0, 1.35fr) minmax(0, 1.1fr) minmax(0, 0.8fr) minmax(0, 0.45fr) !important;
  }
}
@media (max-width: 760px) {
  .pursuit-page .live-panel .live-grid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
  }
}
</style>
<style scoped>
/* Keep every identity-field title aligned to the requested fixed width. */
.pursuit-page .live-panel .live-identity > .live-identity-fields > .live-field > label {
  flex: 0 0 95px !important;
  width: 95px !important;
}
</style>
<style scoped>
.pursuit-page .live-panel .condition-status.condition-backup {
  color: #a6b8c8 !important;
  border-color: rgba(139, 164, 185, .58) !important;
  background: linear-gradient(180deg, rgba(43, 64, 82, .8), rgba(21, 35, 49, .88)) !important;
  box-shadow: inset 0 0 10px rgba(139, 164, 185, .1) !important;
}
.pursuit-page .live-panel .condition-status.condition-backup > i {
  background: #a6b8c8 !important;
  box-shadow: 0 0 5px rgba(166, 184, 200, .65) !important;
}
.pursuit-page .live-panel .condition-status.condition-patrol {
  color: #55d6a7 !important;
  border-color: rgba(85, 214, 167, .56) !important;
  background: linear-gradient(180deg, rgba(22, 75, 59, .78), rgba(10, 35, 29, .88)) !important;
  box-shadow: inset 0 0 10px rgba(85, 214, 167, .12) !important;
}
.pursuit-page .live-panel .condition-status.condition-patrol > i {
  background: #55d6a7 !important;
  box-shadow: 0 0 6px rgba(85, 214, 167, .75) !important;
}
.pursuit-page .live-panel .condition-status.condition-amber {
  color: #f0b45e !important;
  border-color: rgba(240, 180, 94, .62) !important;
  background: linear-gradient(180deg, rgba(78, 54, 23, .82), rgba(37, 25, 12, .9)) !important;
  box-shadow: inset 0 0 10px rgba(240, 180, 94, .12) !important;
}
.pursuit-page .live-panel .condition-status.condition-amber > i {
  background: #f0b45e !important;
  box-shadow: 0 0 6px rgba(240, 180, 94, .78) !important;
}
.pursuit-page .live-panel .condition-status.condition-active {
  color: #e68ac4 !important;
  border-color: rgba(230, 138, 196, .62) !important;
  background: linear-gradient(180deg, rgba(75, 35, 67, .82), rgba(38, 17, 34, .9)) !important;
  box-shadow: inset 0 0 10px rgba(230, 138, 196, .12) !important;
}
.pursuit-page .live-panel .condition-status.condition-active > i {
  background: #e68ac4 !important;
  box-shadow: 0 0 6px rgba(230, 138, 196, .78) !important;
}
</style>
<style scoped>
/* Keep image frames shrink-wrapped to the loaded image's intrinsic ratio. */
.pursuit-page .message-view > .message-detail {
  display: flex !important;
  flex-wrap: nowrap !important;
  flex-direction: column !important;
  align-items: stretch !important;
  align-content: flex-start !important;
  gap: 18px !important;
  min-height: 0 !important;
  height: auto !important;
}
.pursuit-page .message-view > .message-detail .annotated-image {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex: 0 0 auto !important;
  width: min(100%, 840px) !important;
  max-width: 100% !important;
  height: auto !important;
  aspect-ratio: 2 / 1 !important;
  min-height: 0 !important;
  margin: 0 auto !important;
  border: 0 !important;
  overflow: hidden !important;
}
.pursuit-page .message-view > .message-detail .annotated-image:not(:has(img)) {
  display: none !important;
}
.pursuit-page .message-view > .message-detail .annotated-image img {
  position: static !important;
  inset: auto !important;
  display: block !important;
  flex: 0 1 auto !important;
  width: auto !important;
  height: auto !important;
  max-width: 100% !important;
  max-height: 100% !important;
  object-fit: contain !important;
  box-sizing: border-box !important;
  border: 1px solid #08adc6 !important;
}
.pursuit-page .message-view > .message-detail > .target-selector {
  display: flex !important;
  flex-direction: column !important;
  align-items: stretch !important;
  gap: 8px !important;
  flex: 1 1 auto !important;
  width: 100% !important;
  min-width: 0 !important;
  min-height: 0 !important;
  max-width: none !important;
}
.pursuit-page .message-view > .message-detail > .target-selector .target-options {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  align-content: start !important;
  align-items: stretch !important;
  gap: 8px !important;
  flex: 0 0 auto !important;
  min-width: 0 !important;
  max-height: 192px !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
}
.pursuit-page .message-view > .message-detail > .target-selector h2 {
  margin: 0 0 2px !important;
  padding-bottom: 7px !important;
}
.pursuit-page .message-view > .message-detail > .target-selector .target-row {
  width: 100% !important;
  min-width: 0 !important;
  min-height: 42px !important;
  height: auto !important;
  margin: 0 !important;
  padding: 8px 12px !important;
  box-sizing: border-box !important;
  text-align: left !important;
}
.pursuit-page .message-view > .message-detail > .target-selector .target-row b {
  min-width: 0 !important;
  margin-left: 9px !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
  overflow-wrap: anywhere !important;
}
.pursuit-page .message-view > .message-detail > .target-selector .radio-dot {
  flex: 0 0 16px !important;
  width: 16px !important;
  height: 16px !important;
}
.pursuit-page .message-view > .message-detail > .target-selector .authorize-button {
  align-self: center !important;
  width: min(44%, 560px) !important;
  min-width: 0 !important;
  height: 38px !important;
  margin: auto 0 0 !important;
  padding: 0 16px !important;
  border-width: 1px !important;
  border-radius: 6px !important;
  font-size: 13px !important;
}
.pursuit-page .history-detail .summary:has(.history-source-image) > .history-source-image {
  width: max-content !important;
  max-width: 100% !important;
  height: auto !important;
  aspect-ratio: auto !important;
  border: 0 !important;
  background: transparent !important;
}
.pursuit-page .history-detail .summary:has(.history-source-image) > .history-source-image img {
  position: static !important;
  inset: auto !important;
  display: block !important;
  width: auto !important;
  height: auto !important;
  max-width: 100% !important;
  max-height: min(21vh, 190px) !important;
  object-fit: contain !important;
  box-sizing: border-box !important;
}
.pursuit-page .live-panel .live-identity > .live-source-image {
  flex: 0 1 auto !important;
  align-self: center !important;
  width: auto !important;
  height: auto !important;
  max-width: min(26%, 240px) !important;
  max-height: clamp(58px, min(12vh, 150px), 150px) !important;
  aspect-ratio: auto !important;
  object-fit: contain !important;
  box-sizing: border-box !important;
}
@media (max-width: 800px) {
  .pursuit-page .message-view > .message-detail {
    flex-direction: column !important;
  }
  .pursuit-page .message-view > .message-detail .annotated-image {
    max-width: 100% !important;
  }
  .pursuit-page .message-view > .message-detail > .target-selector {
    flex: 1 1 auto !important;
    width: 100% !important;
  }
  .pursuit-page .message-view > .message-detail > .target-selector .authorize-button {
    width: min(100%, 360px) !important;
  }
}
</style>
<style scoped>
@media (min-width: 801px) {
  .pursuit-page {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0 !important;
    box-sizing: border-box;
    overflow: hidden;
  }
  .pursuit-page > .title-strip,
  .pursuit-page > .robot-band,
  .pursuit-page > .live-panel {
    flex: 0 0 auto;
  }
  .pursuit-page > .workspace {
    flex: 1 1 auto;
    height: auto !important;
    min-height: 0 !important;
  }
  .pursuit-page > .workspace > .left-panel,
  .pursuit-page > .workspace > .right-panel {
    height: 100%;
    min-height: 0;
  }
}

@media (min-width: 1200px) and (max-height: 960px) {
  .pursuit-page > .workspace > .right-panel > .message-view {
    display: flex !important;
    flex-direction: column !important;
    box-sizing: border-box !important;
    height: 100%;
    min-height: 0 !important;
    overflow: hidden !important;
  }
  .pursuit-page .right-panel .message-view > .event-log {
    flex: 0 0 72px !important;
    min-height: 0 !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
  }
  .pursuit-page .right-panel .message-view > .detail-time {
    flex: 0 0 24px;
    min-height: 0;
  }
  .pursuit-page .right-panel .message-view > .message-detail {
    display: flex !important;
    flex: 1 1 0 !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: stretch !important;
    gap: 14px !important;
    height: auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
  }
  .pursuit-page .right-panel .message-view > .message-detail .annotated-image {
    display: flex !important;
    flex: 1 1 0 !important;
    align-items: center !important;
    justify-content: center !important;
    width: auto !important;
    max-width: none !important;
    height: auto !important;
    min-width: 0 !important;
    min-height: 0 !important;
    aspect-ratio: auto !important;
    margin: 0 !important;
  }
  .pursuit-page .right-panel .message-view > .message-detail .annotated-image img {
    width: auto !important;
    height: auto !important;
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: contain !important;
  }
  .pursuit-page .right-panel .message-view > .message-detail > .target-selector {
    flex: 0 0 42% !important;
    width: 42% !important;
    height: 100% !important;
    min-width: 0 !important;
    min-height: 0 !important;
    overflow: hidden !important;
  }
  .pursuit-page .right-panel .message-view > .message-detail > .target-selector .target-options {
    flex: 0 1 auto !important;
    max-height: min(300px, calc(100% - 90px)) !important;
    min-height: 0 !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
  }
}
</style>
